import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Search, Calendar as CalendarIcon, Users, Sparkles, ChevronLeft, Plane, Hotel, Car, Check, User, LogOut, Mail, Phone, MapPin, Globe, Star, Shield, Home as HomeIcon, Info, Lock, AlertCircle, CloudSun, Lightbulb, ShieldAlert, CalendarDays } from "lucide-react";
import axios from "axios";
import { API, BACKEND_URL } from "@/App";
import RazorpayTrustBadge from "@/components/RazorpayTrustBadge";
import SSLVerifiedBadge from "@/components/SSLVerifiedBadge";
import { toast } from "sonner";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import AuthModal from "../components/AuthModal";
import BookingDetailModal from "../components/BookingDetailModal";
import { trackVisit, trackStep } from "../utils/tracker";

// Meal preference options surfaced alongside each passenger's name on Step 4.
// `value` is what's stored on the booking; `label` + `emoji` drive the chip UI.
// Empty value ("") = no preference / will decide on flight, which is the default.
const MEAL_OPTIONS = [
  { value: "veg", label: "Veg", emoji: "🥬" },
  { value: "non-veg", label: "Non-Veg", emoji: "🍗" },
  { value: "vegan", label: "Vegan", emoji: "🌱" },
  { value: "jain", label: "Jain", emoji: "🪷" },
];

// Curated country dial codes for the "Invite by SMS" picker on Step 7.
// India first since it's the home market — rest in alphabetical order.
const COUNTRY_DIAL_CODES = [
  { code: "+91",  label: "India (+91)" },
  { code: "+971", label: "UAE (+971)" },
  { code: "+1",   label: "USA / Canada (+1)" },
  { code: "+44",  label: "UK (+44)" },
  { code: "+61",  label: "Australia (+61)" },
  { code: "+65",  label: "Singapore (+65)" },
  { code: "+66",  label: "Thailand (+66)" },
  { code: "+62",  label: "Indonesia (+62)" },
  { code: "+60",  label: "Malaysia (+60)" },
  { code: "+33",  label: "France (+33)" },
  { code: "+49",  label: "Germany (+49)" },
  { code: "+81",  label: "Japan (+81)" },
  { code: "+64",  label: "New Zealand (+64)" },
  { code: "+27",  label: "South Africa (+27)" },
  { code: "+41",  label: "Switzerland (+41)" },
];

// iPad/iPhone Safari Intelligent Tracking Prevention (ITP) breaks the
// Razorpay modal iframe (storage partitioning kills checkout.js's session),
// so on those devices we switch Razorpay to its full-page redirect mode
// (Checkout opens on api.razorpay.com, then 303's back through our backend
// → /booking?paid=<id>). Detection covers both classic iOS and iPadOS 13+,
// which masquerades as macOS but exposes touch points.
function isIOSSafariLike() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
}

// Synchronous form-POST to Razorpay's hosted-checkout endpoint. Designed for
// the iOS-Safari redirect flow: called inside the click handler with ZERO
// awaits between the user gesture and `form.submit()` so Safari preserves
// the click-context (it strips it the moment a promise resolves after the
// click). `action` is typically https://api.razorpay.com/v1/checkout/embedded.
function submitRazorpayEmbeddedForm(action, fields) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  Object.entries(fields).forEach(([k, v]) => {
    if (v == null || v === "") return;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = String(v);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
}

// Compact chip row for picking a meal preference for one passenger.
// Tapping the active chip toggles it off (back to "no preference").
function MealChips({ value, onChange, testid, required = false }) {
  // `required=true` (default false for backwards-compat with infants who
  // are meal-exempt) tightens the label styling and surfaces a small red
  // "*Required" hint until the customer picks a chip. Drives the
  // Step-4 Continue button's enabled state via `allNamesFilledIn()`.
  const isMissing = required && (!value || value.trim() === "");
  return (
    <div className="flex flex-wrap gap-1.5 mt-2 ml-14" data-testid={testid}>
      <span className={`text-[10px] uppercase tracking-wider self-center mr-1 ${isMissing ? "text-red-300/80" : "text-white/40"}`}>
        Meal{required && <span className="text-red-400 ml-0.5">*</span>}
      </span>
      {MEAL_OPTIONS.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-testid={`${testid}-${opt.value}`}
            onClick={() => onChange(active ? "" : opt.value)}
            className={`px-2 py-1 rounded text-[10px] font-medium border transition-all flex items-center gap-1 ${
              active
                ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                : isMissing
                ? "bg-white/5 border-red-400/30 text-white/55 hover:border-red-400/60"
                : "bg-white/5 border-white/10 text-white/50 hover:border-white/25"
            }`}
          >
            <span aria-hidden>{opt.emoji}</span>
            {opt.label}
          </button>
        );
      })}
      {isMissing && (
        <span className="basis-full text-[10px] text-red-300/80 mt-0.5" data-testid={`${testid}-required-hint`}>
          Required — pick one to continue
        </span>
      )}
    </div>
  );
}

export default function BookingPage({ user, isAuthenticated, setIsAuthenticated, setUser, onLogout }) {
  const navigate = useNavigate();
  // Promo code captured from `?promo=` URL param (set by the admin re-engagement
  // nudge email/WhatsApp). When present and validated, it's submitted with
  // /bookings and applied server-side; the breakdown on Step 5 shows it.
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount_pct, expires_at } | null
  // Manual promo entry on Step 5 (Iter 46) — for customers who switched
  // devices and lost the `?promo=` URL param. Same backend endpoint as the
  // auto-apply flow; backend still gates by ownership + expiry + usage.
  const [showPromoInput, setShowPromoInput] = useState(false);
  const [manualPromoCode, setManualPromoCode] = useState("");
  const [applyingPromo, setApplyingPromo] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [detailBooking, setDetailBooking] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [showBookings, setShowBookings] = useState(false);
  const [step, setStep] = useState(0);
  const [travelType, setTravelType] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [departureCity, setDepartureCity] = useState("");
  const [departureAirport, setDepartureAirport] = useState("");
  const [showDeparturePicker, setShowDeparturePicker] = useState(false);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [adultNames, setAdultNames] = useState([""]); 
  const [adultGenders, setAdultGenders] = useState([""]);
  const [adultMeals, setAdultMeals] = useState([""]); // "", "veg", "non-veg", "vegan", "jain"
  const [childNames, setChildNames] = useState([]);
  const [childGenders, setChildGenders] = useState([]);
  const [childMeals, setChildMeals] = useState([]);
  const [infantNames, setInfantNames] = useState([]);
  const [infantGenders, setInfantGenders] = useState([]);
  const [infantMeals, setInfantMeals] = useState([]);
  const [infantDOBs, setInfantDOBs] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentPlan, setPaymentPlan] = useState("weekly");
  const [autoPayEnabled, setAutoPayEnabled] = useState(false);
  const [booking, setBooking] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [liveHotelPrices, setLiveHotelPrices] = useState(null);
  const [loadingHotelPrices, setLoadingHotelPrices] = useState(false);
  const [liveFlightPrices, setLiveFlightPrices] = useState(null);
  const [flightAvailability, setFlightAvailability] = useState(null);
  const [roadTravel, setRoadTravel] = useState(null);
  const [alternateAirports, setAlternateAirports] = useState([]);
  const [selectedReturnAirport, setSelectedReturnAirport] = useState(null);
  const [showPaymentAuth, setShowPaymentAuth] = useState(false);
  // When true, the user just authenticated via the payment-auth modal and we
  // should proceed to create the booking. Avoids a stale-closure loop where
  // the old handleCreateBooking still saw isAuthenticated=false and re-opened
  // the auth modal instead of advancing to Step 6.
  const [pendingBookingAfterAuth, setPendingBookingAfterAuth] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  // Step-7 invite-a-friend form state
  const [inviteValue, setInviteValue] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [sentInvites, setSentInvites] = useState([]); // {contact, channel}
  const [inviteChannel, setInviteChannel] = useState("email"); // "email" | "sms"
  const [inviteCountryCode, setInviteCountryCode] = useState("+91");

  // Mandate-intercept dialog state. Shown AFTER the 1% upfront clears and
  // BEFORE we open the second Razorpay Checkout for the EMI auto-debit
  // mandate. Without this explicit step the second Checkout was either being
  // dismissed (user thought it was a duplicate) or popup-blocked (the awaited
  // axios call between the two opens loses the user-activation trust signal).
  const [mandateInterceptOpen, setMandateInterceptOpen] = useState(false);
  const [mandateInProgress, setMandateInProgress] = useState(false);
  const [mandateError, setMandateError] = useState(null);

  // True from the moment the customer clicks Pay until Razorpay returns or
  // the user explicitly closes the modal. Drives the "do not refresh" notice
  // and disables the Pay button to prevent double-charges.
  // True while we're swapping the booking after the customer clicks the
  // "Postpone trip by 6 months" CTA. The button calls /bookings again with
  // the new dates — without this, the OLD booking row (with the original
  // close-by dates) is what /payments/create-order validates, causing the
  // EMI plan to be rejected at the mandate step.
  const [shiftingDates, setShiftingDates] = useState(false);
  const [paymentBusy, setPaymentBusy] = useState(false);
  // Pre-warmed Razorpay order — created BEFORE the customer taps Pay so that
  // razorpay.open() can fire synchronously inside the click handler.
  // Required for iPad/iPhone Safari, which silently blocks popups when the
  // click handler does an async network call before opening the popup.
  // Shape: { plan: "full"|"weekly"|...; amount: number; bookingId: str; options: object }
  const [prewarmedOrder, setPrewarmedOrder] = useState(null);
  // Pre-warmed Razorpay subscription — same idea as `prewarmedOrder` but for
  // the EMI mandate auth flow. Minted when the mandate-intercept dialog opens
  // so the iOS-Safari "Continue with Auto-Debit" click can submit the hidden
  // form synchronously (no awaits between user-gesture and form.submit).
  // Shape: { bookingId: str; key: str; subscription_id: str; prefill: object }
  const [prewarmedSubscription, setPrewarmedSubscription] = useState(null);
  // iPad/iPhone Safari can stall mid-redirect if the customer's network
  // hiccups during the form POST. After 30s of `paymentBusy` we drop the
  // busy lock and surface a Retry button so they're never stuck staring at
  // a frozen "Processing…" CTA.
  const [iosPaymentTimedOut, setIosPaymentTimedOut] = useState(false);
  // Iteration 45 — visa flow on Step 5 (before payment).
  // `needsVisaAssistance` is a soft toggle for international trips; it's
  // persisted on the booking so admin / ops can route the customer to
  // Travelsate's visa partner. Cost is NEVER added to the package — visa
  // fees vary by passport/destination and are billed separately.
  const [needsVisaAssistance, setNeedsVisaAssistance] = useState(false);
  // Hard gate for international trips departing within 30 days: customer
  // must self-attest they already hold a valid passport AND valid visa.
  // Without this, embassies typically can't process new visas in time.
  const [passportVisaConfirmed, setPassportVisaConfirmed] = useState(false);
  // True while the "WhatsApp me the link" button on the iPad mandate
  // escape-hatch is in-flight. Drives the button's loading state so
  // customers don't double-tap and trigger duplicate Twilio messages.
  const [sendingMandateLink, setSendingMandateLink] = useState(false);
  // Tracks whether Razorpay's checkout.js has fully loaded. On slow iPad
  // Safari connections this may lag — we use this flag to gate the Pay
  // button and avoid falling through to the async-block slow path.
  const [razorpayReady, setRazorpayReady] = useState(false);
  const [loadingFlightPrices, setLoadingFlightPrices] = useState(false);
  // Transfer cost (4-leg per-km via Google Distance Matrix + live FX)
  const [transferCostData, setTransferCostData] = useState({ luxury: null, premium: null, basic: null });
  const [loadingTransferCost, setLoadingTransferCost] = useState(false);
  const [showTransferBreakdown, setShowTransferBreakdown] = useState(false);
  // Browser geolocation pickup is disabled — transfer cost falls back to
  // user's saved pincode / city centroid. `homeCoords` stays null and the
  // backend handles the null case gracefully.
  const [homeCoords] = useState(null);
  const stepRef = useRef(0);

  // Sync ref with state
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  // Push browser history on step change
  useEffect(() => {
    window.history.pushState({ step }, "", `#step-${step}`);
  }, [step]);

  // Handle browser back button
  useEffect(() => {
    const handlePopState = (e) => {
      const currentStep = stepRef.current;
      if (currentStep > 0) {
        e.preventDefault();
        // Push a new entry so repeated back presses keep working
        window.history.pushState({ step: currentStep - 1 }, "", `#step-${currentStep - 1}`);
        setStep(currentStep - 1);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    // If a previous BookingPage mount already loaded checkout.js, skip the
    // duplicate fetch and just flip the readiness flag immediately. This
    // matters when the customer goes Back → Forward through Step 6.
    if (typeof window !== "undefined" && window.Razorpay) {
      setRazorpayReady(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setRazorpayReady(true);
    script.onerror = () => setRazorpayReady(false);
    document.body.appendChild(script);
    return () => {
      // Don't actually remove the script — leaving it in place keeps Razorpay
      // available on Back/Forward navigation. Removing it on unmount was
      // causing iPad Safari to refetch the script during the Step 5 → 6
      // transition, sometimes leaving window.Razorpay undefined exactly when
      // the customer taps Pay.
    };
  }, []);

  // ── Razorpay redirect-mode return-trip handler ──────────────────────
  // When the iOS Safari payment flow finishes, Razorpay POSTs the result to
  // /api/payments/redirect-return which 303's the customer back here with
  // `?paid=<booking_id>` (or `?paid_error=<code>` on failure/cancel). We
  // rehydrate `booking` + dependent state and jump the user to Step 7 so the
  // experience is indistinguishable from the modal flow.

  // Error toast — runs without auth gating because the cross-site Razorpay
  // bounce may have stripped the customer's session and we still need to
  // surface the failure reason instead of dumping them on Step 0 silently.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const paidError = (sp.get("paid_error") || "").trim();
    const mandateError = (sp.get("mandate_error") || "").trim();
    if (!paidError && !mandateError) return;
    const paidReasons = {
      cancelled: "Payment was cancelled. You can try again whenever you're ready.",
      signature: "Payment couldn't be verified. Please contact support if you were charged.",
      not_found: "We couldn't locate this booking. Please contact support.",
      invalid_payload: "Payment gateway returned an invalid response. Please try again.",
    };
    const mandateReasons = {
      cancelled: "Auto-debit setup was cancelled. Open My Bookings to retry — your seat is still reserved.",
      signature: "Auto-debit setup couldn't be verified. Please open My Bookings and retry.",
      not_found: "We couldn't link your auto-debit mandate. Please contact support.",
      invalid_payload: "Payment gateway returned an invalid response. Please retry from My Bookings.",
    };
    const reason = paidError
      ? (paidReasons[paidError] || "Payment didn't complete. Please try again.")
      : (mandateReasons[mandateError] || "Auto-debit setup didn't complete. Please try again from My Bookings.");
    // Defer one tick so sonner's Toaster is definitely mounted (the Toaster
    // lives in the App root which renders alongside this page). Without the
    // microtask deferral, the toast fires before sonner's React effect
    // attaches its listener and the message is dropped silently.
    setTimeout(() => toast.error(reason), 0);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("paid_error");
      url.searchParams.delete("mandate_error");
      window.history.replaceState({}, "", url.toString());
    } catch {/* noop */}
  }, []);

  // Success path — needs auth to fetch the booking & restore state.
  useEffect(() => {
    if (!isAuthenticated) return;
    const sp = new URLSearchParams(window.location.search);
    const paidId = (sp.get("paid") || "").trim();
    const mandateId = (sp.get("mandate") || "").trim();
    const restoreId = paidId || mandateId;
    if (!restoreId) return;
    const isMandateReturn = !!mandateId;

    let cancelled = false;
    (async () => {
      try {
        const [bRes, dRes] = await Promise.all([
          axios.get(`${API}/bookings/${restoreId}`),
          axios.get(`${API}/destinations`).catch(() => null),
        ]);
        if (cancelled) return;
        const b = bRes.data;
        const dest = (dRes?.data || []).find((d) => d.id === b.destination_id) || null;
        setBooking(b);
        if (dest) setSelectedDestination(dest);
        setSelectedPackage(b.package_type);
        setPaymentPlan(b.payment_plan || "full");
        try {
          if (b.start_date) setStartDate(new Date(b.start_date));
          if (b.end_date) setEndDate(new Date(b.end_date));
        } catch {/* date parse failure → leave nulls */}
        setDepartureCity(b.departure_city || "");
        setDepartureAirport(b.departure_airport || "");
        setAdults(b.adults || 1);
        setChildren(b.children || 0);
        setInfants(b.infants || 0);
        setAdultNames(b.adult_names || []);
        setAdultGenders(b.adult_genders || []);
        setAdultMeals(b.adult_meals || []);
        setChildNames(b.child_names || []);
        setChildGenders(b.child_genders || []);
        setChildMeals(b.child_meals || []);
        setInfantNames(b.infant_names || []);
        setInfantGenders(b.infant_genders || []);
        setInfantMeals(b.infant_meals || []);
        setInfantDOBs((b.infant_dobs || []).map((d) => (d ? new Date(d) : null)));
        setBookingConfirmed(true);
        setStep(7);

        // Mandate-return path: the customer just came back from the
        // subscription-auth redirect. If the backend has flipped status to
        // `authenticated`, celebrate. If not (signature failed or webhook
        // lagged), keep the pending banner active for retry.
        if (isMandateReturn) {
          if (b.razorpay_subscription_id && b.subscription_status === "authenticated") {
            toast.success("Auto-debit set up. Your booking is confirmed!");
            setMandateInterceptOpen(false);
          } else if (b.payment_status === "upfront_paid" && !b.razorpay_subscription_id) {
            setMandateError(null);
            setMandateInterceptOpen(true);
          }
          try { sessionStorage.removeItem("tvs_pending_mandate"); } catch {/* noop */}
        } else {
          // Upfront-payment-return path: EMI plans still need the mandate
          // setup — surface the intercept dialog automatically.
          if (b.payment_status === "upfront_paid" && !b.razorpay_subscription_id) {
            setMandateError(null);
            setMandateInterceptOpen(true);
          } else if (b.payment_status === "completed") {
            toast.success("Payment successful! Your booking is confirmed.");
          }
          try { sessionStorage.removeItem("tvs_pending_payment"); } catch {/* noop */}
        }
        // Strip ?paid= / ?mandate= so refresh doesn't re-trigger the flow.
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("paid");
          url.searchParams.delete("plan_status");
          url.searchParams.delete("mandate");
          window.history.replaceState({}, "", url.toString());
        } catch {/* noop */}
      } catch (err) {
        if (!cancelled) {
          toast.error("Couldn't load your booking after payment. Please open My Bookings.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Pick up `?promo=...` from the URL (set by admin re-engagement nudges)
  // and validate it once the user is authenticated. Backend only returns a
  // valid response when the code belongs to the current user, so a bad/
  // stolen link silently no-ops.
  useEffect(() => {
    if (!isAuthenticated) return;
    const sp = new URLSearchParams(window.location.search);
    const code = (sp.get("promo") || "").trim().toUpperCase();
    if (!code) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/promo/${encodeURIComponent(code)}`);
        if (res.data?.valid) {
          setAppliedPromo({
            code: res.data.code,
            discount_pct: res.data.discount_pct,
            expires_at: res.data.expires_at,
          });
          toast.success(`Promo ${res.data.code} applied — ${res.data.discount_pct}% off your booking`);
        } else if (res.data?.reason === "expired") {
          toast.error("This promo code has expired");
        } else if (res.data?.reason === "already_used") {
          toast.error("This promo code has already been used");
        }
      } catch {
        // 404 = invalid or not yours; stay quiet (don't leak existence).
      }
    })();
  }, [isAuthenticated]);

  // Manual promo apply (Iter 46) — same endpoint as the URL-param flow but
  // user-initiated from Step 5. We surface explicit errors here (unlike the
  // silent URL flow) because the customer is typing the code themselves.
  // NOTE: we deliberately don't pre-check `isAuthenticated` — the cookie can
  // be valid even if the React prop briefly lags (e.g. fresh reload before
  // /auth/me resolves). Let the backend respond with 401 and surface it.
  const handleApplyManualPromo = async () => {
    const code = manualPromoCode.trim().toUpperCase();
    if (!code) {
      toast.error("Please enter a promo code");
      return;
    }
    setApplyingPromo(true);
    try {
      const res = await axios.get(`${API}/promo/${encodeURIComponent(code)}`);
      if (res.data?.valid) {
        setAppliedPromo({
          code: res.data.code,
          discount_pct: res.data.discount_pct,
          expires_at: res.data.expires_at,
        });
        toast.success(`Promo ${res.data.code} applied — ${res.data.discount_pct}% off your booking`);
        setManualPromoCode("");
        setShowPromoInput(false);
      } else if (res.data?.reason === "expired") {
        toast.error("This promo code has expired");
      } else if (res.data?.reason === "already_used") {
        toast.error("This promo code has already been used");
      } else {
        toast.error("This promo code is not valid");
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Please log in to apply a promo code");
      } else if (status === 404) {
        toast.error("Promo code not found or not assigned to your account");
      } else {
        toast.error("Could not validate promo code — please try again");
      }
    } finally {
      setApplyingPromo(false);
    }
  };


  // Fetch destinations ONCE on mount only. We deliberately don't depend on
  // `user` — the destinations payload is public and refetching on login
  // pulls in the user's pincode-derived `user_departure_city`, which would
  // silently override the customer's explicit "Travelling from" pick.
  useEffect(() => {
    fetchDestinations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API}/destinations`);
      setDestinations(response.data);

      // Pincode-derived departure auto-fill is DISABLED. The customer must
      // pick their departure airport explicitly in Step 3, and that pick is
      // sticky for the entire session — never overwritten by login.
    } catch (error) {
      toast.error("Failed to load destinations");
    }
  };

  // ── ANALYTICS BEACONS ──
  useEffect(() => {
    trackVisit("booking");
  }, []);

  useEffect(() => {
    trackStep(step, {
      destination_id: selectedDestination?.id,
      package_type: selectedPackage,
    });
    // selectedDestination/selectedPackage may change without step changing —
    // that's fine, we want to capture the latest context whenever step changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleAdultsChange = (newCount) => {
    const count = Math.max(1, newCount);
    setAdults(count);
    setAdultNames(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
    setAdultGenders(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
    setAdultMeals(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
  };

  const handleChildrenChange = (newCount) => {
    const count = Math.max(0, newCount);
    setChildren(count);
    setChildNames(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
    setChildGenders(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
    setChildMeals(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
  };

  const handleInfantsChange = (newCount) => {
    const count = Math.max(0, newCount);
    setInfants(count);
    setInfantNames(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
    setInfantGenders(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
    setInfantMeals(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push("");
      return arr.slice(0, count);
    });
    setInfantDOBs(prev => {
      const arr = [...prev];
      while (arr.length < count) arr.push(null);
      return arr.slice(0, count);
    });
  };

  const getInfantAgeAtTravel = (dob) => {
    if (!dob || !startDate) return null;
    const travel = new Date(startDate);
    const birth = new Date(dob);
    let years = travel.getFullYear() - birth.getFullYear();
    let months = travel.getMonth() - birth.getMonth();
    if (months < 0) { years--; months += 12; }
    if (travel.getDate() < birth.getDate()) months--;
    if (months < 0) { years--; months += 12; }
    return { years, months };
  };

  const allNamesFilledIn = () => {
    const nameRegex = /^[a-zA-Z\s]{3,}$/;
    // Meal preference is mandatory for adults & children (airlines need it
    // for catering load). Infants are exempt by industry convention — they
    // are typically milk-fed and don't get a tray meal on lap-seat tickets.
    const mealsFilled = (arr) => Array.isArray(arr) && arr.every((m) => m && m.trim() !== "");
    const adultsFilled = adultNames.every(n => nameRegex.test(n.trim())) && adultGenders.every(g => g !== "") && mealsFilled(adultMeals);
    const childrenFilled = childNames.every(n => nameRegex.test(n.trim())) && childGenders.every(g => g !== "") && mealsFilled(childMeals);
    const infantsFilled = infantNames.every(n => nameRegex.test(n.trim())) && infantGenders.every(g => g !== "");
    return adultsFilled && childrenFilled && infantsFilled;
  };

  // Granular check used by Step-4 UI hints — true only when names/genders
  // are filled but at least one adult/child meal preference is still blank.
  // Lets us show a precise inline error instead of a generic "fill all fields".
  const mealsAreTheBlocker = () => {
    const nameRegex = /^[a-zA-Z\s]{3,}$/;
    const namesGendersOk =
      adultNames.every(n => nameRegex.test(n.trim())) && adultGenders.every(g => g !== "") &&
      childNames.every(n => nameRegex.test(n.trim())) && childGenders.every(g => g !== "") &&
      infantNames.every(n => nameRegex.test(n.trim())) && infantGenders.every(g => g !== "");
    if (!namesGendersOk) return false;
    const anyAdultBlank = adultMeals.some((m) => !m || m.trim() === "");
    const anyChildBlank = childMeals.some((m) => !m || m.trim() === "");
    return anyAdultBlank || anyChildBlank;
  };

  const getNameError = (name) => {
    if (!name || name.trim() === "") return "Name is required";
    if (name.trim().length < 3) return "Minimum 3 characters";
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) return "Only letters and spaces allowed";
    return null;  };

  // Strip digits and other disallowed characters at input time so users
  // physically CAN'T type numbers in a name field. Belt-and-braces with
  // getNameError() which validates on submit (for pasted/IME input edge cases).
  const sanitizeNameInput = (raw) => (raw || "").replace(/[^a-zA-Z\s]/g, "");

  const fetchMyBookings = async () => {
    if (!isAuthenticated) return;
    setLoadingBookings(true);
    try {
      const res = await axios.get(`${API}/bookings`);
      // Backend already returns most-recent-first via Mongo sort on
      // `created_at`. Re-sort client-side as a defensive fallback for any
      // legacy rows that may lack a sortable created_at value.
      const sorted = [...(res.data || [])].sort((a, b) => {
        const av = a?.created_at ? String(a.created_at) : "";
        const bv = b?.created_at ? String(b.created_at) : "";
        return bv.localeCompare(av);
      });
      setMyBookings(sorted);
    } catch {
      setMyBookings([]);
    } finally {
      setLoadingBookings(false);
    }
  };

  // Top 5 things to do per destination
  const destinationActivities = {
    "taj-mahal-agra": [
      { title: "Sunrise at Taj Mahal", img: "https://images.unsplash.com/photo-1587135941948-670b381f08ce?w=300" },
      { title: "Agra Fort Exploration", img: "https://images.unsplash.com/photo-1591018653367-9c01498b3320?w=300" },
      { title: "Mehtab Bagh Sunset View", img: "https://images.unsplash.com/photo-1548013146-72479768bada?w=300" },
      { title: "Fatehpur Sikri Heritage Walk", img: "https://images.pexels.com/photos/6244988/pexels-photo-6244988.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Petha & Street Food Trail", img: "https://images.pexels.com/photos/36236282/pexels-photo-36236282.jpeg?auto=compress&cs=tinysrgb&w=300" }
    ],
    "kerala-backwaters": [
      { title: "Houseboat Cruise in Alleppey", img: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?w=300" },
      { title: "Ayurvedic Spa Experience", img: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=300" },
      { title: "Munnar Tea Plantation Walk", img: "https://images.unsplash.com/photo-1564574685150-4b45e4009e4b?w=300" },
      { title: "Kathakali Dance Performance", img: "https://images.pexels.com/photos/18832750/pexels-photo-18832750.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Fort Kochi Beach & Art Walk", img: "https://images.unsplash.com/photo-1590050752117-238cb0fb12b1?w=300" }
    ],
    "goa-beaches": [
      { title: "Sunset at Palolem Beach", img: "https://images.pexels.com/photos/32262464/pexels-photo-32262464.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Old Goa Church Heritage Tour", img: "https://images.pexels.com/photos/30875613/pexels-photo-30875613.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Dudhsagar Waterfall Trek", img: "https://images.pexels.com/photos/33347824/pexels-photo-33347824.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Spice Plantation Visit", img: "https://images.pexels.com/photos/16166177/pexels-photo-16166177.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Goan Seafood Culinary Tour", img: "https://images.pexels.com/photos/36885753/pexels-photo-36885753.jpeg?auto=compress&cs=tinysrgb&w=300" }
    ],
    "jaipur-pink-city": [
      { title: "Amber Fort & Elephant Ride", img: "https://images.unsplash.com/photo-1599661046827-dacff0c0f09a?w=300" },
      { title: "Hawa Mahal Sunrise Photo Walk", img: "https://images.unsplash.com/photo-1477587458883-47145ed94245?w=300" },
      { title: "City Palace Museum Tour", img: "https://images.unsplash.com/photo-1524309288853-4eb9bbe6d453?w=300" },
      { title: "Nahargarh Fort Sunset View", img: "https://images.unsplash.com/photo-1603262110263-fb0112e7cc33?w=300" },
      { title: "Jaipur Bazaar Shopping", img: "https://images.pexels.com/photos/10589517/pexels-photo-10589517.jpeg?auto=compress&cs=tinysrgb&w=300" }
    ],
    "default": [
      { title: "Visit the Local Heritage Sites", img: "https://images.pexels.com/photos/31969428/pexels-photo-31969428.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Try Regional Cuisine & Street Food", img: "https://images.pexels.com/photos/984533/pexels-photo-984533.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Explore Nature & Scenic Trails", img: "https://images.pexels.com/photos/30245159/pexels-photo-30245159.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Experience Local Cultural Performances", img: "https://images.pexels.com/photos/19539249/pexels-photo-19539249.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "Shop for Local Handicrafts", img: "https://images.pexels.com/photos/30495690/pexels-photo-30495690.jpeg?auto=compress&cs=tinysrgb&w=300" }
    ],
    "dubai-uae": [
      { title: "Burj Khalifa Observation Deck", img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=300" },
      { title: "Desert Safari & Dune Bashing", img: "https://images.unsplash.com/photo-1451337516015-6b6e9a44a8a3?w=300" },
      { title: "Dubai Mall & Aquarium", img: "https://images.unsplash.com/photo-1553881781-4c55163dc5fd?w=300" },
      { title: "Dhow Cruise at Marina", img: "https://images.unsplash.com/photo-1580674684081-7617fbf3d745?w=300" },
      { title: "Old Dubai Spice & Gold Souk", img: "https://images.unsplash.com/photo-1597659840241-37e2b9c2f55f?w=300" }
    ],
    "bangkok-thailand": [
      { title: "Grand Palace & Wat Phra Kaew", img: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=300" },
      { title: "Floating Market Experience", img: "https://images.unsplash.com/photo-1504214208698-ea1916a2195a?w=300" },
      { title: "Chatuchak Weekend Market", img: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=300" },
      { title: "Chao Phraya River Cruise", img: "https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=300" },
      { title: "Thai Street Food Tour", img: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300" }
    ],
    "bali-indonesia": [
      { title: "Tegallalang Rice Terraces", img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=300" },
      { title: "Uluwatu Temple Sunset", img: "https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=300" },
      { title: "Sacred Monkey Forest", img: "https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?w=300" },
      { title: "Nusa Penida Day Trip", img: "https://images.unsplash.com/photo-1570789210967-2cac24e25519?w=300" },
      { title: "Balinese Spa & Wellness", img: "https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=300" }
    ],
    "singapore-city": [
      { title: "Gardens by the Bay Light Show", img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=300" },
      { title: "Marina Bay Sands SkyPark", img: "https://images.unsplash.com/photo-1496939376851-89342e90adcd?w=300" },
      { title: "Sentosa Island Adventures", img: "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=300" },
      { title: "Hawker Centre Food Trail", img: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=300" },
      { title: "Orchard Road Shopping", img: "https://images.unsplash.com/photo-1555529669-e69e7aa0ba9a?w=300" }
    ],
    "maldives-islands": [
      { title: "Snorkeling with Manta Rays", img: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=300" },
      { title: "Overwater Villa Sunset", img: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=300" },
      { title: "Underwater Restaurant Dining", img: "https://images.unsplash.com/photo-1540202403-b7abd6747a18?w=300" },
      { title: "Dolphin Watching Cruise", img: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=300" },
      { title: "Bioluminescent Beach Night Walk", img: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=300" }
    ],
    "paris-france": [
      { title: "Eiffel Tower at Night", img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=300" },
      { title: "Louvre Museum Masterpieces", img: "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=300" },
      { title: "Montmartre Art Walk", img: "https://images.unsplash.com/photo-1550340499-a6c60fc8287c?w=300" },
      { title: "Seine River Cruise", img: "https://images.unsplash.com/photo-1478391679764-b2d8b3cd1e94?w=300" },
      { title: "French Patisserie Tasting", img: "https://images.unsplash.com/photo-1509440159596-0249088772ff?w=300" }
    ],
    "london-uk": [
      { title: "Tower of London & Crown Jewels", img: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=300" },
      { title: "Buckingham Palace Guard Change", img: "https://images.unsplash.com/photo-1520986606214-8b456906c813?w=300" },
      { title: "London Eye & South Bank Walk", img: "https://images.unsplash.com/photo-1486299267070-83823f5448dd?w=300" },
      { title: "Borough Market Food Tour", img: "https://images.pexels.com/photos/8002133/pexels-photo-8002133.jpeg?auto=compress&cs=tinysrgb&w=300" },
      { title: "West End Theatre Show", img: "https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=300" }
    ],
    "tokyo-japan": [
      { title: "Shibuya Crossing Experience", img: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=300" },
      { title: "Senso-ji Temple & Asakusa", img: "https://images.unsplash.com/photo-1545569341-9eb8b30979d9?w=300" },
      { title: "Tsukiji Outer Market Sushi", img: "https://images.unsplash.com/photo-1553621042-f6e147245754?w=300" },
      { title: "Akihabara Electric Town", img: "https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=300" },
      { title: "Meiji Shrine Forest Walk", img: "https://images.unsplash.com/photo-1478436127897-769e1b3f0f36?w=300" }
    ],
    "sydney-australia": [
      { title: "Sydney Opera House Tour", img: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?w=300" },
      { title: "Bondi to Coogee Beach Walk", img: "https://images.unsplash.com/photo-1523428096049-1c342a7a8996?w=300" },
      { title: "Harbour Bridge Climb", img: "https://images.unsplash.com/photo-1524293581917-878a6d017c71?w=300" },
      { title: "Blue Mountains Day Trip", img: "https://images.unsplash.com/photo-1494233892892-84542a694e72?w=300" },
      { title: "Darling Harbour & Wildlife", img: "https://images.unsplash.com/photo-1546268060-5dccace165b8?w=300" }
    ],
    "switzerland-alps": [
      { title: "Jungfraujoch Top of Europe", img: "https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?w=300" },
      { title: "Lake Lucerne Cruise", img: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=300" },
      { title: "Swiss Chocolate Factory Tour", img: "https://images.unsplash.com/photo-1481391319762-47dff72954d9?w=300" },
      { title: "Zermatt & Matterhorn Views", img: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=300" },
      { title: "Interlaken Adventure Sports", img: "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=300" }
    ]
  };

  const getActivities = () => {
    if (!selectedDestination) return destinationActivities["default"];
    return destinationActivities[selectedDestination.id] || destinationActivities["default"];
  };

  // Travel suitability ratings fetched live from API (Google Reviews + AI analysis)

  const [liveReviews, setLiveReviews] = useState([]);
  const [reviewsDestId, setReviewsDestId] = useState(null);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsSource, setReviewsSource] = useState("");
  const [liveTravelRatings, setLiveTravelRatings] = useState(null);
  const [loadingTravelRatings, setLoadingTravelRatings] = useState(false);
  const [hotelNames, setHotelNames] = useState(null);
  const [loadingHotelNames, setLoadingHotelNames] = useState(false);
  const [itinerary, setItinerary] = useState(null);
  const [loadingItinerary, setLoadingItinerary] = useState(false);
  // Customer-selected count of guided itinerary days. `null` => fall back to
  // (tripDays - 1) so the existing "all days guided" default is preserved.
  // Capped to [1, tripDays-1] in the UI.
  const [itineraryDaysSelected, setItineraryDaysSelected] = useState(null);
  // Free-form text the customer wants the ops team to consider (e.g. "add a
  // visit to X spot", "vegetarian only meals", "include a sunset boat ride").
  const [additionalRequests, setAdditionalRequests] = useState("");
  const [airlineNames, setAirlineNames] = useState(null);
  const [loadingAirlineNames, setLoadingAirlineNames] = useState(false);
  const [dailyCost, setDailyCost] = useState(null);
  const [loadingDailyCost, setLoadingDailyCost] = useState(false);
  const [cuisinesData, setCuisinesData] = useState(null);
  const [loadingCuisines, setLoadingCuisines] = useState(false);
  // Itinerary cost is included in the package by default — customers can
  // OPT OUT via the Step-5 checkbox if they prefer to pay out-of-pocket.
  const [includeItineraryCost, setIncludeItineraryCost] = useState(true);
  // Iter 69 — Additional Step-5 toggles so customers who already have
  // their own flights / hotels / airport transfer can strip those line
  // items and only pay for what they need. Backend `/api/bookings`
  // honours these via `include_flight_cost` / `include_hotel_cost` /
  // `include_transfer_cost` in the client_quote payload.
  const [includeFlightCost, setIncludeFlightCost] = useState(true);
  const [includeHotelCost, setIncludeHotelCost] = useState(true);
  const [includeTransferCost, setIncludeTransferCost] = useState(true);
  const [expandAltAirport, setExpandAltAirport] = useState(false);
  const [expandTravelRatings, setExpandTravelRatings] = useState(false);
  const [expandItinerary, setExpandItinerary] = useState(false);
  const [expandCuisines, setExpandCuisines] = useState(false);
  const [expandAirlines, setExpandAirlines] = useState({ luxury: false, premium: false, basic: false });
  const [expandHotels, setExpandHotels] = useState({ luxury: false, premium: false, basic: false });

  const [reviewsFocus, setReviewsFocus] = useState(null);

  const fetchLiveReviews = async (focus = null) => {
    if (!selectedDestination) return;
    const destIdAtFetch = selectedDestination.id;
    setLoadingReviews(true);
    try {
      const url = focus 
        ? `${API}/reviews/${selectedDestination.id}?focus=${focus}`
        : `${API}/reviews/${selectedDestination.id}`;
      const response = await axios.get(url);
      if (response.data?.reviews?.length > 0) {
        setLiveReviews(response.data.reviews);
        setReviewsDestId(destIdAtFetch);
        setReviewsSource(response.data.source);
        setReviewsFocus(response.data.focus || null);
      } else {
        setLiveReviews([]);
        setReviewsDestId(destIdAtFetch);
        setReviewsSource("");
        setReviewsFocus(null);
      }
    } catch {
      setLiveReviews([]);
      setReviewsDestId(null);
      setReviewsSource("");
      setReviewsFocus(null);
    } finally {
      setLoadingReviews(false);
    }
  };

  const fetchLiveTravelRatings = async () => {
    if (!selectedDestination) return;
    setLoadingTravelRatings(true);
    try {
      const response = await axios.get(`${API}/travel-ratings/${selectedDestination.id}`);
      if (response.data?.ratings) {
        setLiveTravelRatings(response.data);
      } else {
        setLiveTravelRatings(null);
      }
    } catch {
      setLiveTravelRatings(null);
    } finally {
      setLoadingTravelRatings(false);
    }
  };

  useEffect(() => {
    if (selectedDestination && step === 4) {
      // Detect female solo traveler: 1 adult, female, no children, no infants
      const isFemaleSolo = adults === 1 && children === 0 && infants === 0 && adultGenders[0] === "female";
      // Stale reviews from a previously-picked destination would otherwise
      // linger on Step 4 if the customer goes back and changes destination.
      // We compare the cached `reviewsDestId` against the current selection.
      const cachedForOtherDest = reviewsDestId !== selectedDestination.id;
      if (isFemaleSolo && reviewsFocus !== "women_safety") {
        fetchLiveReviews("women_safety");
      } else if (!isFemaleSolo && reviewsFocus === "women_safety") {
        fetchLiveReviews();
      } else if (cachedForOtherDest || liveReviews.length === 0) {
        fetchLiveReviews(isFemaleSolo ? "women_safety" : null);
      }
    }
  }, [selectedDestination?.id, step, adults, children, infants, adultGenders]);

  useEffect(() => {
    if (selectedDestination && step === 5) {
      // Browser geolocation prompt is disabled per product decision —
      // transfer cost will fall back to user's saved pincode / city centroid.

      fetchLiveTravelRatings();
      // Fetch hotel names
      if (!hotelNames || hotelNames.destination_id !== selectedDestination.id) {
        setLoadingHotelNames(true);
        axios.get(`${API}/hotel-names/${selectedDestination.id}`)
          .then(res => { if (res.data?.hotels) setHotelNames(res.data); })
          .catch(() => {})
          .finally(() => setLoadingHotelNames(false));
      }
      // Fetch airline names
      if (!airlineNames || airlineNames.destination_id !== selectedDestination.id) {
        setLoadingAirlineNames(true);
        axios.get(`${API}/airline-names/${selectedDestination.id}`)
          .then(res => { if (res.data?.airlines) setAirlineNames(res.data); })
          .catch(() => {})
          .finally(() => setLoadingAirlineNames(false));
      }
      // Fetch itinerary — skip the LAST day (the passenger flies out that day),
      // honour the customer's chosen guided-day count when set.
      // Iteration 41: pass the Step-4 traveller profile (meals/genders/
      // children/infants) so the LLM tailors food picks, kid-friendly stops,
      // infant-friendly pacing, and all-female safety phrasing. The backend
      // fingerprint-caches per profile so two customers with identical
      // preferences still share a cached response.
      if (startDate && endDate) {
        const tripDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
        const maxItineraryDays = Math.max(1, tripDays - 1);
        const itineraryDays = Math.max(1, Math.min(itineraryDaysSelected ?? maxItineraryDays, maxItineraryDays));
        const mealsParam = [...new Set([...(adultMeals || []), ...(childMeals || [])].filter(Boolean))].sort().join(",");
        const gendersParam = [...new Set([...(adultGenders || []), ...(childGenders || [])].filter(Boolean))].sort().join(",");
        // Fingerprint the personalization signals; if any change, refetch.
        const personalizationFp = `${mealsParam}|${gendersParam}|c${children}|i${infants}`;
        const itinNeedsRefresh =
          !itinerary ||
          itinerary.destination_id !== selectedDestination.id ||
          itinerary.days !== itineraryDays ||
          itinerary._fp !== personalizationFp;
        if (itinNeedsRefresh) {
          setLoadingItinerary(true);
          const params = new URLSearchParams({
            days: String(itineraryDays),
            meals: mealsParam,
            genders: gendersParam,
            children: String(children || 0),
            infants: String(infants || 0),
          });
          axios.get(`${API}/itinerary/${selectedDestination.id}?${params.toString()}`)
            .then(res => {
              if (res.data?.itinerary) {
                // Stash the fingerprint on the state so the next dep-array
                // tick can detect a change without re-deriving it here.
                setItinerary({ ...res.data, _fp: personalizationFp });
              }
            })
            .catch(() => {})
            .finally(() => setLoadingItinerary(false));
        }
      }
      // Daily-cost estimate — destination-flat (USD 50 domestic / USD 75 intl),
      // so we can fetch it the moment the destination is picked, without
      // waiting for a package selection. This means every Luxury/Premium/Basic
      // card already shows the itinerary line by default.
      const needsDailyCost =
        !dailyCost || dailyCost.destination_id !== selectedDestination.id;
      if (needsDailyCost) {
        setLoadingDailyCost(true);
        axios
          .get(`${API}/daily-cost/${selectedDestination.id}?package_type=${selectedPackage || "basic"}`)
          .then((res) => {
            if (res.data) setDailyCost(res.data);
          })
          .catch(() => {})
          .finally(() => setLoadingDailyCost(false));
      }
      // Fetch top cuisines + places to eat for the selected tier
      if (selectedPackage && (!cuisinesData || cuisinesData.destination_id !== selectedDestination.id || cuisinesData.package_type !== selectedPackage)) {
        setLoadingCuisines(true);
        axios.get(`${API}/cuisines-and-restaurants/${selectedDestination.id}?package_type=${selectedPackage}`)
          .then(res => { if (res.data) setCuisinesData(res.data); })
          .catch(() => {})
          .finally(() => setLoadingCuisines(false));
      }
    }
  }, [selectedDestination?.id, step, selectedPackage, itineraryDaysSelected, children, infants, adultMeals.join("|"), childMeals.join("|"), adultGenders.join("|"), childGenders.join("|")]);

  const majorAirports = [
    { city: "Delhi", code: "DEL", country: "India" },
    { city: "Mumbai", code: "BOM", country: "India" },
    { city: "Bangalore", code: "BLR", country: "India" },
    { city: "Chennai", code: "MAA", country: "India" },
    { city: "Kolkata", code: "CCU", country: "India" },
    { city: "Hyderabad", code: "HYD", country: "India" },
    { city: "Pune", code: "PNQ", country: "India" },
    { city: "Ahmedabad", code: "AMD", country: "India" },
    { city: "Kochi", code: "COK", country: "India" },
    { city: "Goa", code: "GOI", country: "India" },
    { city: "Jaipur", code: "JAI", country: "India" },
    { city: "Lucknow", code: "LKO", country: "India" },
    { city: "Chandigarh", code: "IXC", country: "India" },
    { city: "Guwahati", code: "GAU", country: "India" },
    { city: "Varanasi", code: "VNS", country: "India" },
    { city: "Dubai", code: "DXB", country: "UAE" },
    { city: "Singapore", code: "SIN", country: "Singapore" },
    { city: "Bangkok", code: "BKK", country: "Thailand" },
    { city: "London", code: "LHR", country: "UK" },
    { city: "Paris", code: "CDG", country: "France" },
    { city: "New York", code: "JFK", country: "USA" },
    { city: "Tokyo", code: "NRT", country: "Japan" },
    { city: "Sydney", code: "SYD", country: "Australia" },
    { city: "Hong Kong", code: "HKG", country: "Hong Kong" },
    { city: "Kuala Lumpur", code: "KUL", country: "Malaysia" },
    { city: "Doha", code: "DOH", country: "Qatar" },
    { city: "Abu Dhabi", code: "AUH", country: "UAE" },
    { city: "Zurich", code: "ZRH", country: "Switzerland" },
    { city: "Frankfurt", code: "FRA", country: "Germany" },
    { city: "Toronto", code: "YYZ", country: "Canada" }
  ];

  const [departureSearch, setDepartureSearch] = useState("");

  const filteredAirports = majorAirports.filter(
    (ap) =>
      ap.city.toLowerCase().includes(departureSearch.toLowerCase()) ||
      ap.code.toLowerCase().includes(departureSearch.toLowerCase()) ||
      ap.country.toLowerCase().includes(departureSearch.toLowerCase())
  );

  // (Removed) pincode-derived departure auto-fill. Customer's Step-3 pick is
  // the single source of truth and is preserved across login/logout.

  const filteredDestinations = destinations.filter(
    (dest) =>
      (dest.type === travelType || (!dest.type && travelType === "domestic")) &&
      (dest.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      dest.location.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Fetch live hotel prices from SerpAPI when destination + dates are ready
  const fetchLiveHotelPrices = async () => {
    if (!selectedDestination || !startDate || !endDate) return;
    setLoadingHotelPrices(true);
    try {
      const checkIn = startDate.toISOString().split("T")[0];
      const checkOut = endDate.toISOString().split("T")[0];
      const response = await axios.get(
        `${API}/hotel-prices/${selectedDestination.id}?check_in=${checkIn}&check_out=${checkOut}`
      );
      if (response.data?.hotel_prices) {
        setLiveHotelPrices(response.data.hotel_prices);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch live hotel prices:", error);
      }
      // Don't clear existing prices on error — keep last good data
    } finally {
      setLoadingHotelPrices(false);
    }
  };

  // Fetch live flight prices from SerpAPI Google Flights
  const fetchLiveFlightPrices = async () => {
    if (!selectedDestination || !startDate || !endDate) return;
    setLoadingFlightPrices(true);
    try {
      const outbound = startDate.toISOString().split("T")[0];
      const returnDate = endDate.toISOString().split("T")[0];
      const depAirport = departureAirport || selectedDestination.user_departure_airport || "DEL";
      const response = await axios.get(
        `${API}/flight-prices/${selectedDestination.id}?outbound_date=${outbound}&return_date=${returnDate}&departure_airport=${depAirport}`
      );
      if (response.data?.flight_prices) {
        setLiveFlightPrices(response.data.flight_prices);
        setFlightAvailability(response.data.availability || null);
        setRoadTravel(response.data.road_travel || null);
        setAlternateAirports(response.data.alternate_airports || []);
      }
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch live flight prices:", error);
      }
      // Don't clear existing prices on error — keep last good data
    } finally {
      setLoadingFlightPrices(false);
    }
  };

  useEffect(() => {
    if (selectedDestination && startDate && endDate) {
      if (selectedDestination.is_test_package) {
        // Test packages bypass live SerpAPI calls — costs are forced to ₹1
        setLiveHotelPrices({ luxury: 0, premium: 0, basic: 0 });
        setLiveFlightPrices({ luxury: 0, premium: 0, basic: 0 });
        setFlightAvailability(null);
        setRoadTravel(null);
        setAlternateAirports([]);
        return;
      }
      fetchLiveHotelPrices();
      fetchLiveFlightPrices();
    }
  }, [selectedDestination?.id, startDate, endDate, departureAirport]);

  // Fetch the 4-leg transfer cost for all 3 package tiers whenever destination
  // or departure airport changes. Server caches Google Distance Matrix + FX
  // results so this is cheap. Pincode and home coords are intentionally NOT
  // sent — same departure airport must always yield the same transfer fare.
  useEffect(() => {
    if (!selectedDestination) {
      setTransferCostData({ luxury: null, premium: null, basic: null });
      return;
    }
    const dep = departureAirport || "DEL";

    if (selectedDestination.is_test_package) {
      setTransferCostData({
        luxury: { total_cost_inr: 0, total_km: 0, rate_per_km_usd: 1.0, legs: [], available: true },
        premium: { total_cost_inr: 0, total_km: 0, rate_per_km_usd: 0.5, legs: [], available: true },
        basic: { total_cost_inr: 0, total_km: 0, rate_per_km_usd: 0.5, legs: [], available: true },
      });
      return;
    }

    setLoadingTransferCost(true);
    const base = { departure_airport: dep };

    const fetchTier = (tier) =>
      axios
        .get(`${API}/transfer-cost/${selectedDestination.id}`, { params: { ...base, package_type: tier } })
        .then((r) => [tier, r.data])
        .catch(() => [tier, null]);

    Promise.all([fetchTier("luxury"), fetchTier("premium"), fetchTier("basic")])
      .then((results) => {
        const next = { luxury: null, premium: null, basic: null };
        results.forEach(([tier, data]) => {
          next[tier] = data;
        });
        setTransferCostData(next);
      })
      .finally(() => setLoadingTransferCost(false));
  }, [selectedDestination?.id, departureAirport]);

  const handleDestinationSelect = (dest) => {
    setSelectedDestination(dest);
    setStep(3);
  };

  const calculateCosts = (packageType = selectedPackage) => {
    if (!selectedDestination || !startDate || !endDate) return null;

    const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

    const today = new Date();
    const daysUntilTravel = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));

    // ── TEST PACKAGE: total cost is forced to ₹1 (matches backend create_booking override) ──
    if (selectedDestination.is_test_package) {
      const totalCost = 1;
      const bookingCost = 1;
      return {
        days,
        flightCosts: 0,
        hotelCosts: 0,
        transferCosts: 0,
        useRoadTravel: false,
        roomsNeeded: 1,
        subtotal: 1,
        tax: 0,
        totalCost,
        bookingCost,
        remainingAmount: 0,
        daysUntilTravel,
        weeksAvailable: 0,
        biweeksAvailable: 0,
        monthsAvailable: 0,
        paymentOptions: [],
      };
    }

    // No static fallbacks — all prices come from live API
    // If API data not loaded yet, return null (shows loading state)
    if (liveFlightPrices == null || liveHotelPrices == null) return null;
    
    let flightPrice = 0;
    let hotelPerNight = 0;

    if (packageType === "luxury") {
      flightPrice = liveFlightPrices.luxury ?? 0;
      hotelPerNight = liveHotelPrices.luxury ?? 0;
    } else if (packageType === "premium") {
      flightPrice = liveFlightPrices.premium ?? 0;
      hotelPerNight = liveHotelPrices.premium ?? 0;
    } else {
      flightPrice = liveFlightPrices.basic ?? 0;
      hotelPerNight = liveHotelPrices.basic ?? 0;
    }

    // Calculate flight costs (round trip for all travelers)
    const flightCosts = (adults * flightPrice) + (children * flightPrice * 0.3) + (infants * flightPrice * 0.1);

    // Calculate hotel costs (per room per night)
    // Room occupancy: max 2 adults + 1 child + 1 infant per room
    // Iter 59 — room-occupancy formula now matches server.py (updated in
    // Iter 43). A standard hotel room comfortably holds up to 2 adults + 2
    // children, so rooms = max(1, ceil(adults/2), ceil(children/2)).
    // Infants are lap/bed-shared and don't add rooms. The old formula
    // `Math.max(1, ceil(adults/2), children, infants)` over-roomed:
    // 2A + 3C returned 3 rooms client-side vs 2 rooms server-side, which
    // re-triggered the Step-5-to-Step-6 mismatch for families with kids.
    const roomsNeeded = Math.max(1, Math.ceil(adults / 2), Math.ceil(children / 2));
    const hotelCosts = roomsNeeded * hotelPerNight * days;

    // Transfer cost — if same-state with no flights, road travel replaces transfers.
    // Otherwise use the 4-leg per-km formula returned by /api/transfer-cost (Google
    // Distance Matrix + live FX). Falls back to ₹0 only while data is in-flight.
    const noFlightsAvailable = flightAvailability && !flightAvailability.first_class && !flightAvailability.business_class && !flightAvailability.economy_class;
    const useRoadTravel = noFlightsAvailable && roadTravel;
    const tierTransfer = transferCostData?.[packageType];
    let transferCosts = tierTransfer?.total_cost_inr ?? 0;
    if (useRoadTravel) {
      transferCosts = packageType === "luxury" ? roadTravel.luxury_round_trip : roadTravel.standard_round_trip;
    }

    // Itinerary cost is now part of EVERY package tier's breakdown so the
    // customer sees identical "Itinerary" line items on the Luxury / Premium /
    // Basic cards. The daily rate is destination-flat (USD 50 domestic, USD 75
    // intl) so it's tier-agnostic. Adults full · Children 50% · Infants/<3 free.
    // Customer-selected guided-day count caps at (trip days - 1) to leave the
    // flight-out day free; if the customer hasn't picked yet, default to max.
    const maxItineraryDays = Math.max(0, days - 1);
    const itineraryDays = Math.max(0, Math.min(itineraryDaysSelected ?? maxItineraryDays, maxItineraryDays));
    const paxWeighted = adults + (children * 0.5);
    const itineraryEligible = days >= 2;
    const itineraryApplied =
      includeItineraryCost &&
      itineraryEligible &&
      itineraryDays > 0 &&
      dailyCost &&
      dailyCost.total_inr > 0;
    const itineraryCost = itineraryApplied
      ? Math.round(dailyCost.total_inr * itineraryDays * paxWeighted)
      : 0;

    // ── DISPLAYED PRICE MARKUP ──
    // Customer-facing prices include a small margin on top of the wholesale
    // SerpAPI / rate-card costs (flight +5%, hotel +5%, transfer +2%).
    // Keep this in sync with backend/server.py:PRICE_MARKUP.
    // Iter 69 — customer can now exclude any of Flight / Hotel / Transfer
    // (e.g. NRIs who've booked their own flights already). We honour the
    // toggle by zeroing the corresponding line item; the display path and
    // total both react automatically.
    const flightCostsWithMargin    = includeFlightCost   ? flightCosts   * 1.05 : 0;
    const hotelCostsWithMargin     = includeHotelCost    ? hotelCosts    * 1.05 : 0;
    const transferCostsWithMargin  = includeTransferCost ? transferCosts * 1.02 : 0;

    // ── PACKAGE AVAILABILITY ──
    // A package can only be sold if BOTH flights (or a road-transfer
    // substitute) AND hotels are actually available for the chosen dates.
    // Live SerpAPI returns 0 when nothing's on offer for that tier — and a
    // ₹0 line item silently lets the customer book a phantom trip.
    // We surface a clear "unavailable" state on the card instead.
    // Iter 69 — if the customer has explicitly excluded flights or hotels,
    // we treat that component as "available" for the purposes of gating,
    // because they've told us they don't need it.
    const flightAvailable = !includeFlightCost || flightPrice > 0 || useRoadTravel;
    const hotelAvailable  = !includeHotelCost  || hotelPerNight > 0;
    const packageAvailable = flightAvailable && hotelAvailable;
    let unavailableReason = null;
    if (!flightAvailable && !hotelAvailable) {
      unavailableReason = "No flights or hotels available for the chosen dates";
    } else if (!flightAvailable) {
      const tierLabel =
        packageType === "luxury" ? "First class" :
        packageType === "premium" ? "Business class" : "Economy class";
      unavailableReason = `${tierLabel} flights are not available for the chosen dates`;
    } else if (!hotelAvailable) {
      const tierLabel =
        packageType === "luxury" ? "Luxury" :
        packageType === "premium" ? "Premium" : "Standard";
      unavailableReason = `${tierLabel} hotels are not available for the chosen dates`;
    }

    // Calculate subtotal
    const subtotal = flightCostsWithMargin + hotelCostsWithMargin + transferCostsWithMargin + itineraryCost;
    const tax = subtotal * 0.18;
    const totalCost = subtotal + tax;
    const bookingCost = totalCost * 0.01;
    const remainingAmount = totalCost - bookingCost;

    // Calculate available payment options based on days until travel,
    // reserving a destination-type-dependent buffer BEFORE travel so ops
    // can lock in flights/hotels/transfers after the final EMI clears.
    // Keep this map in sync with backend/server.py:EMI_BUFFER_DAYS.
    //   Domestic       → 15 days
    //   International  → 30 days
    const EMI_BUFFER_DAYS = { domestic: 15, international: 30 };
    const destType = (selectedDestination?.type || "domestic").toLowerCase();
    const bufferDays = EMI_BUFFER_DAYS[destType] ?? 30;
    const emiEligibleDays = Math.max(0, daysUntilTravel - bufferDays);
    const daysAvailable = Math.floor(emiEligibleDays / 1);
    const weeksAvailable = Math.floor(emiEligibleDays / 7);
    const biweeksAvailable = Math.floor(emiEligibleDays / 15);
    const monthsAvailable = Math.floor(emiEligibleDays / 30);

    // Determine which payment plans are viable (need at least 2 installments)
    const paymentOptions = [];

    // Note: Razorpay Subscriptions does NOT support truly daily (interval_days=1)
    // auto-debit — the `daily` period enforces a minimum interval of 7 (≡ weekly),
    // and any 1-day interval is rejected with "Interval provided is less than
    // the minimum interval (7)". Daily EMI is therefore disabled platform-wide.

    if (weeksAvailable >= 2) {
      paymentOptions.push({
        type: 'weekly',
        installments: weeksAvailable,
        amount: remainingAmount / weeksAvailable
      });
    }
    
    if (biweeksAvailable >= 2) {
      paymentOptions.push({
        type: 'biweekly',
        installments: biweeksAvailable,
        amount: remainingAmount / biweeksAvailable
      });
    }
    
    if (monthsAvailable >= 2) {
      paymentOptions.push({
        type: 'monthly',
        installments: monthsAvailable,
        amount: remainingAmount / monthsAvailable
      });
    }

    return { 
      days, 
      // Surface the marked-up values so package-card breakdowns match the
      // total the customer pays (wholesale numbers stay backend-only).
      flightCosts: flightCostsWithMargin,
      hotelCosts: hotelCostsWithMargin,
      transferCosts: transferCostsWithMargin,
      itineraryCost,
      itineraryApplied,
      itineraryDays,
      maxItineraryDays,
      itineraryEligible,
      // Package availability — drives the "unavailable" overlay on the card.
      packageAvailable,
      unavailableReason,
      flightAvailable,
      hotelAvailable,
      useRoadTravel,
      roomsNeeded,
      subtotal, 
      tax, 
      totalCost, 
      bookingCost, 
      remainingAmount,
      daysUntilTravel,
      daysAvailable,
      weeksAvailable,
      biweeksAvailable,
      monthsAvailable,
      paymentOptions
    };
  };

  const handleCreateBooking = async () => {
    if (!selectedDestination || !startDate || !endDate || !selectedPackage) {
      toast.error("Please complete all steps");
      return;
    }

    if (!isAuthenticated) {
      // Defer the booking until the user finishes OTP. The effect below
      // watches `isAuthenticated` + `pendingBookingAfterAuth` and proceeds
      // with the LATEST handleCreateBooking — avoiding the stale-closure
      // loop where AuthModal.onSuccess called an outdated reference.
      setPendingBookingAfterAuth(true);
      setShowPaymentAuth(true);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/bookings`,
        {
          destination_id: selectedDestination.id,
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          adults: adults,
          children: children,
          infants: infants,
          package_type: selectedPackage,
          adult_names: adultNames,
          adult_genders: adultGenders,
          adult_meals: adultMeals,
          child_names: childNames,
          child_genders: childGenders,
          child_meals: childMeals,
          infant_names: infantNames,
          infant_genders: infantGenders,
          infant_meals: infantMeals,
          infant_dobs: infantDOBs.map(d => d ? d.toISOString() : null),
          departure_city: departureCity,
          departure_airport: departureAirport,
          include_itinerary_cost: includeItineraryCost,
          include_flight_cost: includeFlightCost,
          include_hotel_cost: includeHotelCost,
          include_transfer_cost: includeTransferCost,
          itinerary_days_selected: itineraryDaysSelected ?? undefined,
          additional_requests: additionalRequests?.trim() || undefined,
          promo_code: appliedPromo?.code || undefined,
          // Lock Step-5 prices into the booking so Step 6 always matches.
          // Server-side guard: only honoured for trips <6 mo away AND each
          // bucket within ±25% of the server's fresh recompute (anti-tamper).
          // Iter 53 — itinerary_cost added so the total the customer sees on
          // Step 5 (with itinerary included) is EXACTLY what Razorpay charges.
          client_quote: costs ? {
            flight_costs: costs.flightCosts,
            hotel_costs: costs.hotelCosts,
            transfer_costs: costs.transferCosts,
            itinerary_cost: costs.itineraryCost,
          } : undefined,
        }
      );
      setBooking(response.data);
      setStep(6);
    } catch (error) {
      toast.error("Failed to create booking: " + (error.response?.data?.detail || error.message));
    }
  };

  // After the user signs in via the payment-auth modal, advance to booking
  // creation automatically. Watching `isAuthenticated` lets this run with the
  // LATEST handleCreateBooking closure — no stale-state loop.
  useEffect(() => {
    if (isAuthenticated && pendingBookingAfterAuth && !showPaymentAuth) {
      setPendingBookingAfterAuth(false);
      handleCreateBooking();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, pendingBookingAfterAuth, showPaymentAuth]);

  const handleInviteFriend = async () => {
    if (!booking) return;
    setSendingInvite(true);
    try {
      let payload;
      let displayContact;
      let channelLabel;
      if (inviteChannel === "sms") {
        const digits = inviteValue.replace(/\D/g, "");
        if (digits.length < 6 || digits.length > 15) {
          toast.error("Please enter a valid phone number");
          setSendingInvite(false);
          return;
        }
        const fullPhone = `${inviteCountryCode}${digits}`;
        payload = { phone: fullPhone };
        displayContact = fullPhone;
        channelLabel = "sms";
      } else {
        const raw = inviteValue.trim();
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(raw)) {
          toast.error("Please enter a valid email address");
          setSendingInvite(false);
          return;
        }
        payload = { email: raw };
        displayContact = raw;
        channelLabel = "email";
      }
      const res = await axios.post(`${API}/bookings/${booking.id}/invite`, payload);
      const channels = res.data?.results || {};
      const ch = channels[channelLabel];
      if (ch?.ok) {
        toast.success(
          channelLabel === "email"
            ? `Invite emailed to ${displayContact}`
            : `Invite SMS sent to ${displayContact}`
        );
        setSentInvites((prev) => [...prev, { contact: displayContact, channel: channelLabel }]);
        setInviteValue("");
      } else {
        toast.error(ch?.error || "Could not send invite");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  // Open Razorpay Checkout in subscription-authorisation mode so the
  // customer signs a UPI AutoPay / Card / eMandate. Razorpay then auto-debits
  // each EMI on its schedule and pings our /payments/webhook.
  //
  // iOS Safari is handled in `startMandate` BEFORE calling this function (the
  // ITP fix needs a fully synchronous click handler with zero awaits between
  // the user gesture and `form.submit()`). This function therefore only
  // covers the popup-modal flow used by non-iOS browsers.
  const authoriseMandate = (bookingId) =>
    new Promise(async (resolve, reject) => {
      try {
        const subRes = await axios.post(`${API}/payments/create-subscription`, {
          booking_id: bookingId,
        });
        const opts = {
          key: subRes.data.key,
          subscription_id: subRes.data.subscription_id,
          name: "Travelsate",
          description: "Authorise EMI auto-debit mandate",
          prefill: subRes.data.prefill || {},
          theme: { color: "#D4AF37" },
          handler: async (rp) => {
            try {
              await axios.post(`${API}/payments/verify-subscription`, {
                booking_id: bookingId,
                razorpay_payment_id: rp.razorpay_payment_id,
                razorpay_subscription_id: rp.razorpay_subscription_id,
                razorpay_signature: rp.razorpay_signature,
              });
              // Refresh booking so Step 7 reflects mandate-active state.
              try {
                const refreshed = await axios.get(`${API}/bookings/${bookingId}`);
                setBooking(refreshed.data);
              } catch (e) {
                if (process.env.NODE_ENV !== "production") {
                  // eslint-disable-next-line no-console
                  console.warn("Mandate auth: post-verify refresh failed (non-fatal):", e?.message || e);
                }
              }
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          modal: {
            ondismiss: () =>
              reject(new Error("Customer dismissed mandate authorisation")),
          },
        };
        const checkout = new window.Razorpay(opts);
        checkout.open();
      } catch (err) {
        reject(err);
      }
    });

  // Triggered from the mandate-intercept dialog's "Continue" button. Because
  // this fires on a direct user click (not after an `await` chain inside the
  // Razorpay payment handler), Chrome / Safari treat the subsequent
  // window.Razorpay popup as user-initiated and don't block it. On success we
  // advance to step 7; on failure we keep the dialog open with a retry CTA.
  const startMandate = async () => {
    if (!booking) return;

    // ── iOS Safari → synchronous hidden-form POST ──────────────────────
    // Same Safari ITP fix as the upfront-payment path: we MUST submit the
    // form inside the click handler with zero awaits before form.submit()
    // or Safari treats the navigation as non-user-initiated and silently
    // blocks it. The subscription_id is pre-warmed when the mandate-intercept
    // dialog opens (see useEffect below); if it's not ready yet we tell the
    // customer to retry in a moment rather than burning the click context.
    if (isIOSSafariLike()) {
      const subReady =
        prewarmedSubscription &&
        prewarmedSubscription.bookingId === booking.id &&
        prewarmedSubscription.subscription_id;
      if (!subReady) {
        // Trigger / re-trigger the pre-warm and ask the customer to retry.
        setPrewarmedSubscription(null);
        toast.message("Preparing auto-debit setup…", {
          description: "Tap Continue with Auto-Debit again in a moment.",
        });
        return;
      }
      setMandateInProgress(true);
      setMandateError(null);
      setIosPaymentTimedOut(false);
      try {
        sessionStorage.setItem(
          "tvs_pending_mandate",
          JSON.stringify({ bookingId: booking.id, at: Date.now() }),
        );
      } catch {/* sessionStorage may be unavailable in private mode */}

      const p = prewarmedSubscription;
      submitRazorpayEmbeddedForm("https://api.razorpay.com/v1/checkout/embedded", {
        key_id: p.key,
        subscription_id: p.subscription_id,
        name: "Travelsate",
        description: "Authorise EMI auto-debit mandate",
        recurring: 1,
        callback_url: `${BACKEND_URL}/api/payments/mandate-redirect-return`,
        "callback_method": "post",
        "prefill[name]": p.prefill?.name || "",
        "prefill[email]": p.prefill?.email || "",
        "prefill[contact]": p.prefill?.contact || "",
      });
      // 30s safety net so the customer isn't stuck on a frozen spinner if
      // the navigation never happens (network glitch, content blocker, etc.).
      window.setTimeout(() => {
        setMandateInProgress(false);
        setIosPaymentTimedOut(true);
      }, 30000);
      return;
    }

    setMandateInProgress(true);
    setMandateError(null);
    try {
      await authoriseMandate(booking.id);
      const refreshed = await axios.get(`${API}/bookings/${booking.id}`);
      setBooking(refreshed.data);
      toast.success("Auto-debit set up. Your booking is confirmed!");
      setMandateInterceptOpen(false);
      setBookingConfirmed(true);
      setStep(7);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not set up auto-debit";
      setMandateError(msg);
    } finally {
      setMandateInProgress(false);
    }
  };

  // Customer chose to skip mandate for now. Booking stays `upfront_paid` and
  // they can retry from "My Bookings". We advance to step 7 with a banner.
  const deferMandate = () => {
    setMandateInterceptOpen(false);
    setBookingConfirmed(true);
    setStep(7);
    toast.message("Auto-debit setup deferred", {
      description: "Your seat is reserved. Finish auto-debit setup from My Bookings to confirm your EMI schedule.",
    });
  };


  const handlePayment = async () => {
    if (!booking) return;

    const hasInstallments = costs && costs.paymentOptions && costs.paymentOptions.length > 0;
    const isFullPayment = paymentPlan === "full" || !hasInstallments;
    const planKey = isFullPayment ? "full" : paymentPlan;
    const amountToCharge = isFullPayment ? booking.total_cost : booking.booking_cost;

    // ── iOS Safari → REDIRECT MODE (synchronous form POST) ──────────────
    // ITP breaks the Razorpay modal iframe on iPad/iPhone Safari. We POST a
    // hidden form to Razorpay's hosted-checkout endpoint synchronously inside
    // this click handler (NO awaits between the user gesture and form.submit
    // — Safari strips the user-activation flag the moment a promise resolves
    // post-click, which causes silent navigation blocks). For this to work
    // we rely on the `prewarmedOrder` minted ahead of time by the Step-6
    // effect; if it's stale or absent we surface a friendly "preparing"
    // message and let the pre-warm useEffect catch up — much better than
    // doing an `await` here and losing the click-context.
    if (isIOSSafariLike()) {
      const orderReady =
        prewarmedOrder &&
        prewarmedOrder.plan === planKey &&
        prewarmedOrder.bookingId === booking.id &&
        Math.abs(prewarmedOrder.amount - amountToCharge) < 1;

      if (!orderReady) {
        // Kick the pre-warm into shape and ask the customer to tap again.
        // The Step-6 useEffect watches prewarmedOrder + razorpayReady and
        // will mint a fresh order_id within ~1s. Clearing the stale one
        // here forces the effect to re-run on its next render.
        setPrewarmedOrder(null);
        toast.message("Preparing secure payment…", {
          description: "Tap Pay again in a moment.",
        });
        return;
      }

      // ── EVERYTHING BELOW MUST BE SYNCHRONOUS ──────────────────────────
      setPaymentBusy(true);
      setIosPaymentTimedOut(false);
      try {
        sessionStorage.setItem(
          "tvs_pending_payment",
          JSON.stringify({ bookingId: booking.id, plan: planKey, at: Date.now() }),
        );
      } catch {/* sessionStorage may be unavailable in private mode */}

      const o = prewarmedOrder.options;
      const pf = o.prefill || {};
      submitRazorpayEmbeddedForm("https://api.razorpay.com/v1/checkout/embedded", {
        key_id: o.key,
        order_id: o.order_id,
        amount: o.amount,
        currency: o.currency,
        name: o.name,
        description: o.description,
        callback_url: `${BACKEND_URL}/api/payments/redirect-return`,
        "callback_method": "post",
        // Prefill so Razorpay's hosted page skips its contact-collection
        // step (iPad Safari customers were re-typing details from Step 4
        // because the previous form-POST helper dropped empty/missing
        // prefill keys — see Iteration 34).
        "prefill[name]": pf.name || "",
        "prefill[email]": pf.email || "",
        "prefill[contact]": pf.contact || "",
      });
      // Safety net: if navigation never happens (network glitch, ad-blocker
      // dropping the POST, etc.) reset the busy lock + show a Retry button
      // so the customer isn't stranded watching a frozen spinner.
      window.setTimeout(() => {
        setPaymentBusy(false);
        setIosPaymentTimedOut(true);
      }, 30000);
      return;
    }

    // Shared handler/dismiss callbacks live outside the sync open() path so
    // both pre-warmed and fallback flows reuse the exact same wiring.
    const handler = async (razorpayResponse) => {
      try {
        const verifyRes = await axios.post(`${API}/payments/verify`, {
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
          booking_id: booking.id,
        });
        try {
          const refreshed = await axios.get(`${API}/bookings/${booking.id}`);
          setBooking(refreshed.data);
        } catch (refreshErr) {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.warn("Could not refresh booking after payment:", refreshErr?.message || refreshErr);
          }
        }
        const isEmiPlan = !isFullPayment && verifyRes?.data?.payment_status === "upfront_paid";
        if (isEmiPlan) {
          setPaymentBusy(false);
          setMandateError(null);
          setMandateInterceptOpen(true);
          return;
        }
        if (isFullPayment) {
          toast.success("Payment successful! Your booking is fully confirmed.");
        }
        setPaymentBusy(false);
        setBookingConfirmed(true);
        setStep(7);
        // Burn the warmed order — it's been used and the order_id is now spent.
        setPrewarmedOrder(null);
      } catch (error) {
        setPaymentBusy(false);
        toast.error("Payment verification failed");
      }
    };

    const ondismiss = () => {
      // Customer closed Razorpay before completing — clear the busy state and
      // burn the warmed order so the next attempt creates a fresh one.
      setPaymentBusy(false);
      setPrewarmedOrder(null);
    };

    // ── FAST PATH: pre-warmed order matches → synchronous razorpay.open() ──
    // This is the path iPad/iPhone Safari needs. No await between user click
    // and the popup spawn, so the user-gesture context is preserved.
    if (
      prewarmedOrder &&
      prewarmedOrder.plan === planKey &&
      prewarmedOrder.bookingId === booking.id &&
      Math.abs(prewarmedOrder.amount - amountToCharge) < 1 &&
      typeof window !== "undefined" &&
      window.Razorpay
    ) {
      setPaymentBusy(true);
      try {
        const rzp = new window.Razorpay({ ...prewarmedOrder.options, handler, modal: { ondismiss } });
        rzp.open();
        return;
      } catch (e) {
        // Fall through to the slow path on any constructor / open() failure.
        setPaymentBusy(false);
        setPrewarmedOrder(null);
      }
    }

    // ── SLOW PATH: fetch order then open. Works on Chrome/desktop but Safari
    //    iPad may block the popup because of the await below.
    setPaymentBusy(true);
    try {
      const response = await axios.post(`${API}/payments/create-order`, {
        booking_id: booking.id,
        payment_plan: planKey,
        amount: amountToCharge,
      });
      const options = {
        key: response.data.key,
        amount: response.data.amount,
        currency: response.data.currency,
        order_id: response.data.order_id,
        name: "Travelsate",
        description: `Booking for ${selectedDestination.name}`,
        handler,
        modal: { ondismiss },
        theme: { color: "#D4AF37" },
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      setPaymentBusy(false);
      toast.error("Failed to initiate payment: " + (error.response?.data?.detail || error.message));
    }
  };

  const luxuryCost = calculateCosts("luxury");
  const premiumCost = calculateCosts("premium");
  const basicCost = calculateCosts("basic");
  const costs = calculateCosts();

  // Pre-warm a Razorpay order on Step 6 the moment we know booking + plan.
  // The order needs ~300-800ms to create; doing it BEFORE the user taps Pay
  // means razorpay.open() can fire synchronously inside the click — which is
  // mandatory for iPad/iPhone Safari (their Intelligent Tracking Prevention
  // blocks popups spawned after an async gap from the user gesture).
  // NOTE: must live AFTER `const costs = calculateCosts()` above so React
  // doesn't hit the temporal-dead-zone on `costs` during mount.
  useEffect(() => {
    if (step !== 6 || !booking || !costs) return;
    // Wait for the Razorpay script before pre-warming the order. Pre-warming
    // an order without the SDK ready is pointless — and on iPad Safari the
    // network call would race with the script load and waste an order_id.
    if (!razorpayReady) return;
    const hasInstallments = costs.paymentOptions && costs.paymentOptions.length > 0;
    const isFullPayment = paymentPlan === "full" || !hasInstallments;
    const planKey = isFullPayment ? "full" : paymentPlan;
    if (!planKey) return;
    const amountToCharge = isFullPayment ? booking.total_cost : booking.booking_cost;
    if (
      prewarmedOrder &&
      prewarmedOrder.plan === planKey &&
      prewarmedOrder.bookingId === booking.id &&
      Math.abs(prewarmedOrder.amount - amountToCharge) < 1
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/payments/create-order`, {
          booking_id: booking.id,
          payment_plan: planKey,
          amount: amountToCharge,
        });
        if (cancelled) return;
        const options = {
          key: res.data.key,
          amount: res.data.amount,
          currency: res.data.currency,
          order_id: res.data.order_id,
          name: "Travelsate",
          description: `Booking for ${selectedDestination?.name || "trip"}`,
          theme: { color: "#D4AF37" },
          // Prefill the customer's details from Step 4 so Razorpay's hosted
          // checkout skips its contact-collection screen on iPad Safari.
          // Without this, customers re-type name/email/phone they've already
          // entered — see Iteration 34 for the bug report from production.
          prefill: res.data.prefill || {},
        };
        setPrewarmedOrder({ plan: planKey, amount: amountToCharge, bookingId: booking.id, options });
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("Razorpay pre-warm failed (will retry on click):", err?.message || err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [step, booking, paymentPlan, costs, prewarmedOrder, selectedDestination, razorpayReady]);

  // Pre-warm a Razorpay subscription the moment the mandate-intercept dialog
  // opens (EMI plans only). The "Continue with Auto-Debit" click MUST submit
  // the form synchronously on iOS Safari — see startMandate above — and we
  // can't do an `await create-subscription` inside that handler without
  // losing the user-activation flag. Pre-warming here means the subscription
  // is already minted by the time the customer reads the dialog and clicks.
  useEffect(() => {
    if (!mandateInterceptOpen || !booking?.id) return;
    if (booking.razorpay_subscription_id) return; // already authenticated/in-flight
    if (
      prewarmedSubscription &&
      prewarmedSubscription.bookingId === booking.id &&
      prewarmedSubscription.subscription_id
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/payments/create-subscription`, {
          booking_id: booking.id,
        });
        if (cancelled) return;
        setPrewarmedSubscription({
          bookingId: booking.id,
          key: res.data.key,
          subscription_id: res.data.subscription_id,
          prefill: res.data.prefill || {},
          // Razorpay's hosted mandate-auth URL. iPad Safari customers can
          // scan this as a QR with their phone and complete the mandate on
          // their iPhone where UPI AutoPay works natively (Razorpay hides
          // UPI on Mac UA, which iPadOS reports by default).
          short_url: res.data.short_url || null,
        });
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("Razorpay subscription pre-warm failed (will retry on click):", err?.message || err);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [mandateInterceptOpen, booking, prewarmedSubscription]);



  // Auto-enable auto-pay for installment plans
  useEffect(() => {
    if (costs && costs.paymentOptions && costs.paymentOptions.length > 0) {
      // Auto-enable auto-pay for installment plans
      if (!autoPayEnabled) {
        setAutoPayEnabled(true);
      }
    } else {
      // Disable auto-pay if no installments available
      if (autoPayEnabled) {
        setAutoPayEnabled(false);
      }
    }
  }, [costs]);

  // Guard against accidental refresh / browser-back / tab-close while a
  // Razorpay payment is in flight. Triggers the browser's native "Leave site?"
  // dialog so the customer doesn't blow away an in-progress transaction.
  useEffect(() => {
    if (!paymentBusy) return undefined;
    const handler = (e) => {
      e.preventDefault();
      // Modern browsers ignore the custom string but require this setter.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [paymentBusy]);

  // Guard: if the currently-selected package becomes unavailable (e.g. live
  // flight/hotel data updated mid-session and the chosen tier is now blocked),
  // deselect so the customer can't proceed to Step 6 with a phantom package.
  useEffect(() => {
    if (!selectedPackage) return;
    const tierCost =
      selectedPackage === "luxury" ? luxuryCost :
      selectedPackage === "premium" ? premiumCost :
      selectedPackage === "basic" ? basicCost : null;
    if (tierCost && tierCost.packageAvailable === false) {
      setSelectedPackage(null);
    }
  }, [selectedPackage, luxuryCost?.packageAvailable, premiumCost?.packageAvailable, basicCost?.packageAvailable]);

  // Guard: if the currently-selected EMI plan stops being viable (e.g. user
  // goes back to Step 3 and shortens the trip into the buffer window), reset
  // to the longest available plan or fall back to full payment. Without this,
  // the stale paymentPlan was being sent to /create-booking and the customer
  // could pay 1% upfront for an impossible-to-honour EMI plan.
  useEffect(() => {
    if (paymentPlan === "full") return;
    const opts = costs?.paymentOptions || [];
    if (opts.length === 0) {
      setPaymentPlan("full");
      return;
    }
    if (!opts.some((o) => o.type === paymentPlan)) {
      // Prefer the longest interval available so installment count stays low.
      const preferred = ["monthly", "biweekly", "weekly"]
        .find((t) => opts.some((o) => o.type === t));
      setPaymentPlan(preferred || "full");
    }
  }, [costs?.paymentOptions, paymentPlan]);

  return (
    <>
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-md border-b border-white/5">
        <div className="container mx-auto px-4 max-w-full flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <a href="/" data-testid="nav-logo-link">
              <img src="/logo.svg" alt="Travelsate" className="h-7" />
            </a>
            <span className="text-[#D4AF37]/70 text-xs tracking-wide hidden sm:inline font-bold italic">Book Now, Pay Later</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              data-testid="booking-nav-home"
              onClick={() => navigate("/")}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-white/70 text-sm hover:bg-white/5 hover:text-white transition-all"
            >
              <HomeIcon className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Home</span>
            </button>
            <button
              data-testid="booking-nav-about"
              onClick={() => navigate("/about")}
              className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-white/70 text-sm hover:bg-white/5 hover:text-white transition-all"
            >
              <Info className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">About Us</span>
            </button>
          {isAuthenticated && user && (
          <div className="relative">
            <button
              data-testid="profile-button"
              onClick={() => setShowProfile(!showProfile)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 hover:border-[#D4AF37]/40 transition-colors"
            >
              <div className="w-7 h-7 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                <User className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <span className="text-white/80 text-sm hidden sm:inline">{user?.name || "My Profile"}</span>
            </button>

            {showProfile && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => { setShowProfile(false); setShowBookings(false); }} />
                <div className="absolute right-0 top-12 w-80 bg-[#141414] border border-white/10 rounded-lg shadow-2xl z-50 overflow-hidden" data-testid="profile-dropdown">
                  <div className="p-4 border-b border-white/5 bg-[#D4AF37]/5">
                    <p className="text-white font-medium text-base" data-testid="profile-name">{user?.name || "—"}</p>
                    <p className="text-white/40 text-xs mt-0.5">Travelsate Member</p>
                  </div>
                  <div className="p-3 space-y-2.5">
                    {user?.email && (
                      <div className="flex items-center gap-3 text-sm">
                        <Mail className="w-4 h-4 text-white/30 flex-shrink-0" />
                        <span className="text-white/70 truncate" data-testid="profile-email">{user.email}</span>
                      </div>
                    )}
                    {user?.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="w-4 h-4 text-white/30 flex-shrink-0" />
                        <span className="text-white/70" data-testid="profile-phone">{user.phone}</span>
                      </div>
                    )}
                    {user?.pincode && (
                      <div className="flex items-center gap-3 text-sm">
                        <MapPin className="w-4 h-4 text-white/30 flex-shrink-0" />
                        <span className="text-white/70" data-testid="profile-pincode">Pin: {user.pincode}</span>
                      </div>
                    )}
                  </div>
                  <div className="border-t border-white/5">
                    <button
                      data-testid="my-bookings-button"
                      onClick={() => {
                        setShowBookings(!showBookings);
                        if (!showBookings) fetchMyBookings();
                      }}
                      className="flex items-center gap-2 w-full px-4 py-3 text-sm text-white/70 hover:bg-white/5 transition-colors"
                    >
                      <Plane className="w-4 h-4" />
                      My Bookings
                      <ChevronLeft className={`w-4 h-4 ml-auto transition-transform ${showBookings ? '-rotate-90' : 'rotate-180'}`} />
                    </button>

                    {showBookings && (
                      <div className="max-h-72 overflow-y-auto border-t border-white/5" data-testid="bookings-list">
                        {loadingBookings ? (
                          <div className="flex items-center justify-center py-6">
                            <div className="animate-spin h-4 w-4 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
                          </div>
                        ) : myBookings.length === 0 ? (
                          <p className="text-white/40 text-sm text-center py-6">No bookings yet</p>
                        ) : (
                          myBookings.map((b, i) => {
                            const statusColor = b.payment_status === "completed" ? "text-green-400" : "text-amber-400";
                            return (
                              <button
                                type="button"
                                key={b.id || i}
                                onClick={async () => {
                                  // Open detail modal. Refetch the booking by id
                                  // so it always has booking_schedule + the latest
                                  // status — list payload may be slimmer than
                                  // /api/bookings/{id}.
                                  setDetailBooking(b);
                                  setLoadingDetail(true);
                                  try {
                                    const res = await axios.get(`${API}/bookings/${b.id}`);
                                    if (res.data) setDetailBooking(res.data);
                                  } catch (e) {
                                    if (process.env.NODE_ENV !== "production") {
                                      // eslint-disable-next-line no-console
                                      console.warn("Booking detail refresh failed; falling back to list row:", e?.message || e);
                                    }
                                  } finally {
                                    setLoadingDetail(false);
                                  }
                                }}
                                className="w-full text-left px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/[0.04] transition-colors cursor-pointer"
                                data-testid={`booking-item-${i}`}
                              >
                                <div className="flex items-start justify-between">
                                  <div>
                                    <p className="text-white text-sm font-medium">{b.destination_name}</p>
                                    <p className="text-white/50 text-xs mt-0.5 capitalize">{b.package_type} Package</p>
                                  </div>
                                  <span className={`text-xs font-medium capitalize ${statusColor}`}>
                                    {b.payment_status || "pending"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 mt-2 text-xs text-white/40">
                                  <span>{b.start_date?.slice(0, 10)} → {b.end_date?.slice(0, 10)}</span>
                                  <span>•</span>
                                  <span>{b.adults} Adult{b.adults > 1 ? 's' : ''}{b.children > 0 ? `, ${b.children} Child` : ''}{b.infants > 0 ? `, ${b.infants} Infant` : ''}</span>
                                </div>
                                <div className="flex items-center justify-between mt-2">
                                  <span className="text-[#D4AF37] text-sm font-medium">₹{(b.total_cost || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                  <span className="text-white/30 text-[10px]">{b.id?.slice(0, 8).toUpperCase()}</span>
                                </div>
                              </button>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                  <div className="p-3 border-t border-white/5">
                    {user?.is_admin && (
                      <button
                        data-testid="admin-panel-button"
                        onClick={() => {
                          setShowProfile(false);
                          navigate("/admin");
                        }}
                        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors mb-1"
                      >
                        <Shield className="w-4 h-4" />
                        Admin Panel
                      </button>
                    )}
                    <button
                      data-testid="logout-button"
                      onClick={() => {
                        onLogout();
                        navigate("/");
                      }}
                      className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-full">
        <div className={`mx-auto w-full ${step === 0 || step === 1 ? 'max-w-7xl' : 'max-w-4xl'}`}>
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl lg:text-5xl text-white mb-4">Your Journey Awaits</h1>
            {departureCity && (
              <p className="text-white/60 mb-4">
                Traveling from <span className="text-[#D4AF37] font-medium">{departureCity}</span>
              </p>
            )}
            {luxuryCost && step === 5 && (
              <div className="flex flex-col items-center gap-2 mb-4">
                <div className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-full px-4 py-2">
                  <span className="text-green-400 text-sm font-medium" data-testid="live-prices-badge">Live Prices from Google Flights & Hotels</span>
                </div>
                <p className="text-white/60 text-xs">
                  Real-time rates powered by Google
                </p>
              </div>
            )}
            {(loadingHotelPrices || loadingFlightPrices) && step === 5 && (
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="animate-spin h-4 w-4 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
                <span className="text-white/60 text-sm">Fetching live prices...</span>
              </div>
            )}
            <div className="flex items-center justify-center gap-2 text-white/60">
              {[0, 1, 2, 3, 4, 5, 6].map((s) => (
                <div
                  key={s}
                  className={`h-1 w-10 rounded-full transition-colors ${
                    s <= step ? "bg-[#D4AF37]" : "bg-white/20"
                  }`}
                  data-testid={`step-indicator-${s}`}
                />
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {step === 0 && (
                <div className="space-y-8" data-testid="travel-type-step">
                  <div className="text-center">
                    <Globe className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                    <h2 className="font-serif text-3xl text-white mb-2">Where would you like to go?</h2>
                    <p className="text-white/60">Choose your travel type to explore destinations</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
                    <button
                      data-testid="domestic-option"
                      onClick={() => { setTravelType("domestic"); setStep(1); }}
                      className="group relative overflow-hidden rounded-xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-300"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src="https://images.pexels.com/photos/3881106/pexels-photo-3881106.jpeg?auto=compress&cs=tinysrgb&w=800"
                          alt="Domestic - India"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="flex items-center gap-2 mb-1">
                          <MapPin className="w-4 h-4 text-[#D4AF37]" />
                          <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-medium">Explore India</span>
                        </div>
                        <h3 className="font-serif text-2xl text-white">Domestic</h3>
                        <p className="text-white/60 text-sm mt-1">Handpicked destinations across India</p>
                      </div>
                    </button>

                    <button
                      data-testid="international-option"
                      onClick={() => { setTravelType("international"); setStep(1); }}
                      className="group relative overflow-hidden rounded-xl border border-white/10 hover:border-[#D4AF37]/50 transition-all duration-300"
                    >
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800"
                          alt="International"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <div className="flex items-center gap-2 mb-1">
                          <Globe className="w-4 h-4 text-[#D4AF37]" />
                          <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-medium">Explore the World</span>
                        </div>
                        <h3 className="font-serif text-2xl text-white">International</h3>
                        <p className="text-white/60 text-sm mt-1">Iconic destinations worldwide</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {step === 1 && (
                <Card className="glass-card p-8" data-testid="destination-search-step">
                  <div className="space-y-6">
                    <div className="text-center">
                      <Search className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                      <h2 className="font-serif text-3xl text-white mb-2">Choose Your Destination</h2>
                      <p className="text-white/60">Where would you like to explore?</p>
                    </div>
                    <Input
                      data-testid="destination-search-input"
                      type="text"
                      placeholder="Search destinations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] w-full"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
                      {filteredDestinations.map((dest) => (
                        <Card
                          key={dest.id}
                          data-testid={`destination-${dest.id}`}
                          className="bg-white/5 border-white/10 cursor-pointer hover:border-[#D4AF37] hover:scale-105 transition-all overflow-hidden w-full group"
                          onClick={() => handleDestinationSelect(dest)}
                        >
                          <div className="relative">
                            <img src={dest.image_url} alt={dest.name} className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                            {dest.is_test_package && (
                              <div
                                className="absolute top-2 right-2 bg-yellow-500 text-black text-[10px] font-bold px-2 py-1 rounded shadow-lg tracking-wider"
                                data-testid={`test-badge-${dest.id}`}
                              >
                                ₹1 TEST
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 p-4">
                              <h3 className="font-serif text-xl text-white mb-1 truncate">{dest.name}</h3>
                              <p className="text-white/80 text-sm truncate">{dest.location}</p>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => { setStep(0); setTravelType(null); setSearchQuery(""); }}
                      className="bg-transparent border-white/20 text-white hover:bg-white/5"
                      data-testid="back-to-travel-type-button"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" /> Back to Travel Type
                    </Button>
                  </div>
                </Card>
              )}

              {step === 3 && (
                <Card className="glass-card p-8" data-testid="date-selection-step">
                  <div className="space-y-6">
                    <div className="text-center">
                      <CalendarIcon className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                      <h2 className="font-serif text-3xl text-white mb-2">Select Your Dates</h2>
                      <p className="text-white/60">When do you plan to travel?</p>
                    </div>

                    {/* ── Travel Insights panel ────────────────────────────
                        Sits between the "Select Your Dates" banner and the
                        "Traveling from" card per the spec. Renders only when
                        the destination has at least one of the four curated
                        fields (best_time / weather / travel_tip / travel_avoid)
                        — populated via /app/backend/migrate_travel_insights.py
                        from the Domestic Destinations.docx source.
                        Destinations without curated content (e.g. Khajuraho,
                        Taj Mahal, Lakshadweep) gracefully render no panel
                        rather than awkward placeholder copy. */}
                    {selectedDestination &&
                      (selectedDestination.best_time ||
                       selectedDestination.weather ||
                       selectedDestination.travel_tip ||
                       selectedDestination.travel_avoid) && (
                      <div
                        className="rounded-lg border border-[#D4AF37]/20 bg-gradient-to-br from-[#D4AF37]/[0.04] to-transparent p-5"
                        data-testid="travel-insights-panel"
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                          <h3 className="text-white font-medium text-sm tracking-wide">
                            Travel insights — <span className="text-[#D4AF37]">{selectedDestination.name}</span>
                          </h3>
                        </div>

                        {/* Top row: best time + weather (compact, two-up on
                            tablet/desktop, stacked on mobile). */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                          {selectedDestination.best_time && (
                            <div
                              className="flex items-start gap-2.5 rounded-md border border-white/10 bg-white/[0.02] p-3"
                              data-testid="travel-insights-best-time"
                            >
                              <CalendarDays className="w-4 h-4 mt-0.5 text-[#D4AF37] shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">
                                  Best time to visit
                                </p>
                                <p className="text-white/85 text-xs leading-snug">
                                  {selectedDestination.best_time}
                                </p>
                              </div>
                            </div>
                          )}
                          {selectedDestination.weather && (
                            <div
                              className="flex items-start gap-2.5 rounded-md border border-white/10 bg-white/[0.02] p-3"
                              data-testid="travel-insights-weather"
                            >
                              <CloudSun className="w-4 h-4 mt-0.5 text-[#D4AF37] shrink-0" />
                              <div className="min-w-0">
                                <p className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">
                                  Weather
                                </p>
                                <p className="text-white/85 text-xs leading-snug">
                                  {selectedDestination.weather}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Pro tip — emerald accent so it reads as "do this". */}
                        {selectedDestination.travel_tip && (
                          <div
                            className="flex items-start gap-2.5 rounded-md border border-emerald-400/20 bg-emerald-400/[0.04] p-3 mb-2"
                            data-testid="travel-insights-tip"
                          >
                            <Lightbulb className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[10px] uppercase tracking-wider text-emerald-200/70 mb-0.5">
                                Pro tip
                              </p>
                              <p className="text-white/85 text-xs leading-snug">
                                {selectedDestination.travel_tip}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* What to avoid — rose accent so it reads as "don't". */}
                        {selectedDestination.travel_avoid && (
                          <div
                            className="flex items-start gap-2.5 rounded-md border border-rose-400/20 bg-rose-400/[0.04] p-3"
                            data-testid="travel-insights-avoid"
                          >
                            <ShieldAlert className="w-4 h-4 mt-0.5 text-rose-300 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[10px] uppercase tracking-wider text-rose-200/70 mb-0.5">
                                What to avoid
                              </p>
                              <p className="text-white/85 text-xs leading-snug">
                                {selectedDestination.travel_avoid}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Departure City */}
                    <div className="border border-white/10 rounded-lg p-4" data-testid="departure-city-section">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Plane className="w-4 h-4 text-[#D4AF37]" />
                          <Label className="text-white text-sm">Traveling from</Label>
                        </div>
                        {!showDeparturePicker && departureCity && (
                          <button
                            data-testid="change-departure-button"
                            onClick={() => setShowDeparturePicker(true)}
                            className="text-[#D4AF37] text-xs hover:underline"
                          >
                            Change
                          </button>
                        )}
                      </div>
                      {!showDeparturePicker ? (
                        departureCity ? (
                          <p className="text-white font-medium" data-testid="departure-city-display">
                            {departureCity} <span className="text-white/40 text-sm">({departureAirport})</span>
                          </p>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setShowDeparturePicker(true)}
                            className="text-[#D4AF37] hover:underline text-sm"
                            data-testid="departure-city-empty-cta"
                          >
                            + Select your departure airport
                          </button>
                        )
                      ) : (
                        <div className="space-y-3">
                          <p className="text-white/50 text-xs">Search or select your departure city</p>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                            <Input
                              data-testid="departure-search-input"
                              type="text"
                              placeholder="Search city, airport code or country..."
                              value={departureSearch}
                              onChange={(e) => setDepartureSearch(e.target.value)}
                              className="bg-transparent border-white/20 text-white focus:border-[#D4AF37] h-9 text-sm pl-9"
                            />
                          </div>
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 max-h-52 overflow-y-auto">
                            {filteredAirports.map((ap) => (
                              <button
                                key={ap.code}
                                data-testid={`airport-${ap.code}`}
                                onClick={() => {
                                  setDepartureCity(ap.city);
                                  setDepartureAirport(ap.code);
                                  setShowDeparturePicker(false);
                                  setDepartureSearch("");
                                }}
                                className={`px-2 py-2 rounded-md text-xs font-medium border transition-all ${
                                  departureAirport === ap.code
                                    ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                                    : "bg-white/5 border-white/10 text-white/70 hover:border-white/30"
                                }`}
                              >
                                {ap.city}
                                <span className="block text-[10px] text-white/40 mt-0.5">{ap.code} • {ap.country}</span>
                              </button>
                            ))}
                            {filteredAirports.length === 0 && (
                              <p className="col-span-full text-white/30 text-xs text-center py-3">No airports found</p>
                            )}
                          </div>
                          <button
                            onClick={() => { setShowDeparturePicker(false); setDepartureSearch(""); }}
                            className="text-white/40 text-xs hover:text-white/60"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-white mb-2 block">Start Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white h-12"
                              data-testid="start-date-button"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {startDate ? format(startDate, "PPP") : <span>Pick start date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#1a1a1a] border-white/20" align="start">
                            <Calendar
                              mode="single"
                              selected={startDate}
                              onSelect={(date) => {
                                setStartDate(date);
                                if (endDate && date && endDate < date) {
                                  setEndDate(null);
                                }
                                if (date) {
                                  setEndDateOpen(true);
                                }
                              }}
                              disabled={(date) => date < new Date()}
                              initialFocus
                              className="rounded-md border-0"
                              data-testid="start-date-calendar"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="text-white mb-2 block">End Date</Label>
                        <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className="w-full justify-start text-left font-normal bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white h-12"
                              data-testid="end-date-button"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {endDate ? format(endDate, "PPP") : <span>Pick end date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#1a1a1a] border-white/20" align="start">
                            <Calendar
                              mode="single"
                              selected={endDate}
                              onSelect={(date) => { setEndDate(date); setEndDateOpen(false); }}
                              disabled={(date) => date < (startDate || new Date())}
                              fromDate={startDate || new Date()}
                              initialFocus
                              className="rounded-md border-0"
                              data-testid="end-date-calendar"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        onClick={() => setStep(1)}
                        className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5"
                        data-testid="back-to-destination-button"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" /> Back
                      </Button>
                      <Button
                        onClick={() => setStep(4)}
                        disabled={!startDate || !endDate || !departureAirport}
                        className="flex-1 bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                        data-testid="continue-to-travelers-button"
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {step === 4 && (
                <Card className="glass-card p-8" data-testid="travelers-selection-step">
                  <div className="space-y-6">
                    <div className="text-center">
                      <Users className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                      <h2 className="font-serif text-3xl text-white mb-2">Travelers</h2>
                      <p className="text-white/60">How many people are traveling?</p>
                    </div>
                    <div className="space-y-4">
                      {/* Adults */}
                      <div className="border border-white/10 rounded p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">Adults</p>
                            <p className="text-white/60 text-sm">Age 12+</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <Button variant="outline" size="icon" onClick={() => handleAdultsChange(adults - 1)} className="bg-transparent border-white/20 text-white" data-testid="decrease-adults">-</Button>
                            <span className="text-white w-8 text-center" data-testid="adults-count">{adults}</span>
                            <Button variant="outline" size="icon" onClick={() => handleAdultsChange(adults + 1)} className="bg-transparent border-white/20 text-white" data-testid="increase-adults">+</Button>
                          </div>
                        </div>
                        {adults > 0 && (
                          <div className="space-y-3 pt-2 border-t border-white/5">
                            {adultNames.map((name, i) => (
                              <div key={`adult-${i}`}>
                                <div className="flex gap-2 items-start">
                                <div className="flex gap-1 pt-1 flex-shrink-0">
                                  <button
                                    data-testid={`adult-gender-male-${i}`}
                                    onClick={() => { const u = [...adultGenders]; u[i] = "male"; setAdultGenders(u); }}
                                    className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${adultGenders[i] === "male" ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]" : "bg-white/5 border-white/10 text-white/50 hover:border-white/25"}`}
                                  >M</button>
                                  <button
                                    data-testid={`adult-gender-female-${i}`}
                                    onClick={() => { const u = [...adultGenders]; u[i] = "female"; setAdultGenders(u); }}
                                    className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${adultGenders[i] === "female" ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]" : "bg-white/5 border-white/10 text-white/50 hover:border-white/25"}`}
                                  >F</button>
                                </div>
                                <Input
                                  data-testid={`adult-name-${i}`}
                                  type="text"
                                  placeholder={`Adult ${i + 1} full name as per ID proof`}
                                  value={name}
                                  required
                                  onChange={(e) => {
                                    const updated = [...adultNames];
                                    updated[i] = sanitizeNameInput(e.target.value);
                                    setAdultNames(updated);
                                  }}
                                  className={`bg-transparent border-white/20 text-white focus:border-[#D4AF37] h-10 text-sm flex-1 ${name.trim() && getNameError(name) ? 'border-red-500/60' : !name.trim() ? 'border-red-500/40' : ''}`}
                                />
                              </div>
                              {name.trim() && getNameError(name) && (
                                <p className="text-red-400 text-[10px] ml-14">{getNameError(name)}</p>
                              )}
                              <MealChips
                                value={adultMeals[i] || ""}
                                onChange={(v) => { const u = [...adultMeals]; u[i] = v; setAdultMeals(u); }}
                                testid={`adult-meal-${i}`}
                                required
                              />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Children */}
                      <div className="border border-white/10 rounded p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">Children</p>
                            <p className="text-white/60 text-sm">Age 2-11</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <Button variant="outline" size="icon" onClick={() => handleChildrenChange(children - 1)} className="bg-transparent border-white/20 text-white" data-testid="decrease-children">-</Button>
                            <span className="text-white w-8 text-center" data-testid="children-count">{children}</span>
                            <Button variant="outline" size="icon" onClick={() => handleChildrenChange(children + 1)} className="bg-transparent border-white/20 text-white" data-testid="increase-children">+</Button>
                          </div>
                        </div>
                        {children > 0 && (
                          <div className="space-y-3 pt-2 border-t border-white/5">
                            {childNames.map((name, i) => (
                              <div key={`child-${i}`}>
                                <div className="flex gap-2 items-start">
                                <div className="flex gap-1 pt-1 flex-shrink-0">
                                  <button
                                    data-testid={`child-gender-male-${i}`}
                                    onClick={() => { const u = [...childGenders]; u[i] = "male"; setChildGenders(u); }}
                                    className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${childGenders[i] === "male" ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]" : "bg-white/5 border-white/10 text-white/50 hover:border-white/25"}`}
                                  >M</button>
                                  <button
                                    data-testid={`child-gender-female-${i}`}
                                    onClick={() => { const u = [...childGenders]; u[i] = "female"; setChildGenders(u); }}
                                    className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${childGenders[i] === "female" ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]" : "bg-white/5 border-white/10 text-white/50 hover:border-white/25"}`}
                                  >F</button>
                                </div>
                                <Input
                                  data-testid={`child-name-${i}`}
                                  type="text"
                                  placeholder={`Child ${i + 1} full name as per ID proof`}
                                  value={name}
                                  required
                                  onChange={(e) => {
                                    const updated = [...childNames];
                                    updated[i] = sanitizeNameInput(e.target.value);
                                    setChildNames(updated);
                                  }}
                                  className={`bg-transparent border-white/20 text-white focus:border-[#D4AF37] h-10 text-sm flex-1 ${name.trim() && getNameError(name) ? 'border-red-500/60' : !name.trim() ? 'border-red-500/40' : ''}`}
                                />
                              </div>
                              {name.trim() && getNameError(name) && (
                                <p className="text-red-400 text-[10px] ml-14">{getNameError(name)}</p>
                              )}
                              <MealChips
                                value={childMeals[i] || ""}
                                onChange={(v) => { const u = [...childMeals]; u[i] = v; setChildMeals(u); }}
                                testid={`child-meal-${i}`}
                                required
                              />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Infants */}
                      <div className="border border-white/10 rounded p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium">Infants</p>
                            <p className="text-white/60 text-sm">Under 2</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <Button variant="outline" size="icon" onClick={() => handleInfantsChange(infants - 1)} className="bg-transparent border-white/20 text-white" data-testid="decrease-infants">-</Button>
                            <span className="text-white w-8 text-center" data-testid="infants-count">{infants}</span>
                            <Button variant="outline" size="icon" onClick={() => handleInfantsChange(infants + 1)} className="bg-transparent border-white/20 text-white" data-testid="increase-infants">+</Button>
                          </div>
                        </div>
                        {infants > 0 && (
                          <div className="space-y-3 pt-2 border-t border-white/5">
                            {infantNames.map((name, i) => {
                              const age = getInfantAgeAtTravel(infantDOBs[i]);
                              const willBe2OrOlder = age && (age.years >= 2);
                              return (
                                <div key={`infant-${i}`} className="space-y-2">
                                  <div className="flex gap-2 items-start">
                                    <div className="flex gap-1 pt-1 flex-shrink-0">
                                      <button
                                        data-testid={`infant-gender-male-${i}`}
                                        onClick={() => { const u = [...infantGenders]; u[i] = "male"; setInfantGenders(u); }}
                                        className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${infantGenders[i] === "male" ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]" : "bg-white/5 border-white/10 text-white/50 hover:border-white/25"}`}
                                      >M</button>
                                      <button
                                        data-testid={`infant-gender-female-${i}`}
                                        onClick={() => { const u = [...infantGenders]; u[i] = "female"; setInfantGenders(u); }}
                                        className={`px-2.5 py-1.5 rounded text-xs font-medium border transition-all ${infantGenders[i] === "female" ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]" : "bg-white/5 border-white/10 text-white/50 hover:border-white/25"}`}
                                      >F</button>
                                    </div>
                                    <Input
                                      data-testid={`infant-name-${i}`}
                                      type="text"
                                      placeholder={`Infant ${i + 1} full name as per ID proof`}
                                      value={name}
                                      required
                                      onChange={(e) => {
                                        const updated = [...infantNames];
                                        updated[i] = sanitizeNameInput(e.target.value);
                                        setInfantNames(updated);
                                      }}
                                      className={`bg-transparent border-white/20 text-white focus:border-[#D4AF37] h-10 text-sm flex-1 ${name.trim() && getNameError(name) ? 'border-red-500/60' : !name.trim() ? 'border-red-500/40' : ''}`}
                                    />
                                  </div>
                                  {name.trim() && getNameError(name) && (
                                    <p className="text-red-400 text-[10px] ml-14">{getNameError(name)}</p>
                                  )}
                                  <MealChips
                                    value={infantMeals[i] || ""}
                                    onChange={(v) => { const u = [...infantMeals]; u[i] = v; setInfantMeals(u); }}
                                    testid={`infant-meal-${i}`}
                                  />
                                  <div>
                                    <Label className="text-white/60 text-xs mb-1 block">Date of Birth</Label>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="outline"
                                          className="w-full justify-start text-left font-normal bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white h-10 text-sm"
                                          data-testid={`infant-dob-button-${i}`}
                                        >
                                          <CalendarIcon className="mr-2 h-4 w-4" />
                                          {infantDOBs[i] ? format(infantDOBs[i], "PPP") : <span className="text-white/40">Pick date of birth</span>}
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-0 bg-[#1a1a1a] border-white/20" align="start">
                                        <Calendar
                                          mode="single"
                                          selected={infantDOBs[i]}
                                          onSelect={(date) => {
                                            const updated = [...infantDOBs];
                                            updated[i] = date;
                                            setInfantDOBs(updated);
                                          }}
                                          disabled={(date) => date > new Date()}
                                          initialFocus
                                          className="rounded-md border-0"
                                          data-testid={`infant-dob-calendar-${i}`}
                                        />
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                  {age && (
                                    <div className={`text-xs px-3 py-1.5 rounded ${willBe2OrOlder ? 'bg-amber-500/15 text-amber-400' : 'bg-green-500/15 text-green-400'}`} data-testid={`infant-age-info-${i}`}>
                                      {willBe2OrOlder
                                        ? `Age at travel: ${age.years}y ${age.months}m — will require a child ticket`
                                        : `Age at travel: ${age.years}y ${age.months}m — infant pricing applies`}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Top 5 Reviews from Google */}
                    {selectedDestination && (
                      <div className="pt-4 border-t border-white/10" data-testid="destination-reviews-section">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <h3 className="font-serif text-xl text-white text-center">
                            {reviewsFocus === "women_safety" 
                              ? `Women Safety Reviews — ${selectedDestination.name}`
                              : `What Travelers Say About ${selectedDestination.name}`
                            }
                          </h3>
                        </div>
                        {reviewsFocus === "women_safety" && (
                          <div className="flex items-center justify-center gap-1.5 mb-2">
                            <Shield className="w-3.5 h-3.5 text-[#D4AF37]" />
                            <span className="text-[#D4AF37]/70 text-xs">Showing reviews focused on women safety for solo female traveler</span>
                          </div>
                        )}
                        {reviewsSource === "google_maps" && (
                          <div className="flex items-center justify-center gap-1.5 mb-4">
                            <svg viewBox="0 0 24 24" className="w-4 h-4" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                            <span className="text-white/40 text-xs">Reviews from Google</span>
                          </div>
                        )}
                        {loadingReviews ? (
                          <div className="flex items-center justify-center gap-2 py-8">
                            <div className="animate-spin h-4 w-4 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
                            <span className="text-white/50 text-sm">Fetching reviews...</span>
                          </div>
                        ) : liveReviews.length > 0 ? (
                          <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin" style={{ scrollbarWidth: "thin" }}>
                            {liveReviews.map((review, i) => {
                              const isExpanded = expandedReviews[i];
                              const isLong = review.text && review.text.length > 120;
                              return (
                                <div key={`${review.name || 'anon'}-${i}`} className="bg-white/[0.03] border border-white/5 rounded-lg p-4 flex-shrink-0 w-[260px] snap-start flex flex-col" data-testid={`review-card-${i}`}>
                                  <div className="flex items-center gap-2.5 mb-2">
                                    {review.thumbnail ? (
                                      <img src={review.thumbnail} alt={review.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-white/10" referrerPolicy="no-referrer" />
                                    ) : (
                                      <div className="w-8 h-8 rounded-full bg-[#D4AF37]/15 flex items-center justify-center flex-shrink-0">
                                        <User className="w-3.5 h-3.5 text-[#D4AF37]" />
                                      </div>
                                    )}
                                    <div className="min-w-0">
                                      <p className="text-white text-xs font-medium truncate">{review.name}</p>
                                      <div className="flex items-center gap-1.5">
                                        <div className="flex gap-0.5">
                                          {[...Array(5)].map((_, s) => (
                                            <Star key={`star-${s}`} className={`w-3 h-3 ${s < review.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-white/15"}`} />
                                          ))}
                                        </div>
                                        <span className="text-white/25 text-[10px]">{review.date}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex-1">
                                    <p className={`text-white/55 text-xs leading-relaxed ${!isExpanded && isLong ? "line-clamp-4" : ""}`}>
                                      {review.text}
                                    </p>
                                    {isLong && (
                                      <button
                                        data-testid={`review-expand-${i}`}
                                        onClick={() => setExpandedReviews(prev => ({ ...prev, [i]: !prev[i] }))}
                                        className="text-[#D4AF37] text-[11px] mt-1 hover:underline"
                                      >
                                        {isExpanded ? "Show less" : "Read more"}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-white/30 text-sm text-center py-6">No reviews available for this destination</p>
                        )}
                      </div>
                    )}

                    {/* Inline blocker hint — surfaces only when names &
                        genders are clean but meal preferences are still
                        empty. Keeps customers from clicking the Continue
                        button repeatedly without understanding why it's
                        disabled. */}
                    {mealsAreTheBlocker() && (
                      <div
                        className="border border-red-400/30 bg-red-500/[0.08] rounded p-3 flex items-start gap-2.5"
                        data-testid="step4-meal-blocker"
                      >
                        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-300" />
                        <p className="text-red-100/90 text-xs leading-relaxed">
                          <span className="font-medium text-red-200">Meal preference is required</span> for every adult and child. Pick one chip for each passenger highlighted above to continue.
                        </p>
                      </div>
                    )}

                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        onClick={() => setStep(3)}
                        className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5"
                        data-testid="back-to-dates-button"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" /> Back
                      </Button>
                      <Button
                        onClick={() => setStep(5)}
                        disabled={!allNamesFilledIn()}
                        className="flex-1 bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="continue-to-packages-button"
                      >
                        Continue
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {step === 5 && (
                <Card className="glass-card p-8" data-testid="package-selection-step">
                  <div className="space-y-6">
                    <div className="text-center">
                      <Sparkles className="w-12 h-12 text-[#D4AF37] mx-auto mb-4" />
                      <h2 className="font-serif text-3xl text-white mb-2">Choose Your Experience</h2>
                      <p className="text-white/60">Select the package that suits you best</p>
                      {departureCity && (
                        <p className="text-white/60 mt-2">
                          Flying from <span className="text-[#D4AF37]">{departureCity}</span> • {luxuryCost?.days} nights
                        </p>
                      )}
                    </div>

                    {appliedPromo && (
                      <div
                        data-testid="promo-banner-step5"
                        className="mx-auto max-w-2xl bg-[#D4AF37]/10 border border-dashed border-[#D4AF37] rounded-lg px-4 py-3 flex items-center justify-between gap-3"
                      >
                        <div className="flex items-center gap-3 text-sm">
                          <span className="bg-[#D4AF37] text-black font-bold text-xs px-2 py-1 rounded tracking-wider">
                            {appliedPromo.discount_pct}% OFF
                          </span>
                          <span className="text-white/80">
                            Code <span className="font-mono text-[#D4AF37]">{appliedPromo.code}</span> applied — discount will reflect at checkout.
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAppliedPromo(null)}
                          className="text-white/40 hover:text-white text-xs"
                          data-testid="promo-banner-remove"
                        >
                          Remove
                        </button>
                      </div>
                    )}

                    {/* Manual promo entry (Iter 46) — only shows when no promo
                        is currently applied. Customers who lost their `?promo=`
                        URL (e.g. switched devices) can type the code in. */}
                    {!appliedPromo && (
                      <div className="mx-auto max-w-2xl" data-testid="promo-manual-block">
                        {!showPromoInput ? (
                          <button
                            type="button"
                            onClick={() => setShowPromoInput(true)}
                            className="text-xs text-white/55 hover:text-[#D4AF37] underline underline-offset-4 decoration-dotted transition-colors"
                            data-testid="promo-manual-toggle"
                          >
                            Have a promo code?
                          </button>
                        ) : (
                          <div
                            className="bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3"
                            data-testid="promo-manual-input-wrap"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <label className="text-xs text-white/70 tracking-wide">
                                Enter promo code
                              </label>
                              <button
                                type="button"
                                onClick={() => {
                                  setShowPromoInput(false);
                                  setManualPromoCode("");
                                }}
                                className="text-white/40 hover:text-white text-xs"
                                data-testid="promo-manual-cancel"
                              >
                                Cancel
                              </button>
                            </div>
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                value={manualPromoCode}
                                onChange={(e) => setManualPromoCode(e.target.value.toUpperCase())}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !applyingPromo) {
                                    e.preventDefault();
                                    handleApplyManualPromo();
                                  }
                                }}
                                placeholder="e.g. WELCOME5"
                                maxLength={32}
                                className="flex-1 bg-black/30 border-white/10 text-white font-mono tracking-wider placeholder:text-white/25"
                                data-testid="promo-manual-input"
                              />
                              <Button
                                type="button"
                                onClick={handleApplyManualPromo}
                                disabled={applyingPromo || !manualPromoCode.trim()}
                                className="bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-50"
                                data-testid="promo-manual-apply"
                              >
                                {applyingPromo ? "Checking…" : "Apply"}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Family-rate badge — shown when the entire family fits
                        into a single hotel room under the room-occupancy
                        rules (≤2 adults + ≤2 children, infants share an
                        adult's bed). Locks in the lower-than-expected hotel
                        price as a feature rather than letting customers
                        worry that we forgot to add a second room. Iter 44. */}
                    {adults >= 2 && children >= 1 && adults <= 2 && children <= 2 && (
                      <div
                        data-testid="family-rate-badge"
                        className="mx-auto max-w-2xl bg-emerald-500/[0.08] border border-emerald-400/30 rounded-lg px-4 py-3 flex items-start gap-3"
                      >
                        <span className="inline-flex shrink-0 items-center justify-center w-9 h-9 rounded-md bg-emerald-500/15 border border-emerald-400/30 text-lg">
                          👨‍👩‍👧‍👦
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-emerald-200 text-xs font-semibold tracking-wide uppercase">
                            Family rate · 1 room
                          </p>
                          <p className="text-white/75 text-xs leading-relaxed mt-0.5">
                            {adults} adult{adults > 1 ? "s" : ""}{children ? ` + ${children} ${children > 1 ? "children" : "child"}` : ""}{infants ? ` + ${infants} infant${infants > 1 ? "s" : ""}` : ""} fit comfortably in <span className="text-white font-medium">1 hotel room</span> with an extra bed for {children > 1 ? "the kids" : "the child"}{infants ? "; infants share an adult's bed at no extra cost" : ""}. Your quote reflects this family-friendly occupancy.
                          </p>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {/* Luxury Package */}
                      <Card
                        data-testid="luxury-package-card"
                        className={`p-6 transition-all ${
                          luxuryCost && !luxuryCost.packageAvailable
                            ? "bg-white/[0.02] border-white/5 opacity-60 cursor-not-allowed"
                            : selectedPackage === "luxury"
                            ? "bg-[#D4AF37]/10 border-[#D4AF37] luxury-glow cursor-pointer"
                            : "bg-white/5 border-white/10 hover:border-white/20 cursor-pointer"
                        }`}
                        onClick={() => {
                          if (luxuryCost && !luxuryCost.packageAvailable) return;
                          setSelectedPackage("luxury");
                        }}
                      >
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                            <Sparkles className="w-8 h-8 text-[#D4AF37]" />
                          </div>
                          <h3 className="font-serif text-2xl text-white mb-2">Luxury</h3>
                          {luxuryCost && !luxuryCost.packageAvailable ? (
                            <div
                              data-testid="luxury-unavailable"
                              className="mt-2 mb-1 border border-amber-400/30 bg-amber-500/5 rounded-md p-4 text-left"
                            >
                              <p className="text-amber-300 text-sm font-medium mb-1">Package unavailable</p>
                              <p className="text-amber-200/70 text-xs leading-relaxed">{luxuryCost.unavailableReason}</p>
                              <p className="text-white/40 text-[10px] mt-2 leading-relaxed">
                                Try different travel dates or pick another package below.
                              </p>
                            </div>
                          ) : luxuryCost && (
                            <>
                              <p className="text-[#D4AF37] text-3xl font-medium mb-1">
                                ₹{luxuryCost.totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </p>
                              {selectedPackage === "luxury" && (
                                <div
                                  className="mb-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-medium tracking-wide"
                                  data-testid="luxury-price-lock-pill"
                                >
                                  <Lock className="w-2.5 h-2.5" />
                                  <span>Price held for 15 min</span>
                                </div>
                              )}
                              <div className="space-y-2 text-left mb-4 pb-4 border-b border-white/10">
                                {/* Iter 69 — Flights / Hotels / Transfers rows now each carry an
                                    inline exclude/include mini-link (same pattern as Itinerary),
                                    so NRIs who already have their own flights or hotels can
                                    strip those line items and only pay for what they need. */}
                                {includeFlightCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">✈️ Flights</span>
                                      <span className="text-white/80">₹{luxuryCost.flightCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeFlightCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="luxury-exclude-flights">Exclude flights</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeFlightCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="luxury-include-flights">+ Include flights</button>
                                  </div>
                                )}
                                {includeHotelCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">🏨 Hotels</span>
                                      <span className="text-white/80">₹{luxuryCost.hotelCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeHotelCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="luxury-exclude-hotels">Exclude hotels</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeHotelCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="luxury-include-hotels">+ Include hotels</button>
                                  </div>
                                )}
                                {includeTransferCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">🚗 Transfers</span>
                                      <span className="text-white/80">₹{luxuryCost.transferCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeTransferCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="luxury-exclude-transfers">Exclude transfers</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeTransferCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="luxury-include-transfers">+ Include transfers</button>
                                  </div>
                                )}
                                {luxuryCost.itineraryApplied && luxuryCost.itineraryCost > 0 ? (
                                  <>
                                    <div className="flex justify-between text-sm" data-testid="luxury-itinerary-line">
                                      <span className="text-white/60">🗺️ Itinerary</span>
                                      <span className="text-white/80">₹{luxuryCost.itineraryCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setIncludeItineraryCost(false); }}
                                        className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors"
                                        data-testid="luxury-exclude-itinerary"
                                      >
                                        Exclude itinerary
                                      </button>
                                    </div>
                                  </>
                                ) : (!selectedDestination.is_test_package && luxuryCost.itineraryEligible && dailyCost && dailyCost.total_inr > 0 && (
                                  <div className="flex justify-end -mt-1">
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setIncludeItineraryCost(true); }}
                                      className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors"
                                      data-testid="luxury-include-itinerary"
                                    >
                                      + Include itinerary
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {luxuryCost?.packageAvailable && (
                          <div className="space-y-3 text-left">
                            <div className="flex items-start gap-2">
                              <Plane className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-white/80 text-sm">{flightAvailability?.first_class ? "First Class Air Tickets" : flightAvailability?.business_class ? "Business Class Air Tickets" : "Economy Class Air Tickets"}</p>
                                {flightAvailability && !flightAvailability.first_class && (
                                  <p className="text-amber-400/70 text-[10px]">First Class not available on this route</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <Hotel className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                              <p className="text-white/80 text-sm">Luxury Accommodation</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <Car className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                              <p className="text-white/80 text-sm">{roadTravel ? "Road Transfer (Round Trip)" : "Luxury Pick up & Drop"}</p>
                            </div>
                          </div>
                          )}
                          {luxuryCost?.packageAvailable && airlineNames?.airlines?.luxury && (
                            <div className="mt-4 border-t border-white/5">
                              <button onClick={() => setExpandAirlines(p => ({...p, luxury: !p.luxury}))} className="w-full flex items-center justify-between py-2.5">
                                <p className="text-white/40 text-[10px] uppercase tracking-wider">Top Airlines in this segment</p>
                                <ChevronLeft className={`w-3.5 h-3.5 text-white/30 transition-transform duration-300 ${expandAirlines.luxury ? "rotate-90" : "-rotate-90"}`} />
                              </button>
                              {expandAirlines.luxury && (
                                <div className="space-y-1 pb-2">
                                  {airlineNames.airlines.luxury.map((a) => (
                                    <p key={`luxury-air-${a}`} className="text-white/60 text-xs flex items-center gap-1.5">
                                      <span className="w-1 h-1 rounded-full bg-[#D4AF37] flex-shrink-0" />{a}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {luxuryCost?.packageAvailable && hotelNames?.hotels?.luxury && (
                            <div className="border-t border-white/5">
                              <button onClick={() => setExpandHotels(p => ({...p, luxury: !p.luxury}))} className="w-full flex items-center justify-between py-2.5">
                                <p className="text-white/40 text-[10px] uppercase tracking-wider">Top Hotels in this segment</p>
                                <ChevronLeft className={`w-3.5 h-3.5 text-white/30 transition-transform duration-300 ${expandHotels.luxury ? "rotate-90" : "-rotate-90"}`} />
                              </button>
                              {expandHotels.luxury && (
                                <div className="space-y-1 pb-2">
                                  {hotelNames.hotels.luxury.map((h) => (
                                    <p key={`luxury-hotel-${h}`} className="text-white/60 text-xs flex items-center gap-1.5">
                                      <span className="w-1 h-1 rounded-full bg-[#D4AF37] flex-shrink-0" />{h}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </Card>

                      {/* Premium Package */}
                      <Card
                        data-testid="premium-package-card"
                        className={`p-6 transition-all ${
                          premiumCost && !premiumCost.packageAvailable
                            ? "bg-white/[0.02] border-white/5 opacity-60 cursor-not-allowed"
                            : selectedPackage === "premium"
                            ? "bg-[#D4AF37]/10 border-[#D4AF37] cursor-pointer"
                            : "bg-white/5 border-white/10 hover:border-white/20 cursor-pointer"
                        }`}
                        onClick={() => {
                          if (premiumCost && !premiumCost.packageAvailable) return;
                          setSelectedPackage("premium");
                        }}
                      >
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                            <Check className="w-8 h-8 text-white" />
                          </div>
                          <h3 className="font-serif text-2xl text-white mb-2">Premium</h3>
                          {premiumCost && !premiumCost.packageAvailable ? (
                            <div
                              data-testid="premium-unavailable"
                              className="mt-2 mb-1 border border-amber-400/30 bg-amber-500/5 rounded-md p-4 text-left"
                            >
                              <p className="text-amber-300 text-sm font-medium mb-1">Package unavailable</p>
                              <p className="text-amber-200/70 text-xs leading-relaxed">{premiumCost.unavailableReason}</p>
                              <p className="text-white/40 text-[10px] mt-2 leading-relaxed">
                                Try different travel dates or pick another package.
                              </p>
                            </div>
                          ) : premiumCost && (
                            <>
                              <p className="text-[#D4AF37] text-3xl font-medium mb-1">
                                ₹{premiumCost.totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </p>
                              {selectedPackage === "premium" && (
                                <div
                                  className="mb-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-medium tracking-wide"
                                  data-testid="premium-price-lock-pill"
                                >
                                  <Lock className="w-2.5 h-2.5" />
                                  <span>Price held for 15 min</span>
                                </div>
                              )}
                              <div className="space-y-2 text-left mb-4 pb-4 border-b border-white/10">
                                {includeFlightCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">✈️ Flights</span>
                                      <span className="text-white/80">₹{premiumCost.flightCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeFlightCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="premium-exclude-flights">Exclude flights</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeFlightCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="premium-include-flights">+ Include flights</button>
                                  </div>
                                )}
                                {includeHotelCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">🏨 Hotels</span>
                                      <span className="text-white/80">₹{premiumCost.hotelCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeHotelCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="premium-exclude-hotels">Exclude hotels</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeHotelCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="premium-include-hotels">+ Include hotels</button>
                                  </div>
                                )}
                                {includeTransferCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">🚗 Transfers</span>
                                      <span className="text-white/80">₹{premiumCost.transferCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeTransferCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="premium-exclude-transfers">Exclude transfers</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeTransferCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="premium-include-transfers">+ Include transfers</button>
                                  </div>
                                )}
                                {premiumCost.itineraryApplied && premiumCost.itineraryCost > 0 ? (
                                  <>
                                    <div className="flex justify-between text-sm" data-testid="premium-itinerary-line">
                                      <span className="text-white/60">🗺️ Itinerary</span>
                                      <span className="text-white/80">₹{premiumCost.itineraryCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setIncludeItineraryCost(false); }}
                                        className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors"
                                        data-testid="premium-exclude-itinerary"
                                      >
                                        Exclude itinerary
                                      </button>
                                    </div>
                                  </>
                                ) : (!selectedDestination.is_test_package && premiumCost.itineraryEligible && dailyCost && dailyCost.total_inr > 0 && (
                                  <div className="flex justify-end -mt-1">
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setIncludeItineraryCost(true); }}
                                      className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors"
                                      data-testid="premium-include-itinerary"
                                    >
                                      + Include itinerary
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {premiumCost?.packageAvailable && (
                          <div className="space-y-3 text-left">
                            <div className="flex items-start gap-2">
                              <Plane className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" />
                              <div>
                                <p className="text-white/80 text-sm">{flightAvailability?.business_class ? "Business Class Air Tickets" : "Economy Class Air Tickets"}</p>
                                {flightAvailability && !flightAvailability.business_class && (
                                  <p className="text-amber-400/70 text-[10px]">Business Class not available on this route</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-start gap-2">
                              <Hotel className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" />
                              <p className="text-white/80 text-sm">Premium Accommodation</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <Car className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" />
                              <p className="text-white/80 text-sm">{roadTravel ? "Road Transfer (Round Trip)" : "Premium Pick up & Drop"}</p>
                            </div>
                          </div>
                          )}
                          {premiumCost?.packageAvailable && airlineNames?.airlines?.premium && (
                            <div className="mt-4 border-t border-white/5">
                              <button onClick={() => setExpandAirlines(p => ({...p, premium: !p.premium}))} className="w-full flex items-center justify-between py-2.5">
                                <p className="text-white/40 text-[10px] uppercase tracking-wider">Top Airlines in this segment</p>
                                <ChevronLeft className={`w-3.5 h-3.5 text-white/30 transition-transform duration-300 ${expandAirlines.premium ? "rotate-90" : "-rotate-90"}`} />
                              </button>
                              {expandAirlines.premium && (
                                <div className="space-y-1 pb-2">
                                  {airlineNames.airlines.premium.map((a) => (
                                    <p key={`premium-air-${a}`} className="text-white/60 text-xs flex items-center gap-1.5">
                                      <span className="w-1 h-1 rounded-full bg-white/40 flex-shrink-0" />{a}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {premiumCost?.packageAvailable && hotelNames?.hotels?.premium && (
                            <div className="border-t border-white/5">
                              <button onClick={() => setExpandHotels(p => ({...p, premium: !p.premium}))} className="w-full flex items-center justify-between py-2.5">
                                <p className="text-white/40 text-[10px] uppercase tracking-wider">Top Hotels in this segment</p>
                                <ChevronLeft className={`w-3.5 h-3.5 text-white/30 transition-transform duration-300 ${expandHotels.premium ? "rotate-90" : "-rotate-90"}`} />
                              </button>
                              {expandHotels.premium && (
                                <div className="space-y-1 pb-2">
                                  {hotelNames.hotels.premium.map((h) => (
                                    <p key={`premium-hotel-${h}`} className="text-white/60 text-xs flex items-center gap-1.5">
                                      <span className="w-1 h-1 rounded-full bg-white/40 flex-shrink-0" />{h}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </Card>

                      {/* Basic Package */}
                      <Card
                        data-testid="basic-package-card"
                        className={`p-6 transition-all ${
                          basicCost && !basicCost.packageAvailable
                            ? "bg-white/[0.02] border-white/5 opacity-60 cursor-not-allowed"
                            : selectedPackage === "basic"
                            ? "bg-[#D4AF37]/10 border-[#D4AF37] cursor-pointer"
                            : "bg-white/5 border-white/10 hover:border-white/20 cursor-pointer"
                        }`}
                        onClick={() => {
                          if (basicCost && !basicCost.packageAvailable) return;
                          setSelectedPackage("basic");
                        }}
                      >
                        <div className="text-center">
                          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center">
                            <Check className="w-8 h-8 text-white" />
                          </div>
                          <h3 className="font-serif text-2xl text-white mb-2">Basic</h3>
                          {basicCost && !basicCost.packageAvailable ? (
                            <div
                              data-testid="basic-unavailable"
                              className="mt-2 mb-1 border border-amber-400/30 bg-amber-500/5 rounded-md p-4 text-left"
                            >
                              <p className="text-amber-300 text-sm font-medium mb-1">Package unavailable</p>
                              <p className="text-amber-200/70 text-xs leading-relaxed">{basicCost.unavailableReason}</p>
                              <p className="text-white/40 text-[10px] mt-2 leading-relaxed">
                                Try different travel dates or pick another package.
                              </p>
                            </div>
                          ) : basicCost && (
                            <>
                              <p className="text-[#D4AF37] text-3xl font-medium mb-1">
                                ₹{basicCost.totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </p>
                              {selectedPackage === "basic" && (
                                <div
                                  className="mb-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-medium tracking-wide"
                                  data-testid="basic-price-lock-pill"
                                >
                                  <Lock className="w-2.5 h-2.5" />
                                  <span>Price held for 15 min</span>
                                </div>
                              )}
                              <div className="space-y-2 text-left mb-4 pb-4 border-b border-white/10">
                                {includeFlightCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">✈️ Flights</span>
                                      <span className="text-white/80">₹{basicCost.flightCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeFlightCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="basic-exclude-flights">Exclude flights</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeFlightCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="basic-include-flights">+ Include flights</button>
                                  </div>
                                )}
                                {includeHotelCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">🏨 Hotels</span>
                                      <span className="text-white/80">₹{basicCost.hotelCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeHotelCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="basic-exclude-hotels">Exclude hotels</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeHotelCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="basic-include-hotels">+ Include hotels</button>
                                  </div>
                                )}
                                {includeTransferCost ? (
                                  <>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-white/60">🚗 Transfers</span>
                                      <span className="text-white/80">₹{basicCost.transferCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeTransferCost(false); }} className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="basic-exclude-transfers">Exclude transfers</button>
                                    </div>
                                  </>
                                ) : (
                                  <div className="flex justify-end -mt-1">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); setIncludeTransferCost(true); }} className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors" data-testid="basic-include-transfers">+ Include transfers</button>
                                  </div>
                                )}
                                {basicCost.itineraryApplied && basicCost.itineraryCost > 0 ? (
                                  <>
                                    <div className="flex justify-between text-sm" data-testid="basic-itinerary-line">
                                      <span className="text-white/60">🗺️ Itinerary</span>
                                      <span className="text-white/80">₹{basicCost.itineraryCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                    </div>
                                    <div className="flex justify-end -mt-1">
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setIncludeItineraryCost(false); }}
                                        className="text-[10px] text-white/40 hover:text-[#D4AF37] underline underline-offset-2 transition-colors"
                                        data-testid="basic-exclude-itinerary"
                                      >
                                        Exclude itinerary
                                      </button>
                                    </div>
                                  </>
                                ) : (!selectedDestination.is_test_package && basicCost.itineraryEligible && dailyCost && dailyCost.total_inr > 0 && (
                                  <div className="flex justify-end -mt-1">
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); setIncludeItineraryCost(true); }}
                                      className="text-[10px] text-[#D4AF37]/80 hover:text-[#D4AF37] underline underline-offset-2 transition-colors"
                                      data-testid="basic-include-itinerary"
                                    >
                                      + Include itinerary
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {basicCost?.packageAvailable && (
                          <div className="space-y-3 text-left">
                            <div className="flex items-start gap-2">
                              <Plane className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" />
                              <p className="text-white/80 text-sm">Economy Class Tickets</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <Hotel className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" />
                              <p className="text-white/80 text-sm">Economy Accommodation</p>
                            </div>
                            <div className="flex items-start gap-2">
                              <Car className="w-5 h-5 text-white/60 flex-shrink-0 mt-0.5" />
                              <p className="text-white/80 text-sm">{roadTravel ? "Road Transfer (Round Trip)" : "Premium Pick up & Drop"}</p>
                            </div>
                          </div>
                          )}
                          {basicCost?.packageAvailable && airlineNames?.airlines?.basic && (
                            <div className="mt-4 border-t border-white/5">
                              <button onClick={() => setExpandAirlines(p => ({...p, basic: !p.basic}))} className="w-full flex items-center justify-between py-2.5">
                                <p className="text-white/40 text-[10px] uppercase tracking-wider">Top Airlines in this segment</p>
                                <ChevronLeft className={`w-3.5 h-3.5 text-white/30 transition-transform duration-300 ${expandAirlines.basic ? "rotate-90" : "-rotate-90"}`} />
                              </button>
                              {expandAirlines.basic && (
                                <div className="space-y-1 pb-2">
                                  {airlineNames.airlines.basic.map((a) => (
                                    <p key={`basic-air-${a}`} className="text-white/60 text-xs flex items-center gap-1.5">
                                      <span className="w-1 h-1 rounded-full bg-white/30 flex-shrink-0" />{a}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {basicCost?.packageAvailable && hotelNames?.hotels?.basic && (
                            <div className="border-t border-white/5">
                              <button onClick={() => setExpandHotels(p => ({...p, basic: !p.basic}))} className="w-full flex items-center justify-between py-2.5">
                                <p className="text-white/40 text-[10px] uppercase tracking-wider">Top Hotels in this segment</p>
                                <ChevronLeft className={`w-3.5 h-3.5 text-white/30 transition-transform duration-300 ${expandHotels.basic ? "rotate-90" : "-rotate-90"}`} />
                              </button>
                              {expandHotels.basic && (
                                <div className="space-y-1 pb-2">
                                  {hotelNames.hotels.basic.map((h) => (
                                    <p key={`basic-hotel-${h}`} className="text-white/60 text-xs flex items-center gap-1.5">
                                      <span className="w-1 h-1 rounded-full bg-white/30 flex-shrink-0" />{h}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>

                    {/* Alternate Return Airport Option — shown for trips > 5 days */}
                    {startDate && endDate && Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) > 5 && alternateAirports.length > 0 && (
                      <Card className="bg-white/[0.03] border-white/10 overflow-hidden" data-testid="alternate-airport-section">
                        <button onClick={() => setExpandAltAirport(!expandAltAirport)} className="w-full flex items-center justify-between p-5 hover:bg-white/[0.02] transition-colors">
                          <div className="flex items-center gap-2">
                            <Plane className="w-5 h-5 text-[#D4AF37]" />
                            <h4 className="text-white text-sm font-medium">Fly back from a nearby airport?</h4>
                          </div>
                          <ChevronLeft className={`w-4 h-4 text-white/40 transition-transform duration-300 ${expandAltAirport ? "rotate-90" : "-rotate-90"}`} />
                        </button>
                        {expandAltAirport && (
                          <div className="px-5 pb-5 pt-0">
                            <p className="text-white/40 text-xs mb-4">
                              Your trip is {Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24))} days. You can choose to return from a nearby airport for potentially better fares or convenience.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              <button
                                data-testid="return-airport-default"
                                onClick={() => setSelectedReturnAirport(null)}
                                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                                  !selectedReturnAirport
                                    ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                                    : "bg-white/5 border-white/10 text-white/60 hover:border-white/20"
                                }`}
                              >
                                Same as arrival
                              </button>
                              {alternateAirports.map((apt) => (
                                <button
                                  key={apt.code}
                                  data-testid={`return-airport-${apt.code}`}
                                  onClick={() => setSelectedReturnAirport(apt)}
                                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                                    selectedReturnAirport?.code === apt.code
                                      ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                                      : "bg-white/5 border-white/10 text-white/60 hover:border-white/20"
                                  }`}
                                >
                                  {apt.name} ({apt.code}) <span className="text-white/30 ml-1">— {apt.distance}</span>
                                </button>
                              ))}
                            </div>
                            {selectedReturnAirport && (
                              <p className="text-[#D4AF37]/70 text-[11px] mt-3">
                                Return flight will be from {selectedReturnAirport.name} ({selectedReturnAirport.code}) to your home city
                              </p>
                            )}
                          </div>
                        )}
                      </Card>
                    )}

                    {/* Compare Packages Toggle */}
                    {luxuryCost && premiumCost && basicCost && (
                      <div className="text-center">
                        <button
                          data-testid="compare-packages-toggle"
                          onClick={() => setShowCompare(!showCompare)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 hover:border-[#D4AF37]/50 bg-white/[0.03] hover:bg-white/[0.06] transition-all text-sm text-white/70 hover:text-white"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#D4AF37]"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                          {showCompare ? "Hide Comparison" : "Compare Packages Side-by-Side"}
                          <ChevronLeft className={`w-4 h-4 transition-transform duration-300 ${showCompare ? "rotate-90" : "-rotate-90"}`} />
                        </button>
                      </div>
                    )}

                    {/* Compare Packages Table */}
                    <AnimatePresence>
                      {showCompare && luxuryCost && premiumCost && basicCost && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <Card className="bg-white/[0.03] border-white/10 overflow-hidden" data-testid="compare-packages-table">
                            {/* Table Header */}
                            <div className="grid grid-cols-4 border-b border-white/10">
                              <div className="p-4 flex items-end">
                                <p className="text-white/40 text-xs uppercase tracking-widest">Feature</p>
                              </div>
                              {[
                                { name: "Luxury", color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/5" },
                                { name: "Premium", color: "text-white", bg: "bg-white/[0.02]" },
                                { name: "Basic", color: "text-white/70", bg: "bg-transparent" }
                              ].map((pkg) => (
                                <div key={pkg.name} className={`p-4 text-center ${pkg.bg}`}>
                                  <p className={`font-serif text-lg ${pkg.color} font-medium`}>{pkg.name}</p>
                                  <p className="text-[#D4AF37] text-xl font-medium mt-1">
                                    ₹{(pkg.name === "Luxury" ? luxuryCost : pkg.name === "Premium" ? premiumCost : basicCost).totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                  </p>
                                </div>
                              ))}
                            </div>

                            {/* Comparison Rows */}
                            {[
                              {
                                label: "Flight Class",
                                icon: <Plane className="w-4 h-4 text-[#D4AF37]" />,
                                values: [
                                  flightAvailability?.first_class ? "First Class" : flightAvailability?.business_class ? "Business Class" : "Economy Class",
                                  flightAvailability?.business_class ? "Business Class" : "Economy Class",
                                  "Economy Class"
                                ],
                                isDifferent: true
                              },
                              {
                                label: "Flight Cost",
                                icon: <Plane className="w-4 h-4 text-white/40" />,
                                values: [
                                  `₹${luxuryCost.flightCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${premiumCost.flightCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${basicCost.flightCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                ],
                                isDifferent: true
                              },
                              {
                                label: "Hotel Rating",
                                icon: <Hotel className="w-4 h-4 text-[#D4AF37]" />,
                                values: ["Luxury", "Premium", "Economy"],
                                isDifferent: true,
                                sameNote: [false, true, true]
                              },
                              {
                                label: "Hotel Cost",
                                icon: <Hotel className="w-4 h-4 text-white/40" />,
                                values: [
                                  `₹${luxuryCost.hotelCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${premiumCost.hotelCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${basicCost.hotelCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                ],
                                isDifferent: luxuryCost.hotelCosts !== premiumCost.hotelCosts,
                                sameNote: [false, true, true]
                              },
                              {
                                label: "Transfers",
                                icon: <Car className="w-4 h-4 text-white/40" />,
                                values: [
                                  `₹${luxuryCost.transferCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${premiumCost.transferCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${basicCost.transferCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                ],
                                isDifferent: false
                              },
                              ...(premiumCost.itineraryApplied && premiumCost.itineraryCost > 0 ? [{
                                label: "Itinerary",
                                icon: <MapPin className="w-4 h-4 text-white/40" />,
                                values: [
                                  `₹${luxuryCost.itineraryCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${premiumCost.itineraryCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${basicCost.itineraryCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                ],
                                isDifferent: false
                              }] : []),
                              {
                                label: "Tax (18%)",
                                icon: null,
                                values: [
                                  `₹${luxuryCost.tax.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${premiumCost.tax.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                                  `₹${basicCost.tax.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                                ],
                                isDifferent: true
                              }
                            ].map((row, idx) => (
                              <div
                                key={row.label}
                                className={`grid grid-cols-4 ${idx % 2 === 0 ? "bg-white/[0.015]" : ""} border-b border-white/5`}
                                data-testid={`compare-row-${row.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
                              >
                                <div className="px-4 py-3 flex items-center gap-2">
                                  {row.icon}
                                  <span className="text-white/60 text-sm">{row.label}</span>
                                </div>
                                {row.values.map((val, vIdx) => (
                                  <div key={`${row.label}-col-${vIdx}`} className={`px-4 py-3 text-center flex items-center justify-center gap-1 ${vIdx === 0 ? "bg-[#D4AF37]/5" : vIdx === 1 ? "bg-white/[0.02]" : ""}`}>
                                    <span className={`text-sm ${row.isDifferent && vIdx === 0 ? "text-[#D4AF37] font-medium" : "text-white/80"}`}>
                                      {val}
                                    </span>
                                    {row.sameNote && row.sameNote[vIdx] && vIdx > 0 && premiumCost.hotelCosts === basicCost.hotelCosts && row.label === "Hotel Cost" && (
                                      <span className="text-[10px] text-green-400/70 ml-1">same</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ))}

                            {/* Total Row */}
                            <div className="grid grid-cols-4 bg-white/[0.04] border-t border-white/10">
                              <div className="px-4 py-4 flex items-center">
                                <span className="text-white font-medium text-sm">Total Cost</span>
                              </div>
                              {[
                                { tier: "luxury", cost: luxuryCost },
                                { tier: "premium", cost: premiumCost },
                                { tier: "basic", cost: basicCost },
                              ].map(({ tier, cost }, i) => (
                                <div key={tier} className={`px-4 py-4 text-center ${i === 0 ? "bg-[#D4AF37]/5" : i === 1 ? "bg-white/[0.02]" : ""}`}>
                                  <span className={`text-lg font-medium ${i === 0 ? "text-[#D4AF37]" : "text-white"}`} data-testid={`compare-total-${tier}`}>
                                    ₹{cost.totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                  </span>
                                </div>
                              ))}
                            </div>

                            {/* Insight Footer */}
                            {premiumCost.hotelCosts === basicCost.hotelCosts && (
                              <div className="px-4 py-3 bg-[#D4AF37]/5 border-t border-[#D4AF37]/10" data-testid="compare-insight">
                                <p className="text-xs text-center text-white/50">
                                  <span className="text-[#D4AF37]">Note:</span> Premium & Basic packages include the same accommodation tier. The price difference comes from the flight class (Business vs Economy).
                                </p>
                              </div>
                            )}
                          </Card>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {costs && selectedPackage && (
                      <Card className="bg-white/5 border-white/10 p-6">
                        <h3 className="font-serif text-xl text-white mb-4">Cost Breakdown</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between text-white/80">
                            <span>Round Trip Flights ({adults} adults, {children} children, {infants} infants)</span>
                            <span>₹{costs.flightCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                          </div>
                          <div className="flex justify-between text-white/80">
                            <span>Hotel ({costs.roomsNeeded} rooms × {costs.days} nights)</span>
                            <span>₹{costs.hotelCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                          </div>
                          <div className="flex justify-between text-white/80">
                            <span>{costs.useRoadTravel ? `Road Transfer (${roadTravel.from_city} — ${roadTravel.to_city}, Round Trip)` : "Transfers"}</span>
                            <span>₹{costs.transferCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                          </div>
                          {costs.itineraryApplied && (
                            <div className="flex justify-between text-white/80" data-testid="itinerary-cost-line">
                              <span>Itinerary ({costs.itineraryDays} days × pax)</span>
                              <span>₹{costs.itineraryCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-white/80 pt-2 border-t border-white/10">
                            <span>Subtotal</span>
                            <span>₹{costs.subtotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                          </div>
                          <div className="flex justify-between text-white/80">
                            <span>Tax (18%)</span>
                            <span>₹{costs.tax.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                          </div>
                          <div className="border-t border-white/10 pt-2 mt-2">
                            <div className="flex justify-between text-white font-medium text-lg">
                              <span>Total Package Cost</span>
                              <span data-testid="total-cost">₹{costs.totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    )}

                    {/* Travel Suitability Ratings — Collapsible */}
                    {selectedDestination && (() => {
                      const ratings = liveTravelRatings?.ratings;
                      const categories = [
                        { key: "family", label: "Family Travel", icon: "👨‍👩‍👧‍👦" },
                        { key: "solo", label: "Solo Travel", icon: "🎒" },
                        { key: "women", label: "Women Safety", icon: "🛡️" },
                        { key: "children", label: "Child Friendly", icon: "🧒" },
                        { key: "elderly", label: "Elderly Friendly", icon: "🧓" },
                      ];
                      return (
                        <div className="mt-8 border-t border-white/10" data-testid="travel-ratings-section">
                          <button onClick={() => setExpandTravelRatings(!expandTravelRatings)} className="w-full flex items-center justify-between py-5 hover:opacity-80 transition-opacity">
                            <div className="flex items-center gap-2">
                              <Shield className="w-5 h-5 text-[#D4AF37]" />
                              <h3 className="font-serif text-lg text-white">Travel Suitability — {selectedDestination.name}</h3>
                            </div>
                            <ChevronLeft className={`w-4 h-4 text-white/40 transition-transform duration-300 ${expandTravelRatings ? "rotate-90" : "-rotate-90"}`} />
                          </button>
                          {expandTravelRatings && (
                            <div className="pb-4">
                              <p className="text-white/40 text-xs text-center mb-1">How this destination rates for different travelers</p>
                              {liveTravelRatings?.source === "google_reviews_ai" && (
                                <div className="flex items-center justify-center gap-1.5 mb-4">
                                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                                  <span className="text-white/30 text-[11px]">Based on {liveTravelRatings.reviews_analyzed} Google Reviews, analyzed by AI</span>
                                </div>
                              )}
                              {loadingTravelRatings ? (
                                <div className="flex items-center justify-center gap-2 py-8">
                                  <div className="animate-spin h-4 w-4 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
                                  <span className="text-white/50 text-sm">Analyzing traveler reviews...</span>
                                </div>
                              ) : ratings ? (
                                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                                  {categories.map((cat) => {
                                    const score = ratings[cat.key] || 0;
                                    const pct = (score / 5) * 100;
                                    const color = score >= 4.5 ? "bg-green-500" : score >= 3.5 ? "bg-[#D4AF37]" : score >= 2.5 ? "bg-amber-500" : "bg-red-400";
                                    return (
                                      <div key={cat.key} className="bg-white/[0.03] border border-white/5 rounded-lg p-3 text-center" data-testid={`travel-rating-${cat.key}`}>
                                        <span className="text-lg">{cat.icon}</span>
                                        <p className="text-white/70 text-xs mt-1.5 mb-2">{cat.label}</p>
                                        <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-1.5">
                                          <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                                        </div>
                                        <p className="text-white text-sm font-medium">{score}<span className="text-white/30 text-xs">/5</span></p>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-white/30 text-sm text-center py-4">Ratings unavailable for this destination</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}


                    {/* Day-wise Itinerary — Collapsible. Hidden for 0-night and 1-night trips. */}
                    {selectedDestination && costs?.itineraryEligible && (
                      <div className="mt-6 border-t border-white/10" data-testid="itinerary-section">
                        <button onClick={() => setExpandItinerary(!expandItinerary)} className="w-full flex items-center justify-between py-5 hover:opacity-80 transition-opacity" data-testid="toggle-itinerary">
                          <div className="flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-[#D4AF37]" />
                            <h3 className="font-serif text-lg text-white">Itinerary — Hidden Gems</h3>
                          </div>
                          <ChevronLeft className={`w-4 h-4 text-white/40 transition-transform duration-300 ${expandItinerary ? "rotate-90" : "-rotate-90"}`} />
                        </button>
                        {expandItinerary && (
                          <div className="pb-4">
                            <p className="text-white/40 text-xs text-center mb-3">Discover the unexplored side of {selectedDestination.name} · last day excluded (flight back)</p>

                            {/* Guided-day count selector — customer picks how many days they want guided.
                                The remaining days are free time at their own pace, and the per-day
                                itinerary cost scales accordingly. Capped at trip-days minus 1. */}
                            {!selectedDestination.is_test_package && costs?.maxItineraryDays > 0 && (
                              <div className="bg-white/[0.02] border border-white/5 rounded-lg p-4 mb-4" data-testid="itinerary-days-selector-card">
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <p className="text-white text-sm font-medium">Guided itinerary days</p>
                                    <p className="text-white/40 text-[11px] mt-0.5">Pick fewer days to explore on your own and save</p>
                                  </div>
                                  <div className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-lg px-1">
                                    <button
                                      type="button"
                                      onClick={() => setItineraryDaysSelected(Math.max(1, (itineraryDaysSelected ?? costs.maxItineraryDays) - 1))}
                                      disabled={(itineraryDaysSelected ?? costs.maxItineraryDays) <= 1}
                                      className="w-7 h-7 flex items-center justify-center text-[#D4AF37] hover:bg-white/5 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                      data-testid="itinerary-days-decrement"
                                    >
                                      −
                                    </button>
                                    <span className="min-w-[36px] text-center text-white font-medium text-sm" data-testid="itinerary-days-value">
                                      {itineraryDaysSelected ?? costs.maxItineraryDays}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => setItineraryDaysSelected(Math.min(costs.maxItineraryDays, (itineraryDaysSelected ?? costs.maxItineraryDays) + 1))}
                                      disabled={(itineraryDaysSelected ?? costs.maxItineraryDays) >= costs.maxItineraryDays}
                                      className="w-7 h-7 flex items-center justify-center text-[#D4AF37] hover:bg-white/5 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                                      data-testid="itinerary-days-increment"
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                                <p className="text-white/40 text-[11px]">
                                  Trip length: {costs.days} {costs.days === 1 ? "day" : "days"} · Max guided days: {costs.maxItineraryDays} (last day kept free for return flight)
                                  {((itineraryDaysSelected ?? costs.maxItineraryDays) < costs.maxItineraryDays) && (
                                    <span className="text-[#D4AF37]"> · {costs.maxItineraryDays - (itineraryDaysSelected ?? costs.maxItineraryDays)} {(costs.maxItineraryDays - (itineraryDaysSelected ?? costs.maxItineraryDays)) === 1 ? "day" : "days"} free for self-exploration</span>
                                  )}
                                </p>
                              </div>
                            )}

                            {/* Itinerary inclusion — ON by default. Customer can opt out by unchecking. */}
                            {!selectedDestination.is_test_package && dailyCost && dailyCost.total_inr > 0 && (
                              <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg p-4 mb-5" data-testid="itinerary-cost-toggle-card">
                                <label className="flex items-start gap-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={includeItineraryCost}
                                    onChange={(e) => setIncludeItineraryCost(e.target.checked)}
                                    className="mt-1 w-4 h-4 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                                    data-testid="include-itinerary-cost-toggle"
                                  />
                                  <div className="flex-1 text-xs">
                                    <span className="text-white font-medium text-sm">
                                      {includeItineraryCost
                                        ? "Itinerary is included in my package"
                                        : "Itinerary not included — I'll pay out-of-pocket"}
                                    </span>
                                    <p className="text-white/50 mt-1 leading-relaxed">
                                      {includeItineraryCost
                                        ? `${costs?.itineraryDays || 0} days × ${adults}${children > 0 ? ` + ${children} child` : ""} adult${adults > 1 ? "s" : ""}${children > 0 ? " (children @ 50%)" : ""}. Uncheck to opt out and pay separately during the trip.`
                                        : "Activities, meals & local spending will not be covered by Travelsate. Check the box to add them back to your package."}
                                    </p>
                                  </div>
                                </label>
                              </div>
                            )}

                            {loadingItinerary ? (
                              <div className="flex items-center justify-center gap-2 py-8">
                                <div className="animate-spin h-4 w-4 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
                                <span className="text-white/50 text-sm">Crafting your itinerary...</span>
                              </div>
                            ) : itinerary?.itinerary?.length > 0 ? (
                              <div className="space-y-4">
                                {itinerary.itinerary.map((day) => (
                                  <div key={`itinerary-day-${day.day}`} className="bg-white/[0.02] border border-white/5 rounded-lg overflow-hidden" data-testid={`itinerary-day-${day.day}`}>
                                    {day.photo_url && (
                                      <div className="w-full h-32 sm:h-36 overflow-hidden bg-white/5">
                                        <img
                                          src={day.photo_url}
                                          alt={day.title}
                                          loading="lazy"
                                          referrerPolicy="no-referrer"
                                          onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                                          className="w-full h-full object-cover"
                                          data-testid={`itinerary-day-${day.day}-photo`}
                                        />
                                      </div>
                                    )}
                                    <div className="p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="w-7 h-7 rounded-full bg-[#D4AF37]/15 flex items-center justify-center text-[#D4AF37] text-[10px] font-medium flex-shrink-0">Day {day.day}</span>
                                        <h4 className="text-white text-sm font-medium">{day.title}</h4>
                                      </div>
                                      <div className="ml-9 space-y-1.5">
                                        {day.activities?.map((act, j) => (
                                          <p key={`day-${day.day}-act-${j}`} className="text-white/50 text-xs flex items-start gap-1.5">
                                            <span className="text-[#D4AF37] mt-0.5 flex-shrink-0">—</span>
                                            {act}
                                          </p>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-white/30 text-sm text-center py-4">Itinerary not available</p>
                            )}
                            {!includeItineraryCost && (
                              <p className="text-white/30 text-[11px] text-center mt-4 italic">
                                Disclaimer: These activities are recommendations only and are not included as part of the travel package.
                              </p>
                            )}

                            {/* Additional Requests — free-form notes captured on the booking
                                so the travel-ops team can customise the trip (add a spot,
                                food preference, accessibility need, etc.). */}
                            <div className="mt-6 bg-white/[0.02] border border-white/5 rounded-lg p-4" data-testid="additional-requests-card">
                              <label htmlFor="additional-requests-input" className="block text-white text-sm font-medium mb-1">
                                Any special requests?
                              </label>
                              <p className="text-white/40 text-[11px] mb-3">
                                E.g. &quot;Please add a sunset boat ride&quot;, &quot;Vegetarian meals only&quot;, &quot;Include local cooking class on Day 3&quot;.
                              </p>
                              <textarea
                                id="additional-requests-input"
                                value={additionalRequests}
                                onChange={(e) => setAdditionalRequests(e.target.value.slice(0, 1000))}
                                rows={3}
                                placeholder="Add any specific spots, preferences, or notes for our travel team…"
                                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50 resize-y"
                                data-testid="additional-requests-input"
                              />
                              <div className="flex items-center justify-between mt-1">
                                <p className="text-white/30 text-[10px]">Our team reviews every note and tweaks your itinerary before the trip.</p>
                                <p className="text-white/30 text-[10px]" data-testid="additional-requests-counter">{additionalRequests.length}/1000</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Top Cuisines & Places to Eat (collapsible) ── */}
                    {selectedDestination && selectedPackage && !selectedDestination.is_test_package && (
                      <div className="border-t border-white/5" data-testid="cuisines-section">
                        <button
                          onClick={() => setExpandCuisines(!expandCuisines)}
                          className="w-full flex items-center justify-between py-5 hover:opacity-80 transition-opacity"
                          data-testid="toggle-cuisines"
                        >
                          <div className="text-left">
                            <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Eat Like a Local</p>
                            <h3 className="font-serif text-lg text-white">Top Cuisines & Places to Eat</h3>
                          </div>
                          <ChevronLeft className={`w-4 h-4 text-white/40 transition-transform duration-300 ${expandCuisines ? "rotate-90" : "-rotate-90"}`} />
                        </button>
                        {expandCuisines && (
                          <div className="pb-6 space-y-5">
                            {loadingCuisines ? (
                              <p className="text-white/40 text-sm text-center py-4">Loading recommendations…</p>
                            ) : cuisinesData?.items?.length > 0 ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {cuisinesData.items.map((item) => (
                                  <div
                                    key={`cuisine-item-${item.name}`}
                                    className="bg-white/[0.03] border border-white/5 rounded-lg overflow-hidden flex flex-col"
                                    data-testid={`cuisine-item-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                                  >
                                    {item.photo_url && (
                                      <div className="w-full h-36 overflow-hidden bg-white/5">
                                        <img
                                          src={item.photo_url}
                                          alt={item.name}
                                          loading="lazy"
                                          referrerPolicy="no-referrer"
                                          onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                                          className="w-full h-full object-cover"
                                          data-testid={`cuisine-photo-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                                        />
                                      </div>
                                    )}
                                    <div className="p-3 space-y-2 flex-1">
                                      <p className="text-white text-sm font-medium">{item.name}</p>
                                      {item.contains && (
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]/80 mb-0.5">Ingredients</p>
                                          <p className="text-white/55 text-xs leading-relaxed">{item.contains}</p>
                                        </div>
                                      )}
                                      {item.where_to_eat && (
                                        <div>
                                          <p className="text-[10px] uppercase tracking-wider text-[#D4AF37]/80 mb-0.5">Where to eat</p>
                                          <p className="text-white/55 text-xs leading-relaxed">{item.where_to_eat}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-white/30 text-sm text-center py-4">Recommendations not available</p>
                            )}
                            <p className="text-white/30 text-[11px] text-center italic">
                              Disclaimer: Dining costs are not included in the package. Restaurants are recommendations only — please check current operating status before visiting.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Visa assistance + passport check (Iter 45) ───
                        Only renders for INTERNATIONAL destinations. The
                        passport-visa attestation hardens to a mandatory
                        checkbox when the customer is travelling within 30
                        days (most embassies cannot process a fresh visa
                        in that window). */}
                    {(selectedDestination?.type || "domestic").toLowerCase() === "international" && (() => {
                      const daysUntilTravel = startDate
                        ? Math.ceil((new Date(startDate) - new Date()) / (1000 * 60 * 60 * 24))
                        : null;
                      const requiresPassportCheck = daysUntilTravel !== null && daysUntilTravel < 30;
                      return (
                        <div
                          data-testid="visa-block"
                          className="rounded-lg border border-blue-400/20 bg-blue-500/[0.05] p-4 space-y-3"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-blue-200 text-sm font-semibold tracking-wide">
                                ✈️ Need visa assistance for {selectedDestination?.name}?
                              </p>
                              <p className="text-white/55 text-xs mt-1 leading-relaxed">
                                Our visa partner will help you collect documents, fill the form, and book your appointment. <span className="text-amber-200/90 font-medium">Visa fees are charged separately by the embassy and are not included in this package.</span>
                              </p>
                            </div>
                            <label className="shrink-0 inline-flex items-center cursor-pointer" data-testid="visa-assistance-switch-label">
                              <input
                                type="checkbox"
                                checked={needsVisaAssistance}
                                onChange={(e) => setNeedsVisaAssistance(e.target.checked)}
                                className="sr-only peer"
                                data-testid="visa-assistance-switch"
                              />
                              <span className="relative inline-block w-11 h-6 bg-white/10 peer-checked:bg-[#D4AF37] rounded-full transition-colors after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></span>
                            </label>
                          </div>
                          {needsVisaAssistance && (
                            <p
                              className="text-[11px] text-emerald-200/80 bg-emerald-500/10 border border-emerald-400/20 rounded px-3 py-2"
                              data-testid="visa-assistance-confirmation"
                            >
                              Got it — our visa team will reach out within 24 hours after your booking is confirmed. Visa fees will be invoiced separately.
                            </p>
                          )}

                          {requiresPassportCheck && (
                            <label
                              className="flex items-start gap-2.5 cursor-pointer rounded-md border border-rose-400/30 bg-rose-500/[0.07] px-3 py-2.5"
                              data-testid="passport-visa-confirm-label"
                            >
                              <input
                                type="checkbox"
                                checked={passportVisaConfirmed}
                                onChange={(e) => setPassportVisaConfirmed(e.target.checked)}
                                className="mt-0.5 accent-[#D4AF37] shrink-0"
                                data-testid="passport-visa-confirm"
                              />
                              <span className="text-xs text-rose-100/95 leading-relaxed">
                                <span className="font-semibold text-rose-200">Required — travel in {daysUntilTravel} days:</span>{" "}
                                I confirm I (and every traveller above) hold a <span className="font-medium">valid passport</span> AND a <span className="font-medium">valid visa</span> for {selectedDestination?.name}. I understand that embassies typically cannot issue new visas inside 30 days and that Travelsate is not responsible for boarding denial if these documents are missing.
                              </span>
                            </label>
                          )}
                        </div>
                      );
                    })()}

                    <div className="flex gap-4 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => setStep(4)}
                        className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5"
                        data-testid="back-to-travelers-button"
                      >
                        <ChevronLeft className="w-4 h-4 mr-2" /> Back
                      </Button>
                      <Button
                        onClick={handleCreateBooking}
                        disabled={
                          !selectedPackage ||
                          // International + travel within 30 days requires the
                          // passport/visa attestation checkbox. Iter 45.
                          (
                            (selectedDestination?.type || "domestic").toLowerCase() === "international" &&
                            startDate &&
                            (new Date(startDate) - new Date()) / (1000 * 60 * 60 * 24) < 30 &&
                            !passportVisaConfirmed
                          )
                        }
                        className="flex-1 bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="continue-to-payment-button"
                      >
                        Continue to Payment
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {step === 6 && costs && (
                <Card className="glass-card p-8" data-testid="payment-breakdown-step">
                  <div className="space-y-6">
                    <div className="text-center">
                      <h2 className="font-serif text-3xl text-white mb-2">Payment Plan</h2>
                      <p className="text-white/60">Choose your payment schedule</p>
                      {costs.daysUntilTravel && (
                        <p className="text-white/60 mt-2">
                          {costs.daysUntilTravel} days until your trip
                        </p>
                      )}
                    </div>

                    <Card className="bg-white/5 border-white/10 p-6">
                      <h3 className="font-serif text-xl text-white mb-4">Booking Summary</h3>
                      {/* ── PRICE LOCK (Iter 49) ─────────────────────────
                          Display the amounts persisted on the booking row,
                          NOT `costs.*` (client-side live recompute). This
                          guarantees the number the customer sees is the
                          EXACT number `/payments/create-order` will send
                          to Razorpay — same source of truth on both ends.
                          Fallback to `costs.*` only for the split second
                          before `setBooking(response.data)` returns. */}
                      {(() => {
                        const lockedTotal = booking?.total_cost ?? costs.totalCost;
                        const lockedUpfront = booking?.booking_cost ?? costs.bookingCost;
                        const lockedRemaining = Math.max(0, (lockedTotal ?? 0) - (lockedUpfront ?? 0));
                        return (
                          <div className="space-y-2">
                            <div className="flex justify-between text-white/80">
                              <span>Total Package Cost</span>
                              <span data-testid="locked-total-cost">₹{Number(lockedTotal).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            </div>
                            <div className="flex justify-between text-[#D4AF37] font-medium">
                              <span>Booking Cost (1%)</span>
                              <span data-testid="booking-cost">₹{Number(lockedUpfront).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            </div>
                            <div className="flex justify-between text-white/80">
                              <span>Remaining Amount</span>
                              <span data-testid="locked-remaining">₹{Number(lockedRemaining).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </Card>

                    {costs.paymentOptions && costs.paymentOptions.length > 0 ? (
                      <>
                        <div>
                          <Label className="text-white mb-2 block">Select Payment Plan</Label>
                          <p className="text-white/40 text-[11px] mb-4 leading-relaxed" data-testid="emi-buffer-note">
                            {(selectedDestination?.type || "domestic").toLowerCase() === "international"
                              ? "EMIs complete 30 days before travel so we can secure international flights, hotels and visas with comfortable lead time."
                              : "EMIs complete 15 days before travel so we can lock in your flights, hotels and transfers with comfortable lead time."}
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
                            {costs.paymentOptions.find(opt => opt.type === 'weekly') && (
                              <button
                                data-testid="weekly-plan-tab"
                                onClick={() => setPaymentPlan("weekly")}
                                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all border ${
                                  paymentPlan === "weekly"
                                    ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                                    : "bg-white/5 border-white/10 text-white/60 hover:border-white/20"
                                }`}
                              >
                                Weekly ({costs.paymentOptions.find(opt => opt.type === 'weekly').installments}x)
                              </button>
                            )}
                            {costs.paymentOptions.find(opt => opt.type === 'biweekly') && (
                              <button
                                data-testid="biweekly-plan-tab"
                                onClick={() => setPaymentPlan("biweekly")}
                                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all border ${
                                  paymentPlan === "biweekly"
                                    ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                                    : "bg-white/5 border-white/10 text-white/60 hover:border-white/20"
                                }`}
                              >
                                Bi-Weekly ({costs.paymentOptions.find(opt => opt.type === 'biweekly').installments}x)
                              </button>
                            )}
                            {costs.paymentOptions.find(opt => opt.type === 'monthly') && (
                              <button
                                data-testid="monthly-plan-tab"
                                onClick={() => setPaymentPlan("monthly")}
                                className={`px-3 py-3 rounded-lg text-sm font-medium transition-all border ${
                                  paymentPlan === "monthly"
                                    ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                                    : "bg-white/5 border-white/10 text-white/60 hover:border-white/20"
                                }`}
                              >
                                Monthly ({costs.paymentOptions.find(opt => opt.type === 'monthly').installments}x)
                              </button>
                            )}
                            <button
                              data-testid="full-payment-tab"
                              onClick={() => setPaymentPlan("full")}
                              className={`px-3 py-3 rounded-lg text-sm font-medium transition-all border ${
                                paymentPlan === "full"
                                  ? "bg-[#D4AF37]/20 border-[#D4AF37] text-[#D4AF37]"
                                  : "bg-white/5 border-white/10 text-white/60 hover:border-white/20"
                              }`}
                            >
                              Full Payment
                            </button>
                          </div>

                          {paymentPlan === "weekly" && costs.paymentOptions.find(opt => opt.type === 'weekly') && (
                            <Card className="bg-white/5 border-white/10 p-6">
                              <p className="text-white mb-2">Pay in {costs.paymentOptions.find(opt => opt.type === 'weekly').installments} weekly installments</p>
                              <p className="text-[#D4AF37] text-2xl font-medium" data-testid="weekly-installment-amount">
                                ₹{costs.paymentOptions.find(opt => opt.type === 'weekly').amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })} / week
                              </p>
                              <p className="text-white/60 text-sm mt-2">Starting one week after booking</p>
                            </Card>
                          )}

                          {paymentPlan === "biweekly" && costs.paymentOptions.find(opt => opt.type === 'biweekly') && (
                            <Card className="bg-white/5 border-white/10 p-6">
                              <p className="text-white mb-2">Pay in {costs.paymentOptions.find(opt => opt.type === 'biweekly').installments} bi-weekly installments</p>
                              <p className="text-[#D4AF37] text-2xl font-medium" data-testid="biweekly-installment-amount">
                                ₹{costs.paymentOptions.find(opt => opt.type === 'biweekly').amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })} / 2 weeks
                              </p>
                              <p className="text-white/60 text-sm mt-2">Starting two weeks after booking</p>
                            </Card>
                          )}

                          {paymentPlan === "monthly" && costs.paymentOptions.find(opt => opt.type === 'monthly') && (
                            <Card className="bg-white/5 border-white/10 p-6">
                              <p className="text-white mb-2">Pay in {costs.paymentOptions.find(opt => opt.type === 'monthly').installments} monthly installments</p>
                              <p className="text-[#D4AF37] text-2xl font-medium" data-testid="monthly-installment-amount">
                                ₹{costs.paymentOptions.find(opt => opt.type === 'monthly').amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })} / month
                              </p>
                              <p className="text-white/60 text-sm mt-2">Starting one month after booking</p>
                            </Card>
                          )}

                          {paymentPlan === "full" && (
                            <Card className="bg-white/5 border-white/10 p-6" data-testid="full-payment-content">
                              <p className="text-white mb-2">Pay the full amount now — no installments</p>
                              <p className="text-[#D4AF37] text-2xl font-medium" data-testid="full-payment-amount">
                                {/* Iter 53 — Use the server-locked booking total,
                                    NOT the live client recompute. Fixes the
                                    Step-6 mismatch where this tile displayed
                                    a different ₹ than the header /
                                    "Pay Full Amount" button / Razorpay charge. */}
                                ₹{Number(booking?.total_cost ?? costs.totalCost).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </p>
                              <p className="text-white/60 text-sm mt-2">One-time payment, nothing more to worry about</p>
                            </Card>
                          )}
                        </div>

                        {paymentPlan === "full" ? (
                          <Card className="bg-[#D4AF37]/10 border-[#D4AF37]/20 p-4">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                                <Check className="w-5 h-5 text-[#D4AF37]" />
                              </div>
                              <div className="flex-1">
                                <p className="text-white font-medium">Full Payment Selected</p>
                                <p className="text-white/60 text-sm">No future installments — everything is covered</p>
                              </div>
                            </div>
                          </Card>
                        ) : (
                          <Card className="bg-green-500/10 border-green-500/20 p-4">
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                                <Check className="w-5 h-5 text-green-400" />
                              </div>
                              <div className="flex-1">
                                <p className="text-white font-medium">Auto-Pay Enabled</p>
                                <p className="text-white/60 text-sm">Installments will be automatically debited on schedule</p>
                              </div>
                            </div>
                          </Card>
                        )}

                        {/* Cancellation & Reschedule Policy */}
                        <Card className="bg-white/[0.02] border-white/10 p-5" data-testid="cancellation-policy">
                          <h4 className="text-white text-sm font-medium mb-3">Cancellation & Reschedule Policy</h4>
                          <div className="space-y-2.5 text-xs">
                            <div className="flex gap-2">
                              <span className="text-red-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">Bookings once confirmed are <span className="text-white/70">non-refundable</span>. The 1% booking fee is non-refundable under all circumstances.</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-amber-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">Cancellations made <span className="text-white/70">30+ days before travel</span> are eligible for a travel credit of 75% of the amount paid (excluding booking fee), valid for 6 months.</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-amber-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">Cancellations made <span className="text-white/70">15–30 days before travel</span> are eligible for a travel credit of 40% of the amount paid (excluding booking fee).</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-red-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">Cancellations within <span className="text-white/70">15 days of travel</span> are not eligible for any refund or credit.</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-amber-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">One reschedule is permitted <span className="text-white/70">30+ days before travel</span>, subject to a ₹2,500 reschedule fee plus any fare/rate difference for the new dates.</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-red-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">Reschedule requests within <span className="text-white/70">30 days of travel</span> are not permitted.</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-red-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">No-shows or failure to complete installment payments will result in <span className="text-white/70">forfeiture of all amounts paid</span>.</p>
                            </div>
                          </div>
                          <p className="text-white/30 text-[10px] mt-3 italic">By proceeding with payment, you agree to the above cancellation and reschedule terms.</p>
                        </Card>

                        {paymentBusy && (
                          <div
                            className="mb-3 border border-amber-400/40 bg-amber-500/10 rounded-md p-3 flex items-start gap-2.5"
                            data-testid="payment-busy-disclaimer"
                          >
                            <div className="w-5 h-5 mt-0.5 shrink-0 rounded-full border-2 border-amber-300 border-t-transparent animate-spin" />
                            <p className="text-amber-100 text-xs leading-relaxed">
                              <span className="font-semibold text-amber-200">Payment in progress.</span>{" "}
                              Please stay on this page and wait for the next instruction.{" "}
                              <span className="text-amber-200/90">Do not refresh or click back</span> — doing so may fail the transaction.
                            </p>
                          </div>
                        )}
                        {iosPaymentTimedOut && !paymentBusy && (
                          <div
                            className="mb-3 border border-rose-400/40 bg-rose-500/10 rounded-md p-3 flex items-start gap-2.5"
                            data-testid="payment-timeout-banner"
                          >
                            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0 text-rose-300" />
                            <p className="text-rose-100 text-xs leading-relaxed flex-1">
                              <span className="font-semibold text-rose-200">Payment didn't open.</span>{" "}
                              Your network or browser may have blocked the secure checkout. No charge was attempted — tap Retry below.
                            </p>
                          </div>
                        )}
                        <div className="flex gap-4">
                          <Button
                            variant="outline"
                            onClick={() => setStep(5)}
                            disabled={paymentBusy}
                            className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5 disabled:opacity-50"
                            data-testid="back-to-packages-button"
                          >
                            <ChevronLeft className="w-4 h-4 mr-2" /> Back
                          </Button>
                          <Button
                            onClick={handlePayment}
                            disabled={paymentBusy || !razorpayReady}
                            className="flex-1 bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-60"
                            data-testid={iosPaymentTimedOut ? "retry-payment-button" : "proceed-to-payment-button"}
                          >
                            {paymentBusy
                              ? "Processing…"
                              : !razorpayReady
                              ? "Loading secure payment…"
                              : iosPaymentTimedOut
                              ? "Retry Secure Payment"
                              : paymentPlan === "full"
                              ? `Pay Full Amount (₹${Number(booking?.total_cost ?? costs.totalCost).toLocaleString('en-IN', { maximumFractionDigits: 0 })})`
                              : `Pay Booking Cost (₹${Number(booking?.booking_cost ?? costs.bookingCost).toLocaleString('en-IN', { maximumFractionDigits: 0 })})`
                            }
                          </Button>
                        </div>
                        {/* Price-lock trust label (Iter 49) — reassures the
                            customer that the number on this page is exactly
                            what Razorpay will charge on the next screen. */}
                        {booking && (
                          <p
                            className="text-center text-white/40 text-[11px] mt-2 flex items-center justify-center gap-1"
                            data-testid="price-lock-trust-label"
                          >
                            <Lock className="w-3 h-3" />
                            <span>
                              Price locked · Razorpay will charge exactly{" "}
                              <span className="text-[#D4AF37]">
                                ₹{Number(
                                  paymentPlan === "full" ? booking.total_cost : booking.booking_cost
                                ).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                              </span>
                            </span>
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <Card className="bg-amber-500/10 border-amber-500/20 p-6">
                          <p className="text-amber-400 font-medium mb-2">Full Payment Required</p>
                          <p className="text-white/80 text-sm mb-3">
                            Your trip is only {costs.daysUntilTravel} days away. Installment payment is not available for bookings made less than 28 days before travel.
                          </p>
                          <div className="bg-white/5 rounded p-4">
                            <div className="flex justify-between items-center">
                              <span className="text-white font-medium">Amount to Pay Now:</span>
                              <span className="text-[#D4AF37] text-2xl font-semibold" data-testid="locked-full-amount">
                                ₹{Number(booking?.total_cost ?? costs.totalCost).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                              </span>
                            </div>
                            <p className="text-white/60 text-xs mt-2">Full package cost (no installments)</p>
                          </div>
                        </Card>

                        {/* Postpone by 6 months proposal */}
                        {(() => {
                          const tripDuration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                          const proposedStart = new Date(startDate);
                          proposedStart.setMonth(proposedStart.getMonth() + 6);
                          const proposedEnd = new Date(proposedStart);
                          proposedEnd.setDate(proposedEnd.getDate() + tripDuration);
                          const proposedDaysUntil = Math.ceil((proposedStart - new Date()) / (1000 * 60 * 60 * 24));
                          const futureRemaining = costs.remainingAmount;
                          const futureWeeks = Math.floor(proposedDaysUntil / 7);
                          const futureBiweeks = Math.floor(proposedDaysUntil / 15);
                          const futureMonths = Math.floor(proposedDaysUntil / 30);
                          const options = [];
                          if (futureWeeks >= 2) options.push({ type: "weekly", label: "Weekly", installments: futureWeeks, amount: futureRemaining / futureWeeks });
                          if (futureBiweeks >= 2) options.push({ type: "biweekly", label: "Bi-Weekly", installments: futureBiweeks, amount: futureRemaining / futureBiweeks });
                          if (futureMonths >= 2) options.push({ type: "monthly", label: "Monthly", installments: futureMonths, amount: futureRemaining / futureMonths });

                          return (
                            <Card className="bg-[#D4AF37]/5 border-[#D4AF37]/20 p-6" data-testid="postpone-proposal">
                              <div className="flex items-center gap-2 mb-3">
                                <CalendarIcon className="w-5 h-5 text-[#D4AF37]" />
                                <h4 className="text-white font-medium">Postpone your trip by 6 months?</h4>
                              </div>
                              <p className="text-white/60 text-sm mb-4">
                                Travel from <span className="text-[#D4AF37] font-medium">{format(proposedStart, "dd MMM yyyy")}</span> to <span className="text-[#D4AF37] font-medium">{format(proposedEnd, "dd MMM yyyy")}</span> ({tripDuration} nights, {proposedDaysUntil} days away) and unlock flexible payment plans:
                              </p>

                              <div className="bg-white/[0.03] rounded-lg p-4 mb-4">
                                <div className="flex justify-between text-sm mb-2">
                                  <span className="text-white/60">Upfront Booking Cost (1%)</span>
                                  <span className="text-[#D4AF37] font-medium">₹{costs.bookingCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                                </div>
                                <p className="text-white/40 text-xs mb-3">Then split the remaining ₹{futureRemaining.toLocaleString('en-IN', { maximumFractionDigits: 0 })} into:</p>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  {options.map((opt) => (
                                    <div key={opt.label} className="bg-white/[0.04] border border-white/5 rounded-lg p-3 text-center">
                                      <p className="text-white/50 text-xs mb-1">{opt.label}</p>
                                      <p className="text-[#D4AF37] text-lg font-medium">
                                        ₹{opt.amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                      </p>
                                      <p className="text-white/30 text-[11px] mt-0.5">{opt.installments} installments</p>
                                    </div>
                                  ))}
                                </div>
                              </div>

                              <Button
                                data-testid="switch-to-postponed-dates"
                                disabled={shiftingDates}
                                onClick={async () => {
                                  // 1. Update the local date state so the next
                                  //    /bookings POST sees the new dates.
                                  // 2. Re-create the booking server-side. The
                                  //    OLD booking row is left orphaned (no
                                  //    payment was attached). The new row
                                  //    carries the postponed dates so
                                  //    /payments/create-order's schedule check
                                  //    will pass.
                                  setStartDate(proposedStart);
                                  setEndDate(proposedEnd);
                                  setShiftingDates(true);
                                  try {
                                    const res = await axios.post(`${API}/bookings`, {
                                      destination_id: selectedDestination.id,
                                      start_date: proposedStart.toISOString(),
                                      end_date: proposedEnd.toISOString(),
                                      adults,
                                      children,
                                      infants,
                                      package_type: selectedPackage,
                                      adult_names: adultNames,
                                      adult_genders: adultGenders,
                                      adult_meals: adultMeals,
                                      child_names: childNames,
                                      child_genders: childGenders,
                                      child_meals: childMeals,
                                      infant_names: infantNames,
                                      infant_genders: infantGenders,
                                      infant_meals: infantMeals,
                                      infant_dobs: infantDOBs.map((d) => (d ? d.toISOString() : null)),
                                      departure_city: departureCity,
                                      departure_airport: departureAirport,
                                      include_itinerary_cost: includeItineraryCost,
                                      include_flight_cost: includeFlightCost,
                                      include_hotel_cost: includeHotelCost,
                                      include_transfer_cost: includeTransferCost,
                                      itinerary_days_selected: itineraryDaysSelected ?? undefined,
                                      additional_requests: additionalRequests?.trim() || undefined,
                                      client_quote: costs ? {
                                        flight_costs: costs.flightCosts,
                                        hotel_costs: costs.hotelCosts,
                                        transfer_costs: costs.transferCosts,
                                        itinerary_cost: costs.itineraryCost,
                                      } : undefined,
                                    });
                                    setBooking(res.data);
                                    toast.success(
                                      `Travel dates updated to ${format(proposedStart, "dd MMM yyyy")} — ${format(proposedEnd, "dd MMM yyyy")}`,
                                      { description: "Installment options are now available" }
                                    );
                                  } catch (err) {
                                    // Roll back the date state so the UI
                                    // stays consistent with the live booking.
                                    setStartDate(startDate);
                                    setEndDate(endDate);
                                    toast.error(
                                      "Could not update travel dates: " +
                                        (err.response?.data?.detail || err.message)
                                    );
                                  } finally {
                                    setShiftingDates(false);
                                  }
                                }}
                                className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C468] font-medium disabled:opacity-60"
                              >
                                {shiftingDates
                                  ? "Updating booking…"
                                  : `Switch to ${format(proposedStart, "dd MMM yyyy")} — Unlock Installments`}
                              </Button>
                            </Card>
                          );
                        })()}

                        <div className="relative flex items-center justify-center my-2">
                          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
                          <span className="relative bg-[#111] px-4 text-white/30 text-xs uppercase tracking-wider">or</span>
                        </div>

                        {/* Cancellation & Reschedule Policy */}
                        <Card className="bg-white/[0.02] border-white/10 p-5" data-testid="cancellation-policy-full">
                          <h4 className="text-white text-sm font-medium mb-3">Cancellation & Reschedule Policy</h4>
                          <div className="space-y-2.5 text-xs">
                            <div className="flex gap-2">
                              <span className="text-red-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">Bookings once confirmed are <span className="text-white/70">non-refundable</span>. The 1% booking fee is non-refundable under all circumstances.</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-red-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">Cancellations within <span className="text-white/70">15 days of travel</span> are not eligible for any refund or credit.</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-red-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">Reschedule requests within <span className="text-white/70">30 days of travel</span> are not permitted.</p>
                            </div>
                            <div className="flex gap-2">
                              <span className="text-red-400 flex-shrink-0 mt-0.5">&#x2022;</span>
                              <p className="text-white/50">No-shows will result in <span className="text-white/70">forfeiture of all amounts paid</span>.</p>
                            </div>
                          </div>
                          <p className="text-white/30 text-[10px] mt-3 italic">By proceeding with payment, you agree to the above cancellation and reschedule terms.</p>
                        </Card>

                        {paymentBusy && (
                          <div
                            className="mb-3 border border-amber-400/40 bg-amber-500/10 rounded-md p-3 flex items-start gap-2.5"
                            data-testid="payment-busy-disclaimer-keep-dates"
                          >
                            <div className="w-5 h-5 mt-0.5 shrink-0 rounded-full border-2 border-amber-300 border-t-transparent animate-spin" />
                            <p className="text-amber-100 text-xs leading-relaxed">
                              <span className="font-semibold text-amber-200">Payment in progress.</span>{" "}
                              Please stay on this page and wait for the next instruction.{" "}
                              <span className="text-amber-200/90">Do not refresh or click back</span> — doing so may fail the transaction.
                            </p>
                          </div>
                        )}
                        <div className="flex gap-4">
                          <Button
                            variant="outline"
                            onClick={() => setStep(5)}
                            disabled={paymentBusy}
                            className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5 disabled:opacity-50"
                            data-testid="back-to-packages-button"
                          >
                            <ChevronLeft className="w-4 h-4 mr-2" /> Back
                          </Button>
                          <Button
                            onClick={handlePayment}
                            disabled={paymentBusy || !razorpayReady}
                            className="flex-1 bg-white/10 border border-white/20 text-white hover:bg-white/15 disabled:opacity-60"
                            data-testid="proceed-to-payment-button"
                          >
                            {paymentBusy
                              ? "Processing…"
                              : !razorpayReady
                              ? "Loading secure payment…"
                              : `Keep Current Dates — Pay Full (₹${Number(booking?.total_cost ?? costs.totalCost).toLocaleString('en-IN', { maximumFractionDigits: 0 })})`}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Step 7: Booking Confirmation */}
          {step === 7 && bookingConfirmed && costs && (
            <AnimatePresence mode="wait">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto">
                <Card className="bg-white/5 border-white/10 p-6 sm:p-8" data-testid="booking-confirmation">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-400" />
                    </div>
                    <h2 className="font-serif text-2xl text-white mb-2">Booking Confirmed!</h2>
                    <p className="text-white/50 text-sm">Your booking reference: <span className="text-[#D4AF37] font-medium tracking-wider">{booking?.id?.slice(0, 8).toUpperCase()}</span></p>
                    <p className="text-white/30 text-xs mt-1">A confirmation email has been sent to your registered email address.</p>
                  </div>

                  {/* Auto-debit setup not yet completed — surface a clear,
                      recoverable CTA right at the top of the confirmation. */}
                  {booking?.payment_status === "upfront_paid" && !booking?.razorpay_subscription_id && (
                    <div
                      className="mb-6 border border-amber-400/40 bg-amber-500/10 rounded-lg p-4"
                      data-testid="mandate-pending-banner"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                          <Shield className="w-4 h-4 text-amber-300" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-amber-200 text-sm font-medium">Auto-debit setup pending</p>
                          <p className="text-amber-100/70 text-xs mt-1 leading-relaxed">
                            Your 1% upfront is captured and your seat is reserved. To start your EMI schedule,
                            please complete the one-time auto-debit mandate authorisation.
                          </p>
                          <Button
                            onClick={() => { setMandateError(null); setMandateInterceptOpen(true); }}
                            className="mt-3 bg-amber-400 hover:bg-amber-300 text-black text-xs font-medium h-9 px-4"
                            data-testid="mandate-retry-from-confirmation"
                          >
                            Set up auto-debit now
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">Booking Details</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-white/50">Destination</span><span className="text-white">{selectedDestination?.name}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Package</span><span className="text-[#D4AF37] font-medium">{selectedPackage?.charAt(0).toUpperCase() + selectedPackage?.slice(1)}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Travel Dates</span><span className="text-white">{startDate && format(startDate, "dd MMM yyyy")} — {endDate && format(endDate, "dd MMM yyyy")}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Duration</span><span className="text-white">{costs.days} nights</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Traveling From</span><span className="text-white">{departureCity || "N/A"} ({departureAirport})</span></div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">Traveler Details</h3>
                      <div className="space-y-1.5 text-sm">
                        {adultNames.map((n, i) => (
                          <div key={`a-${i}`} className="flex justify-between"><span className="text-white/50">Adult {i + 1}</span><span className="text-white">{n} ({adultGenders[i]?.charAt(0).toUpperCase() + adultGenders[i]?.slice(1)})</span></div>
                        ))}
                        {childNames.map((n, i) => (
                          <div key={`c-${i}`} className="flex justify-between"><span className="text-white/50">Child {i + 1}</span><span className="text-white">{n} ({childGenders[i]?.charAt(0).toUpperCase() + childGenders[i]?.slice(1)})</span></div>
                        ))}
                        {infantNames.map((n, i) => (
                          <div key={`i-${i}`} className="flex justify-between"><span className="text-white/50">Infant {i + 1}</span><span className="text-white">{n} ({infantGenders[i]?.charAt(0).toUpperCase() + infantGenders[i]?.slice(1)}){infantDOBs[i] ? ` DOB: ${format(infantDOBs[i], "dd MMM yyyy")}` : ""}</span></div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">Cost Breakdown</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-white/50">Flights ({adults}A + {children}C + {infants}I)</span><span className="text-white">₹{costs.flightCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Hotel ({costs.roomsNeeded} room × {costs.days} nights)</span><span className="text-white">₹{costs.hotelCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">{costs.useRoadTravel ? "Road Transfer (Round Trip)" : "Transfers (4-leg)"}</span><span className="text-white">₹{costs.transferCosts.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
                        {costs.itineraryApplied && (
                          <div className="flex justify-between"><span className="text-white/50">Itinerary ({costs.itineraryDays}d × pax)</span><span className="text-white">₹{costs.itineraryCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
                        )}
                        <div className="flex justify-between"><span className="text-white/50">Tax (18%)</span><span className="text-white">₹{costs.tax.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
                        <div className="flex justify-between pt-2 border-t border-white/10"><span className="text-white font-medium">Total Cost</span><span className="text-[#D4AF37] text-lg font-medium">₹{costs.totalCost.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
                        {/* Quote-locked badge — surfaces the same condition the
                            server uses (trip within 180d) so the customer sees
                            an explicit trust signal right above the Pay button.
                            Tooltip explains when the lock re-quotes. */}
                        {(() => {
                          if (!startDate) return null;
                          const daysToTravel = Math.ceil((startDate - new Date()) / (1000 * 60 * 60 * 24));
                          if (daysToTravel <= 0 || daysToTravel > 180) return null;
                          return (
                            <div
                              className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#D4AF37]/80 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-full px-2.5 py-1 self-start group relative cursor-help"
                              data-testid="quote-locked-badge"
                            >
                              <Lock className="w-3 h-3" />
                              <span>Quote locked for this booking</span>
                              <span className="pointer-events-none absolute left-0 bottom-full mb-1.5 w-64 bg-black/95 text-white/80 text-[11px] leading-snug normal-case tracking-normal px-3 py-2 rounded-md border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                Your price is held at the value shown on Step 5. It will be re-quoted only if you change dates, itinerary days, package, or traveller count.
                              </span>
                            </div>
                          );
                        })()}
                        {/* Payment security trust strip — the compact
                            RazorpayTrustBadge component replaced the inline
                            text strip in Iteration 40 so customers see a
                            recognisable Razorpay-branded certification
                            card at the moment of payment (vs the older
                            line of low-key grey text). */}
                        <RazorpayTrustBadge variant="compact" className="mt-3" />
                        <SSLVerifiedBadge variant="compact" className="mt-2" />
                      </div>
                    </div>

                    <div>
                      <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">Payment Details</h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-white/50">Amount Paid</span><span className="text-white">₹{(paymentPlan === "full" ? costs.totalCost : costs.bookingCost).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Payment Plan</span><span className="text-white">{paymentPlan === "full" ? "Full Payment" : paymentPlan.charAt(0).toUpperCase() + paymentPlan.slice(1) + " Installments"}</span></div>
                        <div className="flex justify-between"><span className="text-white/50">Status</span><span className="text-green-400 font-medium">Confirmed</span></div>
                      </div>
                    </div>

                    {/* Estimated Booking Schedule — only for installment plans */}
                    {booking?.booking_schedule && (
                      <div data-testid="booking-schedule-section">
                        <h3 className="text-[#D4AF37] text-sm font-medium mb-2 border-b border-white/10 pb-2">Estimated Booking Schedule</h3>
                        <p className="text-white/50 text-xs mb-3 leading-relaxed">
                          As your installments come through and the funds clear, each component will be booked in this order:
                        </p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-white/60">✈ Flights booked by</span><span className="text-white" data-testid="schedule-flights">{new Date(booking.booking_schedule.flights_booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>
                          <div className="flex justify-between"><span className="text-white/60">🏨 Hotel booked by</span><span className="text-white" data-testid="schedule-hotel">{new Date(booking.booking_schedule.hotel_booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>
                          <div className="flex justify-between"><span className="text-white/60">🚗 Transfers confirmed by</span><span className="text-white" data-testid="schedule-transfers">{new Date(booking.booking_schedule.transfers_booking_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span></div>
                        </div>
                        <p className="text-white/35 text-[11px] mt-3 italic">These dates assume your installments are paid on time. Any delay shifts them forward.</p>
                      </div>
                    )}

                    {/* Installment Plan — full per-EMI breakdown */}
                    {booking?.booking_schedule?.installments?.length > 0 && (
                      <div data-testid="installment-plan-section">
                        <h3 className="text-[#D4AF37] text-sm font-medium mb-2 border-b border-white/10 pb-2">Your Installment Plan</h3>
                        <p className="text-white/50 text-xs mb-3 leading-relaxed">
                          {booking.booking_schedule.n_installments} {booking.booking_schedule.payment_plan} installments
                          {booking.booking_schedule.per_emi_inr ? ` of approx ₹${Math.round(booking.booking_schedule.per_emi_inr).toLocaleString('en-IN')} each` : ""}.
                          The first installment was paid at checkout — the rest will be auto-debited.
                        </p>
                        <div className="space-y-1.5">
                          {booking.booking_schedule.installments.map((emi) => {
                            const due = new Date(emi.due_on);
                            const isPast = !Number.isNaN(due.getTime()) && due < new Date();
                            return (
                              <div
                                key={`emi-${emi.installment_no}`}
                                className="flex items-center justify-between text-xs bg-white/[0.03] border border-white/5 rounded px-3 py-2"
                                data-testid={`installment-${emi.installment_no}`}
                              >
                                <span className="text-white/60">
                                  <span className="text-[#D4AF37] mr-1.5">#{emi.installment_no}</span>
                                  {due.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                  {isPast && emi.installment_no === 1 && <span className="ml-2 text-green-400/80 text-[10px]">paid</span>}
                                </span>
                                <span className="text-white">₹{Math.round(emi.amount_inr).toLocaleString('en-IN')}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div>
                      <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">Cancellation & Reschedule Policy</h3>
                      <ul className="space-y-1.5 text-xs text-white/40">
                        <li>Bookings once confirmed are <span className="text-white/60">non-refundable</span>. 1% booking fee is non-refundable.</li>
                        <li>30+ days before travel: Travel credit of 75% (excl. booking fee), valid 6 months.</li>
                        <li>15-30 days before travel: Travel credit of 40%.</li>
                        <li>Within 15 days: No refund or credit.</li>
                        <li>One reschedule allowed 30+ days before travel (₹2,500 fee + fare difference).</li>
                        <li>No-shows or missed installments: Forfeiture of all amounts paid.</li>
                      </ul>
                    </div>

                    {/* Invite Friends & Family */}
                    <div data-testid="invite-friends-section">
                      <h3 className="text-[#D4AF37] text-sm font-medium mb-3 border-b border-white/10 pb-2">Invite Friends & Family</h3>
                      <p className="text-white/50 text-xs mb-3 leading-relaxed">
                        Send your friends or family a personal invite to join the trip. They'll get the trip details and a link to book their own spot — no pricing info shared.
                      </p>
                      {/* Channel selector — Email or SMS. SMS requires country code + digits. */}
                      <div className="flex bg-white/5 border border-white/10 rounded-lg p-1 mb-3 w-fit" data-testid="invite-channel-tabs">
                        <button
                          type="button"
                          onClick={() => { setInviteChannel("email"); setInviteValue(""); }}
                          className={`px-3 py-1.5 text-[11px] rounded-md transition-colors ${
                            inviteChannel === "email" ? "bg-[#D4AF37] text-black font-medium" : "text-white/60 hover:text-white"
                          }`}
                          data-testid="invite-channel-email"
                        >
                          ✉ Email
                        </button>
                        <button
                          type="button"
                          onClick={() => { setInviteChannel("sms"); setInviteValue(""); }}
                          className={`px-3 py-1.5 text-[11px] rounded-md transition-colors ${
                            inviteChannel === "sms" ? "bg-[#D4AF37] text-black font-medium" : "text-white/60 hover:text-white"
                          }`}
                          data-testid="invite-channel-sms"
                        >
                          💬 SMS
                        </button>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {inviteChannel === "sms" && (
                          <select
                            value={inviteCountryCode}
                            onChange={(e) => setInviteCountryCode(e.target.value)}
                            className="bg-white/5 border border-white/10 text-white text-sm rounded-md px-2 py-2 sm:w-40 focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
                            data-testid="invite-country-code"
                          >
                            {COUNTRY_DIAL_CODES.map((c) => (
                              <option key={c.code} value={c.code} className="bg-[#0a0a0a]">{c.label}</option>
                            ))}
                          </select>
                        )}
                        <Input
                          value={inviteValue}
                          onChange={(e) => setInviteValue(e.target.value)}
                          placeholder={inviteChannel === "sms" ? "Phone number (digits only)" : "Email address"}
                          type={inviteChannel === "sms" ? "tel" : "email"}
                          inputMode={inviteChannel === "sms" ? "tel" : "email"}
                          autoComplete={inviteChannel === "sms" ? "tel" : "email"}
                          className="flex-1 bg-white/5 border-white/10 text-white text-sm"
                          data-testid="invite-input"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !sendingInvite) {
                              e.preventDefault();
                              handleInviteFriend();
                            }
                          }}
                        />
                        <Button
                          onClick={handleInviteFriend}
                          disabled={sendingInvite || !inviteValue.trim()}
                          className="bg-[#D4AF37] text-black hover:bg-[#E5C468] disabled:opacity-50"
                          data-testid="invite-send-button"
                        >
                          {sendingInvite ? "Sending…" : "Invite"}
                        </Button>
                      </div>
                      <p className="text-white/30 text-[10px] mt-2 leading-relaxed">
                        {inviteChannel === "sms"
                          ? `An SMS will be sent to ${inviteCountryCode}…  with the trip details and a booking link.`
                          : "Each guest receives an HTML email invite with the trip details and books independently for full flexibility."}
                      </p>
                      {sentInvites.length > 0 && (
                        <div className="mt-4 space-y-1.5" data-testid="invite-sent-list">
                          <p className="text-white/40 text-[10px] uppercase tracking-wider">Invites sent</p>
                          {sentInvites.map((inv, i) => (
                            <div key={`inv-${i}`} className="flex items-center justify-between text-xs bg-white/[0.03] border border-white/5 rounded px-3 py-2">
                              <span className="text-white/70 truncate mr-3">{inv.contact}</span>
                              <span className="text-[#D4AF37] text-[10px]">
                                {inv.channel === "sms" ? "💬 SMS" : "✉ Email"}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <Button
                    onClick={() => navigate("/")}
                    className="w-full mt-8 bg-[#D4AF37] text-black hover:bg-[#E5C468]"
                    data-testid="back-to-home-button"
                  >
                    Back to Home
                  </Button>
                </Card>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>
    </div>

      <AuthModal
        open={showPaymentAuth}
        onClose={() => {
          setShowPaymentAuth(false);
          setPendingBookingAfterAuth(false);
        }}
        setIsAuthenticated={setIsAuthenticated}
        setUser={setUser}
        onSuccess={() => {
          // Just close the modal. The useEffect on `isAuthenticated` will
          // pick up `pendingBookingAfterAuth` and fire handleCreateBooking
          // with the LATEST closure (avoiding the stale-state loop).
          setShowPaymentAuth(false);
        }}
      />
      <BookingDetailModal
        booking={detailBooking}
        open={!!detailBooking}
        onClose={() => setDetailBooking(null)}
        onUpdate={async () => {
          // Re-fetch the booking after EMI pause/resume so the modal reflects
          // the new subscription_status without a full page reload.
          if (!detailBooking?.id) return;
          try {
            const res = await axios.get(`${API}/bookings/${detailBooking.id}`);
            if (res.data) setDetailBooking(res.data);
          } catch (e) {
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.warn("Booking modal refresh failed (user can close & reopen):", e?.message || e);
            }
          }
        }}
      />
      {loadingDetail && detailBooking && (
        <div className="fixed top-4 right-4 z-[60] bg-white/10 backdrop-blur-md text-white text-xs px-3 py-2 rounded-md border border-white/15 flex items-center gap-2" data-testid="booking-detail-loading">
          <div className="animate-spin h-3 w-3 border-2 border-[#D4AF37] border-t-transparent rounded-full" />
          Loading full details…
        </div>
      )}

      {/* Mandate-intercept dialog — bridges the 1% upfront and the EMI
          auto-debit mandate Checkout. The Continue button is a direct
          user click, which preserves the popup-trust signal so Razorpay
          Checkout opens reliably on iOS Safari / strict Chrome.
          While `mandateInProgress` is true the Razorpay Checkout iframe is
          on screen — we collapse this Dialog so its overlay can't intercept
          clicks on Razorpay's UPI/Cards/EMandate tabs. */}
      <Dialog open={mandateInterceptOpen && !mandateInProgress} onOpenChange={() => { /* dismissed via deferMandate */ }}>
        <DialogContent
          className="bg-[#0a0a0a] border border-[#D4AF37]/30 text-white max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          data-testid="mandate-intercept-dialog"
        >
          <DialogHeader>
            <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mx-auto mb-2">
              <Shield className="w-7 h-7 text-[#D4AF37]" />
            </div>
            <DialogTitle className="font-serif text-2xl text-center text-white">
              Step 2 of 2 — Authorise auto-debit
            </DialogTitle>
            <DialogDescription className="text-center text-white/60 text-sm pt-1">
              Your <span className="text-[#D4AF37] font-medium">1% upfront payment</span> is captured.
              <br />Razorpay will now open once more to set up the auto-debit mandate for your remaining
              installments. <span className="text-white/80">This is required to confirm your EMI schedule.</span>
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 bg-white/[0.03] border border-white/10 rounded p-3 text-xs text-white/60 leading-relaxed">
            <p className="text-white/80 font-medium mb-1.5">What happens next?</p>
            <ul className="space-y-1 list-disc list-inside marker:text-[#D4AF37]/60">
              <li>Razorpay Checkout opens with your subscription details.</li>
              <li>Pick UPI AutoPay / Card / Net-banking and authorise the mandate.</li>
              <li>Razorpay will auto-debit your selected EMI amount on schedule — no further action needed from you.</li>
            </ul>
            <p className="mt-2 text-[11px] text-white/45 leading-relaxed">
              <span className="text-amber-200/90 font-medium">On iPad?</span> Razorpay hides UPI AutoPay on iPad because iPadOS reports a macOS user-agent. Use the <span className="text-white/70">Continue on your phone</span> option below to finish via UPI on your iPhone, or stay here and authorise via Card / Net-banking.
            </p>
          </div>

          {mandateError && (
            <div
              className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-3 py-2"
              data-testid="mandate-error"
            >
              <span className="font-medium text-red-200">Couldn't complete mandate setup:</span> {mandateError}
            </div>
          )}

          {iosPaymentTimedOut && !mandateInProgress && (
            <div
              className="mt-3 text-xs text-rose-200 bg-rose-500/10 border border-rose-400/30 rounded px-3 py-2 flex items-start gap-2"
              data-testid="mandate-timeout-banner"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-rose-300" />
              <span><span className="font-medium text-rose-100">Auto-debit setup didn't open.</span> Your network or browser may have blocked the secure checkout — tap Retry below.</span>
            </div>
          )}

          <div className="mt-5 flex flex-col gap-2">
            <Button
              onClick={startMandate}
              disabled={mandateInProgress}
              className="bg-[#D4AF37] text-black hover:bg-[#E5C468] font-medium h-11 disabled:opacity-60"
              data-testid={iosPaymentTimedOut ? "mandate-retry-button" : "mandate-continue-button"}
            >
              {mandateInProgress
                ? "Opening Razorpay…"
                : iosPaymentTimedOut
                ? "Retry auto-debit setup"
                : mandateError
                ? "Retry auto-debit setup"
                : "Continue to auto-debit setup"}
            </Button>

            {/* iPad-Safari escape hatch — Razorpay hides UPI AutoPay on
                desktop-class browsers (iPadOS reports macOS UA so UPI is
                hidden even though the customer has UPI apps on their iPhone).
                We surface the subscription's hosted short_url as a QR code
                so customers can scan with their phone and finish UPI
                AutoPay where it works natively. Only shown on iOS-Safari-
                like browsers + when we have a short_url. */}
            {isIOSSafariLike() && prewarmedSubscription?.short_url && (
              <details
                className="mt-2 text-xs border border-white/10 bg-white/[0.02] rounded p-3 group"
                data-testid="mandate-phone-handoff"
              >
                <summary className="cursor-pointer text-white/70 hover:text-white/90 list-none flex items-center justify-between">
                  <span className="font-medium">
                    📱 No UPI option? Continue on your phone
                  </span>
                  <span className="text-white/40 text-[10px] group-open:rotate-180 transition-transform">▾</span>
                </summary>
                <p className="mt-2 text-white/50 leading-relaxed text-[11px]">
                  Razorpay hides UPI AutoPay on iPad (their system thinks
                  it's a Mac). Scan this QR with your phone — your iPhone
                  will open the same secure mandate page where UPI works.
                </p>
                <div className="mt-3 flex flex-col items-center gap-2">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=${encodeURIComponent(prewarmedSubscription.short_url)}`}
                    alt="Scan to continue mandate on phone"
                    className="rounded bg-white p-1"
                    width={180}
                    height={180}
                    data-testid="mandate-phone-qr"
                  />
                  <Button
                    type="button"
                    onClick={async () => {
                      if (!booking?.id) return;
                      setSendingMandateLink(true);
                      try {
                        const res = await axios.post(`${API}/payments/send-mandate-link`, {
                          booking_id: booking.id,
                        });
                        const channels = [];
                        if (res.data.whatsapp_sent) channels.push("WhatsApp");
                        if (res.data.email_sent) channels.push("Email");
                        toast.success(`Mandate link sent via ${channels.join(" + ")}. Open it on your phone to enable UPI AutoPay.`);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Couldn't send the link. Please scan the QR or copy the link instead.");
                      } finally {
                        setSendingMandateLink(false);
                      }
                    }}
                    disabled={sendingMandateLink}
                    className="bg-emerald-500/90 text-black hover:bg-emerald-400 disabled:opacity-60 font-medium h-9 px-4 text-xs"
                    data-testid="mandate-whatsapp-link-button"
                  >
                    {sendingMandateLink ? "Sending…" : "📲 WhatsApp the link to my phone"}
                  </Button>
                  <a
                    href={prewarmedSubscription.short_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D4AF37] text-[11px] underline underline-offset-4 hover:text-[#E5C468] break-all text-center"
                    data-testid="mandate-phone-link"
                  >
                    Or open the link directly
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(prewarmedSubscription.short_url);
                        toast.success("Link copied — paste it in WhatsApp/email to your phone.");
                      } catch {
                        toast.message("Long-press the link above to copy it.");
                      }
                    }}
                    className="text-white/60 hover:text-white/90 text-[11px] transition-colors"
                    data-testid="mandate-phone-copy-link"
                  >
                    Copy link
                  </button>
                </div>
              </details>
            )}

            <button
              type="button"
              onClick={deferMandate}
              disabled={mandateInProgress}
              className="text-white/40 hover:text-white/70 text-xs underline underline-offset-4 transition-colors disabled:opacity-40"
              data-testid="mandate-defer-button"
            >
              Skip for now — I'll finish this from My Bookings
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

